import { db } from "./dbClient";

import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";

// Utility: Debounce a function
const debounce = (fn, delay) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
};

// Utility: Copy to clipboard with feedback
const copyToClipboard = (text, onSuccess) => {
  navigator.clipboard.writeText(text).then(() => {
    if (onSuccess) onSuccess();
  }).catch(err => console.error("Copy failed:", err));
};

// Utility: Export transactions to CSV
const exportToCSV = (txList, filename = "transactions.csv") => {
  const headers = ["Hash", "Method", "From", "To", "Value (ETH)", "Value (USD)", "Age", "Risk"];
  const rows = txList.map(tx => [
    tx.hash || "",
    tx.method || "",
    tx.from || "",
    tx.to || "",
    tx.value || "",
    tx.displayVal || "",
    tx.age || "",
    tx.risk || "",
  ]);

  const csv = [
    headers.join(","),
    ...rows.map(r => r.map(cell => `"${(cell || "").toString().replace(/"/g, '""')}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Utility: Export transactions to JSON
const exportToJSON = (txList, filename = "transactions.json") => {
  const json = JSON.stringify(txList, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// Skeleton loader component
const Skeleton = ({ width = "100%", height = "1rem", style = {} }) => (
  <div style={{
    background: "linear-gradient(90deg, rgba(74,88,112,0.2) 0%, rgba(74,88,112,0.35) 50%, rgba(74,88,112,0.2) 100%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 2s infinite",
    borderRadius: "4px",
    width,
    height,
    ...style
  }} />
);

// Empty state helper
const EmptyState = ({ icon = "📭", title, subtitle, action = null, C }) => (
  <div style={{ textAlign: "center", padding: "40px 20px", color: C.textDim }}>
    <div style={{ fontSize: "48px", marginBottom: "12px" }}>{icon}</div>
    <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "6px", color: C.text }}>{title}</div>
    <div style={{ fontSize: "12px", marginBottom: "16px" }}>{subtitle}</div>
    {action && <>{action}</>}
  </div>
);

// Address Display with hover info and ENS support
const AddressDisplay = ({ address, label, network = "ethereum", C, showCopy = true, onCopy = null }) => {
  const [ensName, setEnsName] = useState(null);
  const [showEnsFull, setShowEnsFull] = useState(false);

  useEffect(() => {
    if (network === "ethereum" && address && !label) {
      getENSName(address).then(setEnsName);
    }
  }, [address, label, network]);

  const displayText = label || ensName || (address ? address.slice(0, 6) + "..." + address.slice(-4) : "");
  const fullText = label || ensName || address;

  return (
    <div
      title={fullText}
      style={{ display: "flex", alignItems: "center", gap: 4, position: "relative" }}
      onMouseEnter={() => ensName && !label && setShowEnsFull(true)}
      onMouseLeave={() => setShowEnsFull(false)}
    >
      <span style={{ color: C.textMid, fontFamily: "monospace", fontSize: 11, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
        {displayText}
      </span>
      {ensName && !label && showEnsFull && (
        <span style={{ position: "absolute", bottom: -20, left: 0, background: C.bg, border: "1px solid " + C.border, borderRadius: 4, padding: "2px 6px", fontSize: 9, whiteSpace: "nowrap", zIndex: 1000 }}>
          {ensName}
        </span>
      )}
      {showCopy && address && (
        <button
          onClick={() => {
            copyToClipboard(address, onCopy);
          }}
          title="Copy address"
          style={{ background: "transparent", border: "none", color: C.textDim, cursor: "pointer", fontSize: 11, padding: "2px 4px", flexShrink: 0 }}
        >
          📋
        </button>
      )}
    </div>
  );
};

// Risk Gauge component with breakdown
const RiskGauge = ({ risk, C, showBreakdown = false, factors = [] }) => {
  const getRiskLabel = (r) => r >= 80 ? "CRITICAL" : r >= 60 ? "HIGH" : r >= 40 ? "MEDIUM" : "LOW";
  const getRiskColor = (r) => r >= 80 ? C.danger : r >= 60 ? "#FF8C00" : r >= 40 ? C.warn : C.success;
  const riskLabel = getRiskLabel(risk);
  const riskColor = getRiskColor(risk);

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:showBreakdown ? 12 : 0}}>
        {/* Circular gauge */}
        <div style={{position:"relative",width:60,height:60,flexShrink:0}}>
          <svg viewBox="0 0 60 60" style={{transform:"rotate(-90deg)",width:"100%",height:"100%"}}>
            {/* Background circle */}
            <circle cx="30" cy="30" r="24" fill="none" stroke={C.border} strokeWidth="3" />
            {/* Progress circle */}
            <circle
              cx="30" cy="30" r="24" fill="none" stroke={riskColor} strokeWidth="3"
              strokeDasharray={`${(risk / 100) * 150.8} 150.8`}
              strokeLinecap="round"
              style={{transition:"stroke-dasharray 0.4s"}}
            />
          </svg>
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column"}}>
            <span style={{fontSize:18,fontWeight:800,color:riskColor}}>{risk}</span>
            <span style={{fontSize:8,color:C.textDim}}>RISK</span>
          </div>
        </div>
        {/* Label and details */}
        <div style={{flex:1}}>
          <div style={{color:riskColor,fontSize:13,fontWeight:700,marginBottom:4}}>{riskLabel}</div>
          <div style={{color:C.textDim,fontSize:10}}>
            {risk >= 80 ? "Critical threat level" :
             risk >= 60 ? "High risk activity" :
             risk >= 40 ? "Medium risk detected" :
             "Low risk profile"}
          </div>
        </div>
      </div>
      {showBreakdown && factors.length > 0 && (
        <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:6,padding:8}}>
          <div style={{color:C.textDim,fontSize:9,marginBottom:6,letterSpacing:"0.05em"}}>RISK FACTORS</div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {factors.slice(0,3).map((f,i)=>(
              <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:9}}>
                <span style={{color:f.severity==="critical"?C.danger:f.severity==="high"?"#FF8C00":C.warn,fontSize:10,fontWeight:700}}>●</span>
                <span style={{color:C.text,flex:1}}>{f.factor}</span>
                <span style={{color:C.textDim}}>{f.severity}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const NODE_COLORS = {
  origin:"#00D4FF", exchange:"#A855F7", defi:"#39FF14", mixer:"#FF6B35",
  sanctioned:"#FF2244", atm:"#FF8C00", gambling:"#F59E0B", darknet:"#FF2244",
  ransomware:"#FF2244", scam:"#FF4444", bridge:"#00D4FF", nft:"#8B5CF6",
  staking:"#10B981", unknown:"#4A5870",
};

const CATEGORY_META = {
  origin:    {label:"Traced Wallet",      icon:"🎯"},
  exchange:  {label:"Exchange",           icon:"🏦"},
  defi:      {label:"DeFi Protocol",      icon:"⚙️"},
  mixer:     {label:"Mixer / Tumbler",    icon:"🌀"},
  sanctioned:{label:"OFAC Sanctioned",    icon:"🚫"},
  atm:       {label:"Crypto ATM",         icon:"🏧"},
  gambling:  {label:"Gambling",           icon:"🎰"},
  darknet:   {label:"Darknet Market",     icon:"🕶️"},
  ransomware:{label:"Ransomware",         icon:"💀"},
  scam:      {label:"Scam / Fraud",       icon:"⚠️"},
  bridge:    {label:"Cross-chain Bridge", icon:"🌉"},
  nft:       {label:"NFT Marketplace",    icon:"🖼️"},
  staking:   {label:"Staking / Yield",    icon:"📈"},
  unknown:   {label:"Unknown Wallet",     icon:"👛"},
};

// All API keys are server-side — no VITE_ keys needed here.
// Blockchain and AI calls go through the proxy at 127.0.0.1:3001
// Railway backend URL — set VITE_PROXY_URL in Vercel environment variables
const PROXY = (import.meta.env.VITE_PROXY_URL || "").replace(/\/+$/, ""); // strip trailing slash
const ENV = {
  etherscan: "proxied", bscscan: "proxied", polygonscan: "proxied",
  alchemy: "proxied", moralis: "proxied", anthropic: "proxied",
  openai: null, gemini: "proxied", groq: "proxied", chainalysis: "proxied",
};

const PROVIDERS = [
  {id:"etherscan",   name:"Etherscan",   chain:"ethereum", url:"https://api.etherscan.io/api",                        apiKey:ENV.etherscan},
  {id:"bscscan",     name:"BscScan",     chain:"binance",  url:"https://api.bscscan.com/api",                         apiKey:ENV.bscscan},
  {id:"polygonscan", name:"PolygonScan", chain:"polygon",  url:"https://api.polygonscan.com/api",                     apiKey:ENV.polygonscan},
  {id:"alchemy",     name:"Alchemy",     chain:"multi",    url:`https://eth-mainnet.g.alchemy.com/v2/${ENV.alchemy}`, apiKey:ENV.alchemy},
  {id:"moralis",     name:"Moralis",     chain:"multi",    url:"https://deep-index.moralis.io/api/v2.2",              apiKey:ENV.moralis},
];

const BLOCKCHAIN_NETWORKS = [
  // EVM Compatible
  {id:"ethereum",    name:"Ethereum",    symbol:"ETH",  icon:"Ξ", color:"#627EEA",   type:"evm"},
  {id:"binance",     name:"Binance Smart Chain", symbol:"BNB", icon:"⧩", color:"#F3BA2F", type:"evm"},
  {id:"polygon",     name:"Polygon",     symbol:"MATIC", icon:"◆", color:"#8247E5",   type:"evm"},
  {id:"avalanche",   name:"Avalanche C-Chain", symbol:"AVAX", icon:"▲", color:"#E84142", type:"evm"},
  {id:"arbitrum",    name:"Arbitrum One",  symbol:"ARB", icon:"Ⓐ", color:"#28A0F0", type:"evm"},
  {id:"optimism",    name:"Optimism",    symbol:"OP", icon:"Ⓞ", color:"#FF0420", type:"evm"},
  {id:"fantom",      name:"Fantom",      symbol:"FTM", icon:"F", color:"#1969FF", type:"evm"},
  {id:"gnosis",      name:"Gnosis Chain",symbol:"GNO", icon:"◆", color:"#04795B", type:"evm"},
  {id:"moonbeam",    name:"Moonbeam",    symbol:"GLMR", icon:"🌙", color:"#51D4FF", type:"evm"},
  {id:"celo",        name:"Celo",        symbol:"CELO", icon:"◈", color:"#FCFF52", type:"evm"},
  // Non-EVM
  {id:"solana",      name:"Solana",      symbol:"SOL", icon:"☉", color:"#14F195", type:"non-evm"},
  {id:"tron",        name:"TRON",        symbol:"TRX", icon:"T", color:"#EF0027", type:"non-evm"},
  {id:"cardano",     name:"Cardano",     symbol:"ADA", icon:"₳", color:"#0033AD", type:"non-evm"},
  {id:"xrpl",        name:"XRP Ledger",  symbol:"XRP", icon:"✕", color:"#23292F", type:"non-evm"},
  {id:"cosmos",      name:"Cosmos",      symbol:"ATOM", icon:"◉", color:"#16F784", type:"non-evm"},
  {id:"near",        name:"NEAR",        symbol:"NEAR", icon:"⊙", color:"#000000", type:"non-evm"},
  {id:"aptos",       name:"Aptos",       symbol:"APT", icon:"△", color:"#0084FF", type:"non-evm"},
  {id:"sui",         name:"Sui",         symbol:"SUI", icon:"☐", color:"#6FB8F7", type:"non-evm"},
  {id:"flow",        name:"Flow",        symbol:"FLOW", icon:"⟋", color:"#02E08C", type:"non-evm"},
];

const EXPLORERS = [
  // Bitcoin & Altcoins
  {name:"Blockchair",  network:"bitcoin", url:"blockchair.com/bitcoin/address/", icon:"₿", color:"#F7931A"},
  {name:"BlockStream", network:"bitcoin", url:"blockstream.info/address/", icon:"₿", color:"#2B2B2B"},
  {name:"Blockchain",  network:"bitcoin-cash", url:"blockchain.com/explorer/address/", icon:"🪙", color:"#FFC72C"},
  {name:"Blockchair BCH", network:"bitcoin-cash", url:"blockchair.com/bitcoin-cash/address/", icon:"🪙", color:"#F7931A"},

  // EVM Chains
  {name:"Etherscan",   network:"ethereum", url:"etherscan.io",    icon:"Ξ", color:"#627EEA"},
  {name:"BscScan",     network:"binance",  url:"bscscan.com",     icon:"⧩", color:"#F3BA2F"},
  {name:"PolygonScan", network:"polygon",  url:"polygonscan.com", icon:"◆", color:"#8247E5"},
  {name:"Snowtrace",   network:"avalanche", url:"snowtrace.io",   icon:"▲", color:"#E84142"},
  {name:"Arbiscan",    network:"arbitrum", url:"arbiscan.io",     icon:"Ⓐ", color:"#28A0F0"},
  {name:"Optimiscan",  network:"optimism", url:"optimistic.etherscan.io", icon:"Ⓞ", color:"#FF0420"},
  {name:"FtmScan",     network:"fantom",   url:"ftmscan.com",     icon:"F", color:"#1969FF"},
  {name:"Gnosisscan",  network:"gnosis",   url:"gnosisscan.io",   icon:"◆", color:"#04795B"},

  // Other Chains
  {name:"Solscan",     network:"solana",   url:"solscan.io/account/", icon:"☉", color:"#14F195"},
  {name:"TronScan",    network:"tron",     url:"tronscan.org/#/address/", icon:"T", color:"#EF0027"},
  {name:"Cardanoscan", network:"cardano",  url:"cardanoscan.io/address/", icon:"₳", color:"#0033AD"},
  {name:"XRPL Explorer", network:"xrpl",   url:"xrpscan.com/tx/", icon:"✕", color:"#23292F"},
];

// Detect which chain an address belongs to
const detectAddressChain = (addr) => {
  if (!addr) return null;
  if (/^0x[a-fA-F0-9]{40}$/.test(addr)) return 'ethereum';
  if (/^T[a-zA-Z0-9]{33}$/.test(addr)) return 'tron';
  if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(addr) || /^bc1[a-z0-9]{39,59}$/.test(addr)) return 'bitcoin';
  if (/^(bitcoincash:)?q[a-z0-9]{41}$/.test(addr)) return 'bitcoin-cash';
  return null;
};

// AI calls go through the proxy waterfall: Anthropic → Gemini → Groq
const AI_PROVIDERS = [
  {
    name: "AI (via proxy)",
    key: () => "proxied",
    call: async (prompt) => {
      const res = await fetch(`${PROXY}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: [{ role: "user", content: prompt }], max_tokens: 1200 }),
      });
      if (!res.ok) throw new Error("AI proxy " + res.status);
      const d = await res.json();
      return d.content?.[0]?.text || "";
    }
  },
];

// ── Multi-network price + symbol system ──────────────────────────────────────
const NETWORK_CURRENCY = {
  ethereum: { symbol: "ETH",  coingeckoId: "ethereum"       },
  binance:  { symbol: "BNB",  coingeckoId: "binancecoin"    },
  polygon:  { symbol: "POL",  coingeckoId: "matic-network"  },
  solana:   { symbol: "SOL",  coingeckoId: "solana"         },
  tron:     { symbol: "TRX",  coingeckoId: "tron"           },
};

const _priceCache = {};
async function getNetworkPrice(network) {
  const cfg = NETWORK_CURRENCY[network] || NETWORK_CURRENCY.ethereum;
  const id  = cfg.coingeckoId;
  const now = Date.now();
  if (_priceCache[id]?.usd && now - _priceCache[id].fetchedAt < 60000) return _priceCache[id].usd;
  try {
    const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${id}&vs_currencies=usd`);
    const d   = await res.json();
    const usd = d[id]?.usd || 0;
    _priceCache[id] = { usd, fetchedAt: now };
    return usd;
  } catch {
    return _priceCache[id]?.usd || 0;
  }
}
// Kept as alias so callers that pass ethPrice still work
async function getEthPrice() { return getNetworkPrice("ethereum"); }

function fmtUSD(amt, price) {
  if (!price || !amt) return "";
  const usd = parseFloat(amt) * price;
  if (usd >= 1e9)  return `$${(usd/1e9).toFixed(2)}B`;
  if (usd >= 1e6)  return `$${(usd/1e6).toFixed(2)}M`;
  if (usd >= 1000) return `$${(usd/1000).toFixed(1)}K`;
  if (usd >= 0.01) return `$${usd.toFixed(2)}`;
  return "<$0.01";
}
function fmtNative(val, network) {
  const sym = NETWORK_CURRENCY[network]?.symbol || "ETH";
  const n   = parseFloat(val) || 0;
  if (n === 0)      return `0 ${sym}`;
  if (n >= 1000)    return `${n.toFixed(1)} ${sym}`;
  if (n >= 1)       return `${n.toFixed(4)} ${sym}`;
  if (n >= 0.00001) return `${n.toFixed(6)} ${sym}`;
  return `${n.toExponential(3)} ${sym}`;
}
// Legacy alias used throughout — will be replaced by fmtNative where network is available
function fmtETH(val) { return fmtNative(val, "ethereum"); }

const KNOWN_ENTITIES = {
  // ── BINANCE (30+ wallets) ─────────────────────────────────────────────────
  "0x28c6c06298d514db089934071355e5743bf21d60":{type:"exchange",label:"Binance Hot Wallet 1",     risk:10},
  "0x21a31ee1afc51d94c2efccaa2092ad1028285549":{type:"exchange",label:"Binance Hot Wallet 2",     risk:10},
  "0xf977814e90da44bfa03b6295a0616a897441acec":{type:"exchange",label:"Binance Cold Wallet",      risk:5},
  "0x56eddb7aa87536c09ccc2793473599fd21a8b17f":{type:"exchange",label:"Binance Cold Wallet 2",    risk:5},
  "0x9696f59e4d72e237be84ffd425dcad154bf96976":{type:"exchange",label:"Binance Wallet",           risk:10},
  "0x4e9ce36e442e55ecd9025b9a6e0d88485d628a67":{type:"exchange",label:"Binance Hot Wallet 3",     risk:10},
  "0xbe0eb53f46cd790cd13851d5eff43d12404d33e8":{type:"exchange",label:"Binance Cold 2",           risk:5},
  "0x5a52e96bacdabb82fd05763e25335261b270efcb":{type:"exchange",label:"Binance Hot Wallet 4",     risk:10},
  "0xe0f0cfde7ee664943906f17f7f14342a76a83bc":{type:"exchange",label:"Binance Hot Wallet 5",     risk:10},
  "0x8f22f2063d253846b53609231ed80fa571bc0c8f":{type:"exchange",label:"Binance Hot Wallet 6",     risk:10},
  "0x3f5ce5fbfe3e9af3971dd833d26ba9b5c936f0be":{type:"exchange",label:"Binance Wallet 2",         risk:10},
  "0xd551234ae421e3bcba99a0da6d736074f22192ff":{type:"exchange",label:"Binance Wallet 3",         risk:10},
  "0x564286362092d8e7936f0549571a803b203aaced":{type:"exchange",label:"Binance Wallet 4",         risk:10},
  "0x0681d8db095565fe8a346fa0277bffde9c0edbbf":{type:"exchange",label:"Binance Wallet 5",         risk:10},
  "0xfe9e8709d3215310075d67e3ed32a380ccf451c8":{type:"exchange",label:"Binance Wallet 6",         risk:10},
  "0x4b16c5de96eb2117bbe5fd171e4d203624b014aa":{type:"exchange",label:"Binance Wallet 7",         risk:10},
  "0xa7efae728d2936e78bda97dc267687568dd593f3":{type:"exchange",label:"Binance Wallet 8",         risk:10},
  "0xd3272d61ab1eae8a87a35f2f0c7b1e6e3b51b6e3":{type:"exchange",label:"Binance Hot 9",           risk:10},
  // ── COINBASE ─────────────────────────────────────────────────────────────
  "0xeb2629a2734e272bcc07bda959863f316f4bd4cf":{type:"exchange",label:"Coinbase",                 risk:10},
  "0xa9d1e08c7793af67e9d92fe308d5697fb81d3e43":{type:"exchange",label:"Coinbase 2",               risk:10},
  "0x71660c4005ba85c37ccec55d0c4493e66fe775d3":{type:"exchange",label:"Coinbase Cold Wallet",     risk:5},
  "0x503828976d22510aad0201ac7ec88293211d23da":{type:"exchange",label:"Coinbase Wallet 3",        risk:10},
  "0xddfabcdc4d8ffc6d5beaf154f18b778f892a0740":{type:"exchange",label:"Coinbase Wallet 4",        risk:10},
  "0x3cd751e6b0078be393132286c442345e5dc49699":{type:"exchange",label:"Coinbase Wallet 5",        risk:10},
  "0xb5d85cbf7cb3ee0d56b3bb207d5fc4b82f43f511":{type:"exchange",label:"Coinbase Wallet 6",        risk:10},
  "0xeb2629a2734e272bcc07bda959863f316f4bd4cf":{type:"exchange",label:"Coinbase Hot",             risk:10},
  "0x02466e547bfdab679fc49e96bbfc62b9747d997c":{type:"exchange",label:"Coinbase Custody",         risk:5},
  "0xf6874c88757721a02f47592140905c4336dfbc61":{type:"exchange",label:"Coinbase Prime",           risk:5},
  // ── KRAKEN ───────────────────────────────────────────────────────────────
  "0xd882cfc20f52f2599d84b8e8d58c7fb62cfe344b":{type:"exchange",label:"Kraken",                   risk:10},
  "0x2910543af39aba0cd09dbb2d50200b3e800a63d2":{type:"exchange",label:"Kraken Wallet 2",          risk:10},
  "0xae2d4617c862309a3d75a0ffb358c7a5009c673f":{type:"exchange",label:"Kraken Wallet 3",          risk:10},
  "0x43984d578803891dfa9706bdeee6078d80cfc79e":{type:"exchange",label:"Kraken Wallet 4",          risk:10},
  "0x66c57bf505a85a74609d2c83e7f8b4c44a862cec":{type:"exchange",label:"Kraken Hot Wallet",        risk:10},
  // ── OKX / OKEx ───────────────────────────────────────────────────────────
  "0x66f820a414680b5bcda5eeca5dea238543f42054":{type:"exchange",label:"OKX",                      risk:15},
  "0x6cc5f688a315f3dc28a7781717a9a798a59fda7b":{type:"exchange",label:"OKX Wallet 2",             risk:15},
  "0x236f9f97e0e62388479bf9e5ba4889e46b0273c3":{type:"exchange",label:"OKX Hot Wallet",           risk:15},
  "0x8103683202aa8da10536036edef04cdd865c225e":{type:"exchange",label:"OKX Wallet 3",             risk:15},
  // ── KUCOIN ───────────────────────────────────────────────────────────────
  "0x2b5634c42055806a59e9107ed44d43c426e99540":{type:"exchange",label:"KuCoin",                   risk:15},
  "0xd6216fc19db775df9774a6e33526131da7d19a2c":{type:"exchange",label:"KuCoin 2",                 risk:15},
  "0x1692e170361cefd1eb7240ec13d048fd9af6d667":{type:"exchange",label:"KuCoin Hot Wallet",        risk:15},
  // ── BITFINEX ─────────────────────────────────────────────────────────────
  "0x1151314c646ce4e0efd76d1af4760ae66a9fe30f":{type:"exchange",label:"Bitfinex",                 risk:20},
  "0x742d35cc6634c0532925a3b8440bc9841c3b53f5":{type:"exchange",label:"Bitfinex 2",               risk:20},
  "0x77eb4b0c8e4e2b97f76f4b3f5a8b0e2f1c8b5a9d":{type:"exchange",label:"Bitfinex Cold",           risk:15},
  // ── HUOBI / HTX ──────────────────────────────────────────────────────────
  "0xab5c66752a9e8167967685f1450532fb96d5d24f":{type:"exchange",label:"Huobi/HTX",                risk:20},
  "0x6748f50f686bfbca6fe8ad62b22228b87f31ff2b":{type:"exchange",label:"Huobi Wallet 2",           risk:20},
  "0xfdb16996831753d5331ff813c29a93c76834a0ad":{type:"exchange",label:"Huobi Wallet 3",           risk:20},
  "0xeee28d484628d41a82d01e21d12e2e78d69920da":{type:"exchange",label:"Huobi Hot Wallet",         risk:20},
  // ── BYBIT ─────────────────────────────────────────────────────────────────
  "0xf89d7b9c864f589bbf53a82105107622b35eaa40":{type:"exchange",label:"Bybit",                    risk:20},
  "0x2c7536e3605d9c16a7a3d7b1898e529396a65c23":{type:"exchange",label:"Bybit Wallet 2",           risk:20},
  "0x1db3439a222c519ab44bb1144fc28167b4fa6ee6":{type:"exchange",label:"Bybit Hot Wallet",         risk:20},
  // ── GATE.IO ───────────────────────────────────────────────────────────────
  "0x0d0707963952f2fba59dd06f2b425ace40b492fe":{type:"exchange",label:"Gate.io",                  risk:25},
  "0x7793cd85c11a924478d358d49b05b37e91b5810f":{type:"exchange",label:"Gate.io Wallet 2",         risk:25},
  // ── GEMINI ────────────────────────────────────────────────────────────────
  "0xd24400ae8bfebb18ca49be86258a3c749cf46853":{type:"exchange",label:"Gemini",                   risk:10},
  "0x07ee55aa48bb72dcc6e9d78256648910de513eca":{type:"exchange",label:"Gemini Cold Wallet",       risk:5},
  // ── BITSTAMP ──────────────────────────────────────────────────────────────
  "0x00bdb5699745f5b860228a68c996c09a14bb6ce0":{type:"exchange",label:"Bitstamp",                 risk:10},
  "0x1c4b70a3968436b9a0a9cf5205c787eb81bb558c":{type:"exchange",label:"Bitstamp Cold",            risk:5},
  // ── CRYPTO.COM ────────────────────────────────────────────────────────────
  "0x6262998ced04146fa42253a5c0af90ca02dfd2a3":{type:"exchange",label:"Crypto.com",               risk:15},
  "0x46340b20830761efd32832a74d7169b29feb9758":{type:"exchange",label:"Crypto.com Wallet 2",      risk:15},
  // ── MEXC ──────────────────────────────────────────────────────────────────
  "0x75e89d5979e4f6fba9f97c104f2af40a1de1e5c5":{type:"exchange",label:"MEXC",                     risk:25},
  // ── BITMEX ────────────────────────────────────────────────────────────────
  "0xfe3b557e8fb62b89f4916b721be55ceb828dbd73":{type:"exchange",label:"BitMEX",                   risk:30},
  // ── COINCHECK ─────────────────────────────────────────────────────────────
  "0x4b01721f0244e7c5b5f63c20942850e447f5a5ee":{type:"exchange",label:"Coincheck",                risk:35},
  // ── CRYPTO ATMs ───────────────────────────────────────────────────────────
  "0xa090e606e30bd747d4e6245a1517ebe430f0057e":{type:"atm",      label:"Bitcoin Depot ATM",       risk:55},
  "0x19b0e3878ef7c67d0a16d2ca04cb70f2e9bd18f4":{type:"atm",      label:"CoinFlip ATM",            risk:55},
  "0x2cd2ef9e86e3d7a2a32c5e2a7e90c99a7b2abc6e":{type:"atm",      label:"Coinhub ATM",             risk:60},
  "0xf4516b5fc2d1cc49f56efd90cf6d08d6b8c44f24":{type:"atm",      label:"CoinMe ATM",              risk:55},
  // ── MIXERS / TUMBLERS ─────────────────────────────────────────────────────
  "0xd90e2f925da726b50c4ed8d0fb90ad053324f31b":{type:"mixer",   label:"Tornado Cash 0.1 ETH",     risk:95},
  "0x910cbd523d972eb0a6f4cae4618ad62622b39dbf":{type:"mixer",   label:"Tornado Cash 0.1 ETH 2",   risk:95},
  "0xa160cdab225685da1d56aa342ad8841c3b53f291":{type:"mixer",   label:"Tornado Cash 1 ETH",       risk:95},
  "0xfd8610d20aa15b7b2e3be39b396a1bc3516c7144":{type:"mixer",   label:"Tornado Cash 10 ETH",      risk:95},
  "0x07687e702b410fa43f4cb4af7fa097918ffd2730":{type:"mixer",   label:"Tornado Cash 100 ETH",     risk:95},
  "0x23773e65ed146a459667e6b6c2c100a39391f6d8":{type:"mixer",   label:"Tornado Cash TORN",        risk:90},
  "0x8576acc5c05d6ce88f4e49bf65bdf0c62f91353c":{type:"mixer",   label:"Sinbad Mixer",             risk:92},
  "0x722122df12d4e14e13ac3b6895a86e84145b6967":{type:"mixer",   label:"Tornado Cash Nova",        risk:95},
  "0x4736dcf1b7a3d580672cce6e7c65cd5cc9cfba9d":{type:"mixer",   label:"Tornado Cash V2",          risk:95},
  "0x12d66f87a04a9e220c9d6f1b1eb9b7a8b2fe1c3e":{type:"mixer",   label:"ChipMixer (ETH)",          risk:90},
  "0xe7aa314c77f4233c18c6cc84384a9247c0cf367b":{type:"mixer",   label:"Wasabi Coordinator",       risk:85},
  "0x94be88213a387e992dd87de56de9cf548e5c3a2b":{type:"mixer",   label:"CoinJoin Pool",            risk:80},
  // ── SANCTIONED / OFAC ────────────────────────────────────────────────────
  "0x7f367cc41522ce07553e823bf3be79a889debe1b":{type:"sanctioned",label:"OFAC Lazarus Group",     risk:100},
  "0x72a5843cc08275c8171e582972aa4fda8c397b2a":{type:"sanctioned",label:"OFAC Blender.io",        risk:100},
  "0x098b716b8aaf21512996dc57eb0615e2383e2f96":{type:"sanctioned",label:"OFAC Lazarus (ETH)",     risk:100},
  "0xa7e5d5a720f06526557c513402f2e6b5fa20b008":{type:"sanctioned",label:"OFAC Sanctioned",        risk:100},
  "0xd882cfc20f52f2599d84b8e8d58c7fb62cfe344b":{type:"exchange", label:"Kraken",                  risk:10},
  "0x1da5821544e25c636c1417ba96ade4cf6d2f9b5a":{type:"sanctioned",label:"OFAC Lazarus Group 2",   risk:100},
  "0x7db418b5d567a4e0e8c59ad71be1fce48f3e6107":{type:"sanctioned",label:"OFAC Lazarus Group 3",   risk:100},
  "0x901bb9583b24d97e995513c6778dc6888ab6870e":{type:"sanctioned",label:"OFAC Garantex (ETH)",    risk:100},
  "0x48549a34ae37b12f6a30566245176994e17c6b4a":{type:"sanctioned",label:"OFAC SUEX",              risk:100},
  "0x983a81ca6fb1e441266d2fbcb7d8e530ac2e05a2":{type:"sanctioned",label:"OFAC Chatex",            risk:100},
  "0x58e8dcc13be9780fc42e8723d8ead4cf46943df2":{type:"sanctioned",label:"OFAC Hydra Market",      risk:100},
  "0x19aa5fe80d33a56d56c78e82ea5e50e5d80b4dfe":{type:"sanctioned",label:"OFAC Bitpapa",           risk:100},
  "0x0330070fd38ec3bb94f58fa55d40368271e9e54a":{type:"sanctioned",label:"OFAC Sanctioned",         risk:100},
  // ── DARKNET MARKETS ───────────────────────────────────────────────────────
  "0xf3b0073e3a7f747c7a38b36b805247b222c302fd":{type:"darknet",  label:"AlphaBay Remnant",        risk:100},
  "0xbb2b8038a1640196fbe3e38816f3e67cba72d940":{type:"darknet",  label:"Darknet Market",          risk:95},
  // ── RANSOMWARE ────────────────────────────────────────────────────────────
  "0x7c195d981abfdc3ddecd2ca0fed0958430488e34":{type:"ransomware",label:"REvil Ransomware",       risk:100},
  "0xf1eb7d1e8f3e286e8c91399f35d50dce4a93fccc":{type:"ransomware",label:"Ryuk Ransomware",        risk:100},
  "0x3e93f13c23b61fd5c020abab89a7e92f20e5b671":{type:"ransomware",label:"DarkSide Ransomware",    risk:100},
  // ── GAMBLING ─────────────────────────────────────────────────────────────
  "0xb3a4bc89d8517e0e2c9b66703d09d3029ffa1e6d":{type:"gambling", label:"Rollbit Casino",          risk:60},
  "0xdf032bc4b9dc2782bb09352007d4c57b75160b15":{type:"gambling", label:"Roobet Casino",           risk:62},
  "0x4fabb145d64652a948d72533023f6e7a623c7c53":{type:"gambling", label:"Stake Casino",            risk:60},
  "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48":{type:"gambling", label:"Betfury",                 risk:55},
  // ── BRIDGES ───────────────────────────────────────────────────────────────
  "0x3ee18b2214aff97000d974cf647e7c347e8fa585":{type:"bridge",   label:"Wormhole Bridge",         risk:20},
  "0x40ec5b33f54e0e8a33a975908c5ba1c14e5bbbdf":{type:"bridge",   label:"Polygon Bridge",          risk:18},
  "0x99c9fc46f92e8a1c0dec1b1747d010903e884be1":{type:"bridge",   label:"Optimism Bridge",         risk:18},
  "0x4dbd4fc535ac27206064b68ffcf827b0a60bab3f":{type:"bridge",   label:"Arbitrum Bridge",         risk:18},
  "0x5427fefa711eff984124bfbb1ab6fbf5e3da1820":{type:"bridge",   label:"Celer cBridge",           risk:25},
  "0x1116898dda4015ed8ddefb84b6e8bc24528af2d8":{type:"bridge",   label:"Stargate Finance",        risk:20},
  "0xc30141b657f4216252dc59af2e7cdb9d8792e1b0":{type:"bridge",   label:"Socket Bridge",           risk:22},
  // ── DEFI ─────────────────────────────────────────────────────────────────
  "0x7a250d5630b4cf539739df2c5dacb4c659f2488d":{type:"defi",     label:"Uniswap V2 Router",       risk:15},
  "0xe592427a0aece92de3edee1f18e0157c05861564":{type:"defi",     label:"Uniswap V3 Router",       risk:15},
  "0x68b3465833fb72a70ecdf485e0e4c7bd8665fc45":{type:"defi",     label:"Uniswap Universal",       risk:15},
  "0xd9e1ce17f2641f24ae83637ab66a2cca9c378b9f":{type:"defi",     label:"SushiSwap Router",        risk:15},
  "0xba12222222228d8ba445958a75a0704d566bf2c8":{type:"defi",     label:"Balancer Vault",          risk:15},
  "0x7d2768de32b0b80b7a3454c06bdac94a69ddc7a9":{type:"defi",     label:"Aave V2",                 risk:15},
  "0x87870bca3f3fd6335c3f4ce8392d69350b4fa4e2":{type:"defi",     label:"Aave V3",                 risk:15},
  "0x3d9819210a31b4961b30ef54be2aed79b9c9cd3b":{type:"defi",     label:"Compound Comptroller",    risk:15},
  "0xc36442b4a4522e871399cd717abdd847ab11fe88":{type:"defi",     label:"Uniswap V3 Positions",    risk:15},
  "0x1f98431c8ad98523631ae4a59f267346ea31f984":{type:"defi",     label:"Uniswap V3 Factory",      risk:15},
  "0x6b175474e89094c44da98b954eedeac495271d0f":{type:"defi",     label:"DAI Stablecoin",          risk:5},
  "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48":{type:"defi",     label:"USDC Contract",           risk:5},
  "0xdac17f958d2ee523a2206206994597c13d831ec7":{type:"defi",     label:"USDT Contract",           risk:5},
  "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599":{type:"defi",     label:"WBTC Contract",           risk:8},
  // ── NFT ───────────────────────────────────────────────────────────────────
  "0x00000000000001ad428e4906ae43d8f9852d0dd6":{type:"nft",      label:"OpenSea Seaport",         risk:12},
  "0x7be8076f4ea4a4ad08075c2508e481d6c946d12b":{type:"nft",      label:"OpenSea Old",             risk:12},
  "0x0000000000a39bb272e79075ade125fd351887ac":{type:"nft",      label:"Blur Exchange",           risk:15},
  "0x000000000000ad05ccc4f10045630fb830b95127":{type:"nft",      label:"Blur Marketplace",        risk:15},
  // ── STAKING ───────────────────────────────────────────────────────────────
  "0x00000000219ab540356cbb839cbe05303d7705fa":{type:"staking",  label:"ETH2 Deposit Contract",   risk:5},
  "0x7f39c581f595b53c5cb19bd0b3f8da6c935e2ca0":{type:"staking",  label:"Lido wstETH",             risk:10},
  "0xae7ab96520de3a18e5e111b5eaab095312d7fe84":{type:"staking",  label:"Lido stETH",              risk:8},
  "0xdc24316b9ae028f1497c275eb9192a3ea0f67022":{type:"staking",  label:"Lido ETH-stETH Pool",     risk:10},
  // ── KNOWN BUILDERS & DEVELOPERS ────────────────────────────────────────────
  "0x4838b106fce9647bdf1e7877bf73ce8b0bad5f97":{type:"unknown",  label:"Titan Builder",           risk:20},
};

// ── Name-based fallback detection ─────────────────────────────────────────────
// When an address isn't in KNOWN_ENTITIES, match by contract name from block explorer
const EXCHANGE_NAME_PATTERNS = [
  // Major Exchanges
  { pattern: /binance|bnb|bn|bn\s/i,          type: "exchange",   label: "Binance",    risk: 10 },
  { pattern: /coinbase|coinbase\s/i,         type: "exchange",   label: "Coinbase",   risk: 10 },
  { pattern: /kraken/i,           type: "exchange",   label: "Kraken",     risk: 10 },
  { pattern: /kucoin|ku\.?coin|kcs/i,        type: "exchange",   label: "KuCoin",     risk: 15 },
  { pattern: /okx|okex|ok\.?coin/i,         type: "exchange",   label: "OKX",        risk: 15 },
  { pattern: /huobi|htx|ht\s/i,        type: "exchange",   label: "Huobi/HTX",  risk: 20 },
  { pattern: /bybit|bybitt|byb/i,            type: "exchange",   label: "Bybit",      risk: 20 },
  { pattern: /gate\.io|gateio|gt\.?exchange/i,  type: "exchange",   label: "Gate.io",    risk: 25 },
  { pattern: /gemini|gem/i,           type: "exchange",   label: "Gemini",     risk: 10 },
  { pattern: /bitstamp|stamp/i,         type: "exchange",   label: "Bitstamp",   risk: 10 },
  { pattern: /bitfinex|fnx/i,         type: "exchange",   label: "Bitfinex",   risk: 20 },
  { pattern: /bitmex|bmex|xbtusd/i,           type: "exchange",   label: "BitMEX",     risk: 30 },
  { pattern: /crypto\.com|cryptocom|cro\s/i, type: "exchange", label: "Crypto.com", risk: 15 },
  { pattern: /mexc|m\.?exchange/i,             type: "exchange",   label: "MEXC",       risk: 25 },
  { pattern: /bitget/i,           type: "exchange",   label: "Bitget",     risk: 25 },
  { pattern: /phemex|px\s/i,           type: "exchange",   label: "Phemex",     risk: 25 },
  { pattern: /poloniex/i,         type: "exchange",   label: "Poloniex",   risk: 30 },
  { pattern: /ftx/i,              type: "exchange",   label: "FTX (defunct)", risk: 50 },
  { pattern: /celsius|cel\s/i,          type: "exchange",   label: "Celsius (bankrupt)", risk: 50 },
  // Additional Exchanges
  { pattern: /upbit/i,            type: "exchange",   label: "Upbit",      risk: 20 },
  { pattern: /bithumb/i,          type: "exchange",   label: "Bithumb",    risk: 20 },
  { pattern: /coinone/i,          type: "exchange",   label: "Coinone",    risk: 25 },
  { pattern: /z\.com|zaif/i,      type: "exchange",   label: "Zaif",       risk: 25 },
  { pattern: /bitbank/i,          type: "exchange",   label: "Bitbank",    risk: 20 },
  { pattern: /btcbox/i,           type: "exchange",   label: "BTCBox",     risk: 30 },
  { pattern: /dydx/i,             type: "exchange",   label: "dYdX",       risk: 18 },
  { pattern: /kucoin.?labs|labs/i, type: "exchange",   label: "KuCoin Labs",risk: 20 },
  { pattern: /binance.?(ieo|launchpad|pool)/i, type: "exchange", label: "Binance Launchpad", risk: 12 },
  { pattern: /defi|yield|farm|pool|vault|protocol/i, type: "defi", label: "DeFi Protocol", risk: 20 },
  { pattern: /tornado/i,          type: "mixer",      label: "Tornado Cash", risk: 95 },
  { pattern: /sinbad|blender|chipmixer|wasabi|coinjoin|mixer|tumbl/i, type: "mixer", label: "Crypto Mixer", risk: 90 },
  { pattern: /lazarus|dprk/i,     type: "sanctioned", label: "OFAC Lazarus",  risk: 100 },
  { pattern: /garantex|suex|chatex|blender\.io/i, type: "sanctioned", label: "OFAC Sanctioned", risk: 100 },
  { pattern: /atm|coinflip|coinhub|coinme|bitcoin.?depot/i, type: "atm", label: "Crypto ATM", risk: 55 },
  { pattern: /rollbit|roobet|stake|betfury|casino|gambl/i, type: "gambling", label: "Crypto Gambling", risk: 60 },
  { pattern: /darknet|hydra|alphabay|dream.?market/i, type: "darknet", label: "Darknet Market", risk: 100 },
  { pattern: /ransomware|revil|ryuk|darkside|lockbit/i, type: "ransomware", label: "Ransomware", risk: 100 },
  { pattern: /uniswap/i,          type: "defi",       label: "Uniswap",    risk: 15 },
  { pattern: /sushiswap|sushi/i,  type: "defi",       label: "SushiSwap",  risk: 15 },
  { pattern: /aave/i,             type: "defi",       label: "Aave",       risk: 15 },
  { pattern: /compound/i,         type: "defi",       label: "Compound",   risk: 15 },
  { pattern: /balancer/i,         type: "defi",       label: "Balancer",   risk: 15 },
  { pattern: /curve/i,            type: "defi",       label: "Curve",      risk: 15 },
  { pattern: /wormhole|stargate|cbridge|arbitrum.?bridge|optimism.?bridge|polygon.?bridge/i, type: "bridge", label: "Cross-chain Bridge", risk: 20 },
  { pattern: /opensea|blur|nft/i, type: "nft",        label: "NFT Market", risk: 12 },
  // Builders & Developers
  { pattern: /builder|developer|dev\s|deployer|multi.?sig|gnosis.?safe|safe/i, type: "unknown", label: "Builder/Developer", risk: 15 },
  { pattern: /titan.?builder|titan/i, type: "unknown", label: "Titan Builder", risk: 20 },
  { pattern: /safe\s|multisig|multi.?signature/i, type: "unknown", label: "Multi-sig Wallet", risk: 10 },
];

function matchByName(name) {
  if (!name) return null;
  for (const p of EXCHANGE_NAME_PATTERNS) {
    if (p.pattern.test(name)) return { type: p.type, label: p.label, risk: p.risk };
  }
  return null;
}

// ── Exchange wallet sub-type detector ────────────────────────────────────────
// Returns a sub-label and icon for exchange nodes based on their label text
function getExchangeSubtype(label) {
  if (!label) return { sub: "CEX", icon: "🏦", color: "#A855F7" };
  if (/hot wallet|hot\s*\d*$|hot\s+\d+$/i.test(label)) return { sub: "Hot Wallet",     icon: "🔥", color: "#FF8C00" };
  if (/cold wallet|cold\s*\d*$|cold$/i.test(label))     return { sub: "Cold Wallet",    icon: "🧊", color: "#00D4FF" };
  if (/custody/i.test(label))                           return { sub: "Custody",        icon: "🏛",  color: "#10B981" };
  if (/prime/i.test(label))                             return { sub: "Prime / Inst",   icon: "⭐", color: "#C084FC" };
  if (/wallet\s+\d+$|\s+\d+$/i.test(label))            return { sub: "Hot Wallet",     icon: "🔥", color: "#FF8C00" };
  if (/\d+$/.test(label))                              return { sub: "Hot Wallet",     icon: "🔥", color: "#FF8C00" };
  return { sub: "CEX", icon: "🏦", color: "#A855F7" };
}

// ── Node card dimensions (used in rendering and hit detection) ────────────────
const NODE_W = 148, NODE_H = 54;

// Returns the point on the edge of a node's rectangle in the direction of (tx, ty)
function getRectEdge(nx, ny, tx, ty) {
  const dx = tx - nx, dy = ty - ny;
  if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) return { x: nx, y: ny };
  const scaleX = Math.abs(dx) > 0 ? (NODE_W / 2) / Math.abs(dx) : Infinity;
  const scaleY = Math.abs(dy) > 0 ? (NODE_H / 2) / Math.abs(dy) : Infinity;
  const s = Math.min(scaleX, scaleY);
  return { x: nx + dx * s, y: ny + dy * s };
}


const _cache = new Map();

// Map eth-labels label strings → our internal entity type + risk score
function classifyEthLabel(nameTag, label, labelType) {
  const text = [nameTag, label, labelType].filter(Boolean).join(" ").toLowerCase();
  if (!text) return null;

  // Run through EXCHANGE_NAME_PATTERNS first (catches all known names)
  const nameMatch = matchByName(text);
  if (nameMatch) return nameMatch;

  // Fallback keyword classification
  if (/tornado|mixer|tumbl|sinbad|chipmixer|blender|wasabi/i.test(text))
    return { type: "mixer",      label: nameTag || "Crypto Mixer",      risk: 90 };
  if (/sanction|ofac|lazarus|blocked|illicit/i.test(text))
    return { type: "sanctioned", label: nameTag || "OFAC Sanctioned",   risk: 100 };
  if (/darknet|hydra|alphabay|silkroad|market/i.test(text))
    return { type: "darknet",    label: nameTag || "Darknet Market",     risk: 100 };
  if (/ransomware|revil|ryuk|lockbit|darkside/i.test(text))
    return { type: "ransomware", label: nameTag || "Ransomware",         risk: 100 };
  if (/scam|phish|fraud|fake|ponzi/i.test(text))
    return { type: "scam",       label: nameTag || "Scam/Fraud",         risk: 90 };
  if (/atm|coinflip|coinhub|coinme|bitcoin.?depot|rockitcoin|athena/i.test(text))
    return { type: "atm",        label: nameTag || "Crypto ATM",         risk: 55 };
  if (/casino|gambl|rollbit|roobet|stake|bet/i.test(text))
    return { type: "gambling",   label: nameTag || "Crypto Gambling",    risk: 60 };
  if (/bridge|wormhole|stargate|cbridge|hop|across|synapse/i.test(text))
    return { type: "bridge",     label: nameTag || "Cross-chain Bridge", risk: 20 };
  if (/exchange|cex|hot.?wallet|cold.?wallet|deposit/i.test(text))
    return { type: "exchange",   label: nameTag || "Exchange",           risk: 20 };
  if (/uniswap|sushi|aave|compound|curve|balancer|defi|dex|swap|liquidity|amm/i.test(text))
    return { type: "defi",       label: nameTag || "DeFi Protocol",      risk: 15 };
  if (/nft|opensea|blur|rarible|zora|collectible/i.test(text))
    return { type: "nft",        label: nameTag || "NFT Market",         risk: 12 };
  if (/staking|lido|rocket.?pool|eth2|deposit/i.test(text))
    return { type: "staking",    label: nameTag || "Staking",            risk: 8  };

  // Has a nameTag but no category match — still label it
  if (nameTag) return { type: "unknown", label: nameTag, risk: 30 };
  return null;
}

async function lookupIntelligence(address) {
  const addr = address.toLowerCase();
  if (_cache.has(addr)) return _cache.get(addr);

  // 1. Local hardcoded DB — instant, no network
  const local = KNOWN_ENTITIES[addr];
  if (local) { _cache.set(addr, local); return local; }

  if (PROXY) {
    // 2. eth-labels live lookup — 170k+ Etherscan-sourced labels, no API key needed
    try {
      const res = await fetch(`${PROXY}/api/labels/${addr}`, {
        headers: { "Accept": "application/json" },
        signal: AbortSignal.timeout(3000),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.nameTag || data.label || data.labelType) {
          const classified = classifyEthLabel(data.nameTag, data.label, data.labelType);
          if (classified) {
            // Cache the result and also add to KNOWN_ENTITIES for graph rendering
            KNOWN_ENTITIES[addr] = classified;
            _cache.set(addr, classified);
            return classified;
          }
          // Has a name tag but unclassified — still useful
          const r = { type: "unknown", label: data.nameTag || data.label, risk: 30 };
          _cache.set(addr, r);
          return r;
        }
      }
    } catch(e) {
      // eth-labels unavailable — continue to Chainalysis
    }

    // 3. Chainalysis sanctions screening
    try {
      const res = await fetch(`${PROXY}/api/chainalysis/${addr}`, {
        headers: { "Accept": "application/json" },
        signal: AbortSignal.timeout(3000),
      });
      if (res.ok) {
        const data = await res.json();
        const ids = data.identifications || [];
        if (ids.length > 0) {
          const r = {
            type: "sanctioned",
            label: ids[0].name || "OFAC Sanctioned",
            risk: 100,
            sanctionDetails: ids.map(i => i.name).join(", "),
          };
          KNOWN_ENTITIES[addr] = r;
          _cache.set(addr, r);
          return r;
        }
        const r = { type: "unknown", label: "Untagged Wallet", risk: 30, chainalysisCleared: true };
        _cache.set(addr, r);
        return r;
      }
    } catch(e) {
      // Chainalysis unavailable
    }
  }

  // 4. Fallback — unknown
  const r = { type: "unknown", label: "Untagged Wallet", risk: 40 };
  _cache.set(addr, r);
  return r;
}

function scoreAddr(addr, txList=[]) {
  const a = addr.toLowerCase();
  if (KNOWN_ENTITIES[a]) return KNOWN_ENTITIES[a];
  let risk = 40;
  const dangerous = ["mixer","sanctioned","darknet","ransomware"];

  // Check for risky counterparties
  if (txList.some(tx => {
    const toType = KNOWN_ENTITIES[tx.to?.toLowerCase()]?.type;
    const fromType = KNOWN_ENTITIES[tx.from?.toLowerCase()]?.type;
    return dangerous.includes(toType) || dangerous.includes(fromType);
  })) risk = 85;

  // Detect exchange patterns - multiple addresses interacting indicates exchange deposits/withdrawals
  const uniqueCounterparties = new Set();
  txList.forEach(tx => {
    if (tx.from?.toLowerCase() === a) uniqueCounterparties.add(tx.to?.toLowerCase());
    if (tx.to?.toLowerCase() === a) uniqueCounterparties.add(tx.from?.toLowerCase());
  });

  // If interacting with many addresses (>20), likely an exchange or service
  if (uniqueCounterparties.size > 20) risk = Math.min(risk + 15, 60);

  return {type:"unknown", label:"Untagged Wallet", risk};
}

async function fetchEVMTxs(address, providerId, signal) {
  const chain = detectAddressChain(address);
  console.log(`[fetchEVMTxs] Detected chain: ${chain}`);

  // Bitcoin addresses - use Bitcoin analysis API
  if (chain === 'bitcoin') {
    try {
      console.log(`[fetchEVMTxs] Fetching Bitcoin data for ${address}`);
      const res = await fetch(
        `https://www.crossregnexus.com/api/crypto/bitcoin-analysis?address=${encodeURIComponent(address)}`,
        { signal }
      );

      if (res.ok) {
        const d = await res.json();
        console.log(`[fetchEVMTxs] Bitcoin analysis result:`, JSON.stringify(d, null, 2));

        // Try different transaction data structures
        let txs = [];
        if (d.graph?.links?.length > 0) {
          txs = d.graph.links.map((link, i) => ({
            hash: link.txHash || `${address}-${i}`,
            from: link.source,
            to: link.target,
            value: link.value?.toString() || '0',
            timeStamp: Math.floor((link.timestamp || Date.now()) / 1000),
            blockNumber: 0,
            _src: 'bitcoin-analysis'
          }));
        } else if (d.txCount > 0 || d.addressData?.txCount > 0) {
          // Graph is empty but we have transaction count
          const txCount = d.txCount || d.addressData?.txCount || 0;
          console.log(`[fetchEVMTxs] Bitcoin address has ${txCount} transactions but graph links are empty`);

          // Create placeholder transaction summary instead of individual tx objects
          // The actual transactions can be viewed on blockchain explorer
          if (txCount > 0) {
            txs.push({
              hash: `bitcoin-summary-${address}`,
              from: address,
              to: 'blockchain',
              value: (d.addressData?.totalReceived || 0).toString(),
              description: `${txCount} total transactions (${d.addressData?.unconfirmedTxCount || 0} unconfirmed)`,
              timeStamp: Math.floor(Date.now() / 1000),
              blockNumber: 0,
              _src: 'bitcoin-analysis-summary'
            });
          }
        }

        console.log(`[fetchEVMTxs] Got ${txs.length} Bitcoin transaction entries`);
        return txs;
      }
    } catch (err) {
      console.warn(`[fetchEVMTxs] Bitcoin analysis failed: ${err.message}`);
    }
    return [];
  }

  // Ethereum addresses - use Etherscan V2 API
  const isEthereumAddress = /^0x[a-fA-F0-9]{40}$/.test(address);

  if (!isEthereumAddress) {
    console.log(`[fetchEVMTxs] Address ${address} is not Ethereum format - skipping`);
    return [];
  }

  // Use official Etherscan V2 API with unified format
  console.log(`[fetchEVMTxs] calling Etherscan V2 for ${address}`);

  try {
    const etherscanKey = import.meta.env.VITE_ETHERSCAN_KEY || 'demo';
    const chainId = 1; // Ethereum mainnet; will support multi-chain in future

    // Official Etherscan V2 endpoint: https://api.etherscan.io/v2/api
    // V2 still requires module parameter, just uses unified base URL
    const res = await fetch(
      `https://api.etherscan.io/v2/api?chainid=${chainId}&module=account&action=txlist&address=${address}&startblock=0&endblock=99999999&sort=desc&page=1&offset=100&apikey=${etherscanKey}`,
      { signal }
    );

    if (!res.ok) {
      throw new Error(`Etherscan V2 API error: ${res.status}`);
    }

    const d = await res.json();
    console.log(`[fetchEVMTxs] Etherscan response:`, d);

    if (d.status === '1' && Array.isArray(d.result)) {
      const txs = d.result.map(t => ({
        hash: t.hash,
        from: t.from,
        to: t.to,
        value: t.value || '0',
        input: t.input || '',
        timeStamp: parseInt(t.timeStamp) || Math.floor(Date.now() / 1000),
        blockNumber: parseInt(t.blockNumber) || 0,
        isError: t.isError || '0',
        gas: t.gas,
        gasPrice: t.gasPrice,
        gasUsed: t.gasUsed,
        _src: 'etherscan-v2'
      }));
      console.log(`[fetchEVMTxs] Got ${txs.length} transactions from Etherscan V2`);
      return txs;
    }

    if (d.message === 'No transactions found' || d.result === '') {
      console.log(`[fetchEVMTxs] No transactions found for ${address}`);
      return [];
    }

    // Log full error response for debugging
    console.log(`[fetchEVMTxs] Etherscan API response:`, JSON.stringify(d, null, 2));
    throw new Error(`Etherscan error: ${d.message || 'Unknown error'} (status: ${d.status})`);
  } catch (err) {
    console.warn(`[fetchEVMTxs] Etherscan V2 failed: ${err.message}`);
    console.warn(`[fetchEVMTxs] Using fallback - Zerion and other data sources will provide transactions`);
    // Fallback: return empty array to allow other data sources (Zerion) to provide transactions
    return [];
  }
}

async function fetchAlchemy(address, signal) {
  const res = await fetch(`${PROXY}/api/blockchain/alchemy`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    signal,
    body: JSON.stringify({ address }),
  });
  if (!res.ok) throw new Error(`Alchemy HTTP ${res.status}`);
  const d = await res.json();
  return d.result?.transfers || [];
}

async function fetchMoralis(address, chain, signal) {
  const res = await fetch(
    `${PROXY}/api/blockchain/moralis?address=${encodeURIComponent(address)}&chain=${chain}`,
    { signal }
  );
  if (!res.ok) throw new Error(`Moralis HTTP ${res.status}`);
  const d = await res.json();
  return d.result || [];
}

async function fetchZerion(address, network, signal) {
  // Map our network names to Zerion chain IDs
  const chainMap = { ethereum:"ethereum", binance:"binance-smart-chain", polygon:"polygon", solana:"solana" };
  const chain = chainMap[network] || "ethereum";
  const res = await fetch(
    `${PROXY}/api/blockchain/zerion?address=${encodeURIComponent(address)}&type=transactions&chain=${chain}`,
    { signal }
  );
  if (!res.ok) throw new Error(`Zerion HTTP ${res.status}`);
  const d = await res.json();
  if (d.error) throw new Error(d.error);
  let result = d.data || [];
  // Defensive: ensure result is an array
  if (!Array.isArray(result)) {
    if (typeof result === 'object' && result !== null) {
      result = Object.values(result).find(v => Array.isArray(v)) || [];
    } else {
      result = [];
    }
  }
  return result;
}

function normZerion(raw, address) {
  try {
    // Zerion transaction format
    const attr = raw.attributes || {};
    let transfers = attr.transfers || [];

    // Defensive: ensure transfers is an array
    if (!Array.isArray(transfers)) {
      transfers = [];
    }

    // Find the main transfer (largest value)
    const main = transfers.length > 0
      ? transfers.reduce((best, t) =>
          (parseFloat(t?.value||0) > parseFloat(best?.value||0)) ? t : best, transfers[0] || {})
      : {};

    const from = (attr.sent_from || "").toLowerCase();
    const to   = (attr.sent_to   || "").toLowerCase();
    const sym  = main.fungible_info?.symbol || "ETH";
    const amt  = parseFloat(main.quantity?.float || main.value || "0") || 0;
    const isToken = !["ETH","BNB","POL","SOL","TRX"].includes(sym);
    const displayVal = isToken
      ? `${amt.toLocaleString(undefined,{maximumFractionDigits:6})} ${sym}`
      : null;
    const ts = attr.mined_at ? new Date(attr.mined_at).toLocaleString() : "?";
    return {
      hash:      attr.hash || raw.id || "",
      from:      attr.sent_from || "",
      to:        attr.sent_to   || "",
      value:     amt.toFixed(6),
      displayVal, isToken,
      tokenSymbol: isToken ? sym : null,
      age:       ts,
      method:    attr.operation_type || "Transfer",
      risk:      "MED", riskScore: 30,
      fromLabel: KNOWN_ENTITIES[from]?.label || null,
      toLabel:   KNOWN_ENTITIES[to]?.label   || null,
      direction: from === address.toLowerCase() ? "out" : "in",
    };
  } catch(e) {
    console.error("[normZerion] Parse error:", e.message);
    return null;
  }
}

async function fetchBitquery(address, network, signal) {
  const netMap = { ethereum:"ethereum", binance:"bsc", polygon:"matic", solana:"solana" };
  const res = await fetch(`${PROXY}/api/blockchain/bitquery`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
    signal,
    body: JSON.stringify({ address, network: netMap[network]||"ethereum", limit: 100 }),
  });
  if (!res.ok) throw new Error(`Bitquery HTTP ${res.status}`);
  const d = await res.json();
  if (d.error) throw new Error(d.error);
  return d.result || [];
}

function normBitquery(raw, address) {
  const t   = raw.Transfer    || {};
  const blk = raw.Block       || {};
  const tx  = raw.Transaction || {};
  const from = (t.Sender   || "").toLowerCase();
  const to   = (t.Receiver || "").toLowerCase();
  const sym  = t.Currency?.Symbol || "ETH";
  const dec  = parseInt(t.Currency?.Decimals || "18");
  const amt  = parseFloat(t.Amount || "0");
  const isToken = !["ETH","BNB","POL","MATIC","SOL","TRX"].includes(sym);
  const displayVal = isToken
    ? `${amt.toLocaleString(undefined,{maximumFractionDigits:6})} ${sym}`
    : null;
  return {
    hash: tx.Hash||"", from: t.Sender||"", to: t.Receiver||"",
    value: amt.toFixed(Math.min(dec,6)), displayVal, isToken,
    tokenSymbol: isToken ? sym : null,
    age: blk.Time ? new Date(blk.Time).toLocaleString() : "?",
    method: isToken ? `${sym} Transfer` : "Transfer",
    risk: "MED", riskScore: 30,
    fromLabel: KNOWN_ENTITIES[from]?.label || null,
    toLabel:   KNOWN_ENTITIES[to]?.label   || null,
    direction: from === address.toLowerCase() ? "out" : "in",
  };
}

async function fetchTron(address, signal) {
  // GetBlock Tron RPC — fetches transaction list for a Tron address
  const res = await fetch(`${PROXY}/api/blockchain/tron?address=${encodeURIComponent(address)}`, { signal });
  if (!res.ok) throw new Error(`Tron HTTP ${res.status}`);
  const d = await res.json();
  if (d.error) throw new Error(d.error);
  return d.data || [];
}

function normTron(raw, address) {
  // Normalise TronGrid/GetBlock transaction format
  const from = (raw.ownerAddress || raw.from_address || "").toLowerCase();
  const to   = (raw.toAddress   || raw.to_address   || "").toLowerCase();
  // TRX values are in SUN (1 TRX = 1,000,000 SUN)
  const sunVal = parseInt(raw.amount || raw.value || raw.contractData?.amount || "0");
  const trxVal = sunVal / 1_000_000;
  // Check if it's a TRC-20 token transfer
  const isToken = raw.tokenInfo != null || raw.contractType === "TransferAssetContract";
  const sym     = raw.tokenInfo?.tokenAbbr || raw.tokenName || (isToken ? "TRC20" : "TRX");
  const tokenAmt = isToken ? (parseInt(raw.amount || "0") / Math.pow(10, raw.tokenInfo?.tokenDecimal || 6)) : trxVal;
  const ts   = raw.timestamp ? new Date(raw.timestamp).toLocaleString() : (raw.block_timestamp ? new Date(raw.block_timestamp).toLocaleString() : "?");
  const hash = raw.hash || raw.txID || raw.transaction_id || "";
  const displayVal = isToken ? `${tokenAmt.toLocaleString(undefined,{maximumFractionDigits:4})} ${sym}` : null;

  return {
    hash,
    from: raw.ownerAddress || raw.from_address || "",
    to:   raw.toAddress    || raw.to_address   || "",
    value:       trxVal.toFixed(6),
    displayVal,
    isToken,
    tokenSymbol: isToken ? sym : null,
    age:         ts,
    method:      isToken ? `${sym} Transfer` : (raw.contractType || "Transfer"),
    risk:        "MED",
    riskScore:   30,
    fromLabel:   KNOWN_ENTITIES[from]?.label || null,
    toLabel:     KNOWN_ENTITIES[to]?.label   || null,
    direction:   from === address.toLowerCase() ? "out" : "in",
  };
}

async function fetchWalletBalance(address, network, signal) {
  const results = { native: null, tokens: [], tokenPortfolio: [], totalUSD: null, error: null };
  const chain = detectAddressChain(address);

  // Bitcoin addresses - use Bitcoin analysis API
  if (chain === 'bitcoin') {
    try {
      console.log(`[fetchWalletBalance] Fetching Bitcoin balance for ${address}`);
      const res = await fetch(
        `https://www.crossregnexus.com/api/crypto/bitcoin-analysis?address=${encodeURIComponent(address)}`,
        { signal }
      );

      if (res.ok) {
        const d = await res.json();
        console.log(`[fetchWalletBalance] Bitcoin analysis response:`, d);

        // Try different balance fields - prioritize finalBalance (actual wallet balance)
        let balance = null;
        if (d.addressData?.finalBalance) {
          // finalBalance is the actual balance in the wallet
          balance = parseFloat(d.addressData.finalBalance);
        } else if (d.analysis?.metrics?.netFlowBTC && d.analysis.metrics.netFlowBTC !== '0' && d.analysis.metrics.netFlowBTC !== 0) {
          // netFlowBTC is flow, not balance - only use if non-zero
          balance = parseFloat(d.analysis.metrics.netFlowBTC);
        } else if (d.metrics?.netFlowBTC) {
          balance = parseFloat(d.metrics.netFlowBTC);
        }

        if (balance !== null && balance !== undefined) {
          results.native = balance;
          console.log(`[fetchWalletBalance] Got Bitcoin balance: ${results.native} BTC`);
        } else {
          console.warn(`[fetchWalletBalance] No balance found in response`);
        }
      }
    } catch (e) { console.warn("[balance] Bitcoin balance failed:", e.message); }

    return results;
  }

  // Only use Etherscan for Ethereum addresses
  const isEthereumAddress = /^0x[a-fA-F0-9]{40}$/.test(address);

  if (isEthereumAddress) {
    // 1. Native balance via Etherscan V2 API (Ethereum only)
    try {
      const etherscanKey = import.meta.env.VITE_ETHERSCAN_KEY || 'demo';
      const chainId = 1; // Ethereum mainnet

      const res = await fetch(
        `https://api.etherscan.io/v2/api?chainid=${chainId}&module=account&action=balance&address=${address}&apikey=${etherscanKey}`,
        { signal }
      );

      if (res.ok) {
        const d = await res.json();
        console.log(`[fetchWalletBalance] Etherscan response:`, d);

        if (d.status === '1' && d.result) {
          // Balance is in wei, convert to ETH
          results.native = Number(BigInt(d.result)) / 1e18;
          console.log(`[fetchWalletBalance] Got balance: ${results.native} ETH`);
        }
      }
    } catch(e) { console.warn("[balance] Etherscan native balance failed:", e.message); }
  } else {
    console.log(`[fetchWalletBalance] Address ${address} is not Ethereum format - skipping Etherscan`);
  }

  // Fallback: Try BlockScout if Etherscan fails
  if (results.native === null) {
    try {
      const res = await fetch(
        `https://www.crossregnexus.com/api/crypto/blockscout-lookup`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address, chain: network || 'ethereum' }),
          signal
        }
      );
      if (res.ok) {
        const d = await res.json();
        if (d.data?.balance) {
          results.native = Number(BigInt(d.data.balance)) / 1e18;
          console.log(`[fetchWalletBalance] Got balance from BlockScout: ${results.native} ETH`);
        }
      }
    } catch(e) { console.warn("[balance] BlockScout fallback failed:", e.message); }
  }

  // 2. Token transfer history (optional, skip if PROXY unavailable)
  // Tokens will be shown from transaction analysis if available
  try {
    // Attempt token data from blockchain analysis endpoints
    // This is enrichment only, not critical
  } catch(e) { console.warn("[balance] tokens skipped"); }

  // 3. Portfolio enrichment (optional, skip if unavailable)
  try {
    // Zerion portfolio data is optional enrichment
    // Will be populated if available from blockchain API
  } catch(e) { console.warn("[balance] portfolio skipped"); }

  return results;
}

async function fetchTxByHash(hash, network, signal) {
  const provId = network==="binance" ? "bscscan" : network==="polygon" ? "polygonscan" : "etherscan";

  // Helper to normalise a result into our tx shape
  function buildTxDetail(tx, receipt, isHex) {
    let ethVal = "0";
    try {
      const raw = tx.value || (isHex ? "0x0" : "0");
      const wei = isHex ? BigInt(raw) : BigInt(Math.round(parseFloat(raw)));
      ethVal = wei === 0n ? "0" : (Number(wei) / 1e18).toFixed(8);
    } catch { ethVal = "0"; }

    const gasUsed   = receipt?.gasUsed   ? (isHex ? parseInt(receipt.gasUsed,16)   : parseInt(receipt.gasUsed))   : null;
    const gasPrice  = tx.gasPrice        ? (isHex ? parseInt(tx.gasPrice,16)        : null)                        : null;
    const gasFeeEth = gasUsed && gasPrice ? ((gasUsed * gasPrice) / 1e18).toFixed(8) : null;
    const blockNum  = tx.blockNumber     ? (isHex ? parseInt(tx.blockNumber,16)    : parseInt(tx.blockNumber))    : null;

    let status = "Unknown";
    if (receipt) {
      if (receipt.status === "0x1" || receipt.txreceipt_status === "1") status = "Success";
      else if (receipt.status === "0x0" || receipt.txreceipt_status === "0") status = "Failed";
    }
    const ts = tx.timeStamp ? new Date(parseInt(tx.timeStamp)*1000).toLocaleString() : "—";

    return {
      hash:        tx.hash || hash,
      from:        tx.from,
      to:          tx.to,
      value:       ethVal,
      gasUsed,     gasPrice,  gasFeeEth,
      blockNumber: blockNum,
      nonce:       tx.nonce ? (isHex ? parseInt(tx.nonce,16) : parseInt(tx.nonce)) : null,
      status,
      method:      tx.input?.length > 10 ? tx.input.slice(0,10) : (tx.functionName?.split("(")[0] || "Transfer"),
      age:         ts,
      risk:        "—",
      direction:   "out",
      fromLabel:   KNOWN_ENTITIES[tx.from?.toLowerCase()]?.label || null,
      toLabel:     KNOWN_ENTITIES[tx.to?.toLowerCase()]?.label   || null,
      confirmations: tx.confirmations,
    };
  }

  // Attempt 1: proxy module — eth_getTransactionByHash (hex response, works on paid tier)
  try {
    const res = await fetch(
      `${PROXY}/api/blockchain/${provId}?module=proxy&action=eth_getTransactionByHash&txhash=${encodeURIComponent(hash)}`,
      { signal }
    );
    if (res.ok) {
      const d = await res.json();
      if (d.result && typeof d.result === "object" && d.result.hash) {
        // Get receipt too
        let receipt = null;
        try {
          const rr = await fetch(
            `${PROXY}/api/blockchain/${provId}?module=proxy&action=eth_getTransactionReceipt&txhash=${encodeURIComponent(hash)}`,
            { signal }
          );
          if (rr.ok) receipt = (await rr.json()).result;
        } catch { /* receipt optional */ }
        return buildTxDetail(d.result, receipt, true);
      }
    }
  } catch(e) { console.warn("[hash] proxy module failed:", e.message); }

  // Attempt 2: account/txlist filtered to this hash — works on free tier
  // We can't search by hash directly, but we can use the transaction module
  try {
    const res = await fetch(
      `${PROXY}/api/blockchain/${provId}?module=transaction&action=gettxreceiptstatus&txhash=${encodeURIComponent(hash)}`,
      { signal }
    );
    // This gives us status — combine with a txlist search isn't possible
    // But try gettxinfo which some providers support
  } catch { /* ignore */ }

  // Attempt 3: account module gettxinfo (Etherscan Pro, but worth trying)
  try {
    const res = await fetch(
      `${PROXY}/api/blockchain/${provId}?module=account&action=txlist&txhash=${encodeURIComponent(hash)}&page=1&offset=1`,
      { signal }
    );
    if (res.ok) {
      const d = await res.json();
      if (d.status === "1" && Array.isArray(d.result) && d.result[0]) {
        const r = d.result[0];
        return buildTxDetail(r, r, false);
      }
    }
  } catch { /* ignore */ }

  // Attempt 4: Alchemy (supports hash lookups on free tier)
  try {
    const alchemyChainMap = {
      ethereum: "eth-mainnet",
      polygon: "polygon-mainnet",
      binance: "bnb-mainnet",
    };
    const alchemyChain = alchemyChainMap[network];

    if (alchemyChain && ENV.alchemy === "proxied") {
      // Only try if Alchemy is configured
      const res = await fetch(
        `${PROXY}/api/blockchain/alchemy`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            method: "eth_getTransactionByHash",
            params: [hash],
            id: 1,
            chain: alchemyChain,
          }),
          signal,
        }
      );

      if (res.ok) {
        const d = await res.json();
        if (d.result && typeof d.result === "object" && d.result.hash) {
          // Get receipt from Alchemy too
          let receipt = null;
          try {
            const rr = await fetch(
              `${PROXY}/api/blockchain/alchemy`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  jsonrpc: "2.0",
                  method: "eth_getTransactionReceipt",
                  params: [hash],
                  id: 1,
                  chain: alchemyChain,
                }),
                signal,
              }
            );
            if (rr.ok) receipt = (await rr.json()).result;
          } catch { /* receipt optional */ }
          return buildTxDetail(d.result, receipt, true);
        }
      }
    }
  } catch(e) { console.warn("[hash] Alchemy fallback failed:", e.message); }

  throw new Error(
    `Transaction not found on ${provId}. ` +
    `Make sure you selected the correct network — this hash appears to be on Polygon. ` +
    `The free API tier may not support direct hash lookups; click the explorer links below to view it.`
  );
}

function normTx(raw, src, root) {
  if (src === "evm") {
    const isToken = raw._src === "token";
    const ms  = Date.now() - parseInt(raw.timeStamp)*1000;
    const age = ms<3600000   ? `${Math.floor(ms/60000)}m ago`
              : ms<86400000  ? `${Math.floor(ms/3600000)}h ago`
              : `${Math.floor(ms/86400000)}d ago`;
    const from = raw.from?.toLowerCase();
    const to   = raw.to?.toLowerCase();
    // Enrich with name-based matching if not in KNOWN_ENTITIES
    if (from && !KNOWN_ENTITIES[from] && raw.fromContractName) {
      const m = matchByName(raw.fromContractName);
      if (m) KNOWN_ENTITIES[from] = m;
    }
    if (to && !KNOWN_ENTITIES[to] && (raw.toContractName || raw.contractName)) {
      const m = matchByName(raw.toContractName || raw.contractName);
      if (m) KNOWN_ENTITIES[to] = m;
    }

    let val = "0";
    let displayVal = null;

    if (isToken) {
      // ERC-20 token transfer: value is in token's smallest unit
      try {
        const decimals = parseInt(raw.tokenDecimal || "18");
        const amt = parseFloat(raw.value || "0") / Math.pow(10, decimals);
        val = amt.toFixed(Math.min(decimals, 6));
        displayVal = `${parseFloat(val).toLocaleString(undefined, {maximumFractionDigits:6})} ${raw.tokenSymbol||"TOKEN"}`;
      } catch { val = "0"; }
    } else {
      // Native transfer: Wei → ETH/BNB/POL using BigInt
      try {
        const wei = BigInt(raw.value || "0");
        const n   = Number(wei) / 1e18;
        val = n === 0 ? "0" : n >= 1 ? n.toFixed(6) : n.toFixed(10).replace(/\.?0+$/, "");
      } catch { val = (parseFloat(raw.value||"0")/1e18).toFixed(6); }
    }

    const dangerous = ["mixer","sanctioned","darknet","ransomware"];
    const isMixer   = dangerous.includes(KNOWN_ENTITIES[from]?.type) || dangerous.includes(KNOWN_ENTITIES[to]?.type);
    return {
      hash: raw.hash, from: raw.from, to: raw.to,
      value: val, displayVal, isToken,
      tokenSymbol: isToken ? raw.tokenSymbol : null,
      tokenName:   isToken ? raw.tokenName   : null,
      age,
      method: isToken
        ? `${raw.tokenSymbol||"Token"} Transfer`
        : (raw.functionName?.split("(")[0] || "Transfer"),
      risk:      isMixer ? "HIGH" : KNOWN_ENTITIES[to]?.type==="exchange" ? "LOW" : "MED",
      riskScore: isMixer ? 90 : 30,
      fromLabel: KNOWN_ENTITIES[from]?.label || null,
      toLabel:   KNOWN_ENTITIES[to]?.label   || null,
      direction: raw.from?.toLowerCase()===root.toLowerCase() ? "out" : "in",
    };
  }
  if (src === "moralis") {
    let mVal = "0";
    try {
      const wei = BigInt(raw.value || "0");
      mVal = wei === 0n ? "0" : (Number(wei) / 1e18).toFixed(6);
    } catch { mVal = (parseFloat(raw.value||"0")/1e18).toFixed(6); }
    return {
      hash: raw.hash, from: raw.from_address, to: raw.to_address,
      value: mVal, displayVal: null, isToken: false,
      age: raw.block_timestamp ? new Date(raw.block_timestamp).toLocaleString() : "?",
      method: raw.decoded_call?.name || "Transfer",
      risk: "LOW", riskScore: 30, fromLabel: null, toLabel: null,
      direction: raw.from_address?.toLowerCase()===root.toLowerCase() ? "out" : "in",
    };
  }
  return null;
}

// Enforces a hard cap on exchange nodes — keeps top N by risk score, removes excess
function capExchanges(g, max = 5) {
  if (!g || !g.nodes || !Array.isArray(g.nodes)) return g || { nodes: [], links: [] };
  const exchanges = g.nodes.filter(n => n.type === "exchange")
    .sort((a, b) => (b.risk || 0) - (a.risk || 0));
  if (exchanges.length <= max) return g;
  const keepIds = new Set([
    ...g.nodes.filter(n => n.type !== "exchange").map(n => n.id),
    ...exchanges.slice(0, max).map(n => n.id),
  ]);
  return {
    nodes: g.nodes.filter(n => keepIds.has(n.id)),
    links: g.links.filter(l => keepIds.has(l.source?.id || l.source) && keepIds.has(l.target?.id || l.target)),
  };
}

// Enforces a hard cap on unknown wallet nodes — keeps top N by link count, removes excess
function capUnknowns(g, max = 10) {
  if (!g || !g.nodes || !Array.isArray(g.nodes)) return g || { nodes: [], links: [] };
  const unknowns = g.nodes.filter(n => n.type === "unknown");
  if (unknowns.length <= max) return g;
  // Rank by how many links they have (most connected = most relevant)
  const linkCount = {};
  g.links.forEach(l => {
    const s = l.source?.id || l.source;
    const t = l.target?.id || l.target;
    linkCount[s] = (linkCount[s] || 0) + 1;
    linkCount[t] = (linkCount[t] || 0) + 1;
  });
  const sorted = unknowns.sort((a, b) => (linkCount[b.id] || 0) - (linkCount[a.id] || 0));
  const keepIds = new Set([
    ...g.nodes.filter(n => n.type !== "unknown").map(n => n.id),
    ...sorted.slice(0, max).map(n => n.id),
  ]);
  return {
    nodes: g.nodes.filter(n => keepIds.has(n.id)),
    links: g.links.filter(l => keepIds.has(l.source?.id || l.source) && keepIds.has(l.target?.id || l.target)),
  };
}

// Addresses that should never appear as graph nodes — null/burn/placeholder addresses
const JUNK_ADDRS = new Set([
  "0x0000000000000000000000000000000000000000", // ETH null / burn address
  "0x000000000000000000000000000000000000dead", // EVM dead address
  "0x0000000000000000000000000000000000000001", // Precompile
  "0x0000000000000000000000000000000000000002",
  "0x0000000000000000000000000000000000000003",
  "0x0000000000000000000000000000000000000004",
]);

function buildGraph(root, txList, customEntities={}) {
  const nodes = new Map();
  const links = [];
  const r = root.toLowerCase();
  const addNode = (addr, overrides={}) => {
    const a = addr.toLowerCase();
    if (JUNK_ADDRS.has(a)) return;   // never render null/burn addresses
    if (nodes.has(a)) return;
    const known = customEntities[a] || KNOWN_ENTITIES[a] || {};
    const scored = scoreAddr(a, txList);
    nodes.set(a, {
      id:a, label:a.slice(0,6)+"..."+a.slice(-4),
      type:known.type||scored.type||"unknown",
      risk:known.risk??scored.risk??50,
      value:a===r?100:50,
      tag:known.label||null,
      ...overrides,
    });
  };
  addNode(root, {type:"origin", risk:0, value:100});
  // Score counterparties — top 15 by frequency + risk, with exchanges capped at 5
  const cpScore = {};
  txList.forEach(tx => {
    if (!tx.from || !tx.to) return;
    const other = tx.from.toLowerCase()===r ? tx.to.toLowerCase() : tx.from.toLowerCase();
    if (other===r) return;
    if (JUNK_ADDRS.has(other)) return;   // skip null/burn addresses
    cpScore[other] = (cpScore[other]||0)+1;
    const k=KNOWN_ENTITIES[other]; if(k?.risk>=70) cpScore[other]+=5; if(k?.type&&k.type!=="unknown") cpScore[other]+=3;
  });
  const sorted = Object.entries(cpScore).sort((a,b)=>b[1]-a[1]);
  const exchangeAddrs = sorted.filter(([a]) => (customEntities[a]?.type || KNOWN_ENTITIES[a]?.type || scoreAddr(a, txList).type) === "exchange");
  const nonExchangeAddrs = sorted.filter(([a]) => (customEntities[a]?.type || KNOWN_ENTITIES[a]?.type || scoreAddr(a, txList).type) !== "exchange");
  const topCP = new Set([
    ...exchangeAddrs.slice(0, 5).map(([a]) => a),
    ...nonExchangeAddrs.slice(0, 10).map(([a]) => a),
  ]);

  txList.forEach(tx => {
    if (!tx.from || !tx.to) return;
    const from = tx.from.toLowerCase();
    const to = tx.to.toLowerCase();
    if (from!==r && to!==r) return;
    const other = from===r ? to : from;
    if (!topCP.has(other) && other!==r) return;
    addNode(from);
    addNode(to);
    const ex = links.find(l => l.source===from && l.target===to);
    if (ex) {
      ex.amount = (ex.amount||0) + (parseFloat(tx.value)||0);
      ex.count = (ex.count||1) + 1;
    } else {
      links.push({source:from, target:to, amount:parseFloat(tx.value)||0, direction:tx.direction, count:1});
    }
  });
  return {nodes:Array.from(nodes.values()), links};
}

// ─── AI Risk Panel ──────────────────────────────────────────────────────────────
function AIRiskPanel({node, graph, setGraph, txList, onLog, company, currentUser, drawerNode, drawerBalance, setDrawerNode, savedAnalyses, setSavedAnalyses, onSave, onLoad, customEntities, defiIntelligence, defiLoading, defiError}) {
  const C = useTheme();
  const [result,      setResult]      = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [tryingName,  setTryingName]  = useState("");
  const [usedName,    setUsedName]    = useState("");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);
  const [walletExchangeLookup, setWalletExchangeLookup] = useState({ loading: false, results: [], error: null });
  const prevId = useRef(null);

  useEffect(() => {
    if (!node || node.id === prevId.current) return;
    prevId.current = node.id;
    analyze();
  }, [node?.id]); // eslint-disable-line

  async function analyze() {
    setLoading(true); setError(null); setResult(null); setTryingName(""); setUsedName("");
    try {
      const addr = node.id;
      const meta = CATEGORY_META[node.type] || CATEGORY_META.unknown;

      // Check OFAC database for sanctioned status
      let isSanctioned = node.type === "sanctioned";
      if (!isSanctioned) {
        try {
          const dbRes = await fetch(`/api/crypto/sanctioned-lookup?address=${encodeURIComponent(addr)}`, {
            signal: AbortSignal.timeout(5000)
          });
          if (dbRes.ok) {
            const sanctionedList = await dbRes.json();
            isSanctioned = sanctionedList.length > 0;
            if (isSanctioned) console.log('✅ Address is OFAC sanctioned:', addr);
          }
        } catch (e) {
          console.warn('OFAC check failed in analyze:', e.message);
        }
      }

      const addrTxs = txList.filter(tx => tx.from?.toLowerCase()===addr || tx.to?.toLowerCase()===addr);
      const totalOut = addrTxs.filter(t=>t.direction==="out").reduce((s,t)=>s+parseFloat(t.value||0),0);
      const totalIn  = addrTxs.filter(t=>t.direction==="in" ).reduce((s,t)=>s+parseFloat(t.value||0),0);
      const highRisk = addrTxs.filter(t=>t.risk==="HIGH").length;
      const methods  = [...new Set(addrTxs.map(t=>t.method).filter(Boolean))].slice(0,5).join(", ")||"Transfer only";
      const G = graph || {nodes:[], links:[]};
      const outLinks = G.links.filter(l=>(l.source?.id||l.source)===addr);
      const inLinks  = G.links.filter(l=>(l.target?.id||l.target)===addr);
      const neighborLines = [...outLinks,...inLinks].slice(0,10).map(l => {
        const oid = (l.source?.id||l.source)===addr ? (l.target?.id||l.target) : (l.source?.id||l.source);
        const other = G.nodes.find(n=>n.id===oid);
        const dir = (l.source?.id||l.source)===addr ? "sent to" : "received from";
        return `  ${dir} ${other?.tag||oid.slice(0,10)+"..."} [${other?.type||"unknown"}, risk ${other?.risk||"?"}] ${l.amount?parseFloat(l.amount).toFixed(4)+" ETH":""}`;
      }).join("\n");
      const allNeighbors = [...outLinks,...inLinks]
        .map(l => {
          const oid = (l.source?.id||l.source)===addr ? (l.target?.id||l.target) : (l.source?.id||l.source);
          return G.nodes.find(n=>n.id===oid);
        })
        .filter(n => n);

      const dangerNeighbors = allNeighbors
        .filter(n => n.risk>=70)
        .map(n=>`${n.tag||n.id.slice(0,10)+"..."} (${n.type}, risk ${n.risk})`)
        .join(", ")||"None";

      const mediumRiskNeighbors = allNeighbors.filter(n => n.risk >= 40 && n.risk < 70).length;
      const unknownNeighbors = allNeighbors.filter(n => !n.tag && n.risk === 40).length;

      const prompt = `You are a blockchain forensics and AML compliance expert. Analyze this wallet and return ONLY valid JSON with no markdown, no text outside the JSON.

WALLET: ${addr}
CATEGORY: ${meta.label} (type: ${node.type})
KNOWN LABEL: ${node.tag||"None - untagged wallet"}
${isSanctioned ? "⚠️ CRITICAL: THIS ADDRESS IS ON THE OFAC SDN LIST - MUST RETURN SCORE 95-100 AND VERDICT HIGH RISK" : ""}
SANCTIONS: ${isSanctioned?"CONFIRMED OFAC SANCTIONED":"Not sanctioned"}

TRANSACTIONS: ${addrTxs.length} total | ${totalOut.toFixed(4)} ETH out | ${totalIn.toFixed(4)} ETH in | ${highRisk} high-risk transactions | Methods: ${methods}

CONNECTIONS (${outLinks.length} out, ${inLinks.length} in):
${neighborLines||"  None mapped yet"}

HIGH-RISK NEIGHBORS: ${dangerNeighbors}
MEDIUM-RISK NEIGHBORS: ${mediumRiskNeighbors}
UNKNOWN/UNTAGGED NEIGHBORS: ${unknownNeighbors}

RISK CALCULATION GUIDANCE:
- Sanctioned addresses: +90-100 points
- Known high-risk (mixer, darknet, ransomware): +80-95 points
- Connections to high-risk entities: +30-50 points
- Connections to unknown/untagged entities: +15-30 points per connection (baseline 40)
- High-risk transaction patterns (structuring, rapid layering): +20-40 points
- Simple peer-to-peer transfers to unknown addresses: +10-25 points
- No sanctioned counterparties: -10 to 0 points
- Low transaction volume: -5 to 0 points

Return exactly this JSON structure:
{
  "score": 45,
  "verdict": "MEDIUM RISK",
  "confidence": 85,
  "summary": "2-3 sentences about what this wallet is and its risk profile.",
  "score_reasoning": "Explain specifically how you calculated this risk score, citing the transaction count, counterparty types, patterns, and risk factors.",
  "risk_factors": [
    {"factor": "name", "severity": "critical", "explanation": "specific explanation citing actual data"}
  ],
  "mitigating_factors": [
    {"factor": "name", "explanation": "why this reduces risk"}
  ],
  "recommended_actions": ["action 1", "action 2"],
  "compliance_notes": "OFAC/BSA/FATF considerations for this specific wallet."
}

verdict must be: HIGH RISK (70+), MEDIUM RISK (40-69), LOW RISK (20-39), or CLEAN (0-19)
score must be 0-100
severity must be: critical, high, medium, or low`;

      let raw = "";
      let lastError = "";
      const available = AI_PROVIDERS.filter(p => { const k = p.key(); return k && k !== "undefined" && k.trim() !== ""; });

      if (available.length === 0) {
        throw new Error("All AI providers failed. Check that the proxy is running and API keys are set in .env.server.");
      }

      for (const provider of available) {
        try {
          setTryingName(provider.name);
          raw = await provider.call(prompt);
          setUsedName(provider.name);
          break;
        } catch(e) {
          lastError = `${provider.name}: ${e.message}`;
          console.warn("AI provider failed:", lastError);
        }
      }

      if (!raw) throw new Error("All providers failed. Last: " + lastError);

      // Check if response is an error message
      if (raw.startsWith("API Error") || raw.startsWith("Error")) {
        throw new Error(raw);
      }

      // Clean up the response more aggressively
      let clean = raw.replace(/```json\n?|\n?```/g,"").trim();

      // Remove any leading text before JSON object (be more careful)
      let jsonMatch = clean.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        // Find the matching closing brace by counting braces
        let braceCount = 0;
        let endIdx = -1;
        for (let i = 0; i < clean.length; i++) {
          if (clean[i] === '{') braceCount++;
          else if (clean[i] === '}') {
            braceCount--;
            if (braceCount === 0) {
              endIdx = i + 1;
              break;
            }
          }
        }
        if (endIdx > 0) {
          // Find the start of the JSON
          let startIdx = clean.indexOf('{');
          clean = clean.substring(startIdx, endIdx);
        } else {
          clean = jsonMatch[0];
        }
      }

      // Validate JSON before parsing
      if (!clean.startsWith("{")) {
        throw new Error(`Invalid response format (no JSON object found). Got: ${clean.substring(0, 100)}`);
      }

      let parsed;
      try {
        // Remove BOM if present
        if (clean.charCodeAt(0) === 0xFEFF) {
          clean = clean.slice(1);
        }
        parsed = JSON.parse(clean);
      } catch(parseErr) {
        // Suppress verbose logging - we'll use fallback extraction
        // Just note that parsing failed for debugging
        console.debug("[AIRiskPanel] JSON parse failed, using fallback extraction");

        let fixed = clean
          .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
          .replace(/:\s*undefined/g, ': null') // Replace undefined with null
          .replace(/\n\s*\n/g, '\n') // Remove extra blank lines
          .replace(/[\x00-\x1F\x7F]/g, ' '); // Remove control characters

        // Fix unescaped quotes in strings: find pattern like ..." foo" bar"...
        // This is risky but necessary for malformed AI responses
        fixed = fixed.replace(/": "([^"]*)"([^"]*)"([^"]*)"/, '": "$1 $2 $3"');

        try {
          // Remove BOM again if present
          if (fixed.charCodeAt(0) === 0xFEFF) {
            fixed = fixed.slice(1);
          }
          parsed = JSON.parse(fixed);
        } catch(retryErr) {
          // Last resort: extract essential fields using pattern matching
          // (Minimal fallback when AI returns malformed JSON)

          // Extract core fields with more flexible regex
          const verdictMatch = clean.match(/"verdict"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/);
          const confidenceMatch = clean.match(/"confidence"\s*:\s*(\d+)/);
          const scoreMatch = clean.match(/"score"\s*:\s*(\d+)/);

          // Extract summary (may contain escaped quotes and newlines)
          const summaryStart = clean.indexOf('"summary"');
          let summary = "Unable to parse summary";
          if (summaryStart !== -1) {
            const afterLabel = clean.indexOf(':', summaryStart);
            if (afterLabel !== -1) {
              const afterColon = clean.substring(afterLabel + 1).trim();
              if (afterColon.startsWith('"')) {
                // Find the closing quote, handling escapes
                let i = 1;
                let escaped = false;
                while (i < afterColon.length) {
                  if (escaped) {
                    escaped = false;
                  } else if (afterColon[i] === '\\') {
                    escaped = true;
                  } else if (afterColon[i] === '"') {
                    summary = afterColon.substring(1, i).replace(/\\"/g, '"');
                    break;
                  }
                  i++;
                }
              }
            }
          }

          // Extract risk_factors array if present
          let risk_factors = [];
          const rfStart = clean.indexOf('"risk_factors"');
          if (rfStart !== -1) {
            const arrayStart = clean.indexOf('[', rfStart);
            if (arrayStart !== -1) {
              const arrayEnd = clean.indexOf(']', arrayStart);
              if (arrayEnd !== -1) {
                const arrayStr = clean.substring(arrayStart + 1, arrayEnd);
                const factorMatches = arrayStr.match(/"factor"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/g);
                if (factorMatches) {
                  risk_factors = factorMatches.map(m => {
                    const match = m.match(/"factor"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/) || [];
                    return (match[1] || "").replace(/\\"/g, '"');
                  });
                }
              }
            }
          }

          if (verdictMatch && confidenceMatch) {
            // Use score from AI response, or derive from verdict if not provided
            const verdictText = verdictMatch[1];
            let score = scoreMatch ? parseInt(scoreMatch[1]) : 50; // Use AI-calculated score if available, default to medium
            if (!scoreMatch) {
              // Fallback: derive score from verdict if AI didn't provide it
              if (verdictText.includes("HIGH")) score = 80;
              else if (verdictText.includes("MEDIUM")) score = 55;
              else if (verdictText.includes("LOW")) score = 25;
              else if (verdictText.includes("CLEAN")) score = 5;
            }

            parsed = {
              verdict: verdictText,
              confidence: parseInt(confidenceMatch[1]),
              score: score, // Use AI-calculated risk score
              summary: summary,
              risk_factors: risk_factors.length > 0
                ? risk_factors.map(f => ({ factor: f, severity: "high", explanation: "" }))
                : [],
              mitigating_factors: [],
              recommended_actions: [],
              compliance_notes: ""
            };
          } else {
            throw new Error(`Unable to extract verdict/confidence. Got verdict=${verdictMatch?.[1]} confidence=${confidenceMatch?.[1]}`);
          }
        }
      }

      setResult(parsed);

      // Update node's risk score in graph with AI analysis result
      const aiRiskScore = parsed.score || node.risk;
      if (graph && node.id) {
        const updatedNode = graph.nodes.find(n => n.id === node.id);
        if (updatedNode && updatedNode.risk !== aiRiskScore) {
          setGraph(g => ({
            ...g,
            nodes: g.nodes.map(n => n.id === node.id ? {...n, risk: aiRiskScore} : n)
          }));
          setDrawerNode(prev => prev ? {...prev, risk: aiRiskScore} : prev);
        }
      }

      if (onLog) onLog({
        action: "crypto_ai_risk_analysis",
        module: "crypto_monitoring",
        entityName: node.tag || node.id.slice(0,10)+"...",
        meta: { verdict: parsed.verdict, confidence: parsed.confidence, riskScore: parsed.score || node.risk, nodeType: node.type, walletAddress: node.id },
      });
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
    setTryingName("");
  }

  const verdictColor = {
    "HIGH RISK":C.danger, "MEDIUM RISK":C.warn, "LOW RISK":C.success, "CLEAN":C.success
  };
  const sevColor = {critical:C.danger, high:C.orange, medium:C.warn, low:C.success};

  async function lookupWalletsExchange() {
    setWalletExchangeLookup({ loading: true, results: [], error: null });
    try {
      const wallets = graph.nodes.map(n => n.id);
      console.log('[Exchange Lookup] Starting lookup for', wallets.length, 'wallets');

      if (wallets.length === 0) {
        setWalletExchangeLookup({ loading: false, results: [], error: "No wallets in graph" });
        return;
      }

      const response = await fetch('/api/crypto/wallet-exchange-lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ wallets, chain: 'ethereum', companyId: company?.id })
      });

      console.log('[Exchange Lookup] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Exchange Lookup] Error response:', errorText);
        throw new Error(`Lookup failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Exchange Lookup] Response data:', data);

      setWalletExchangeLookup({
        loading: false,
        results: data.data || [],
        error: null
      });

      if (data.summary) {
        const msg = `Found ${data.summary.found}/${data.summary.total_scanned} wallets in exchange database`;
        console.log('[Exchange Lookup] Summary:', msg);
      }
    } catch (err) {
      console.error('[Exchange Lookup] Error:', err.message);
      setWalletExchangeLookup({ loading: false, results: [], error: err.message });
    }
  }

  return (
    <div style={{display:"flex",flexDirection:"column",gap:12}}>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
        <span style={{color:C.textMid,fontSize:11,letterSpacing:"0.08em"}}>AI RISK ANALYSIS</span>
        <div style={{display:"flex",gap:6}}>
          {node && (
            <button onClick={() => setShowSaveModal(true)} disabled={loading || !result}
              style={{padding:"4px 10px",background:C.success+"22",border:"1px solid "+C.success+"44",
                borderRadius:6,color:C.success,cursor:loading||!result?"not-allowed":"pointer",
                fontFamily:"monospace",fontSize:9}}>
              💾 Save
            </button>
          )}
          {node && (
            <button onClick={analyze} disabled={loading}
              style={{padding:"4px 10px",background:C.accentGlow,border:"1px solid"+C.accent+"44",
                borderRadius:6,color:C.accent,cursor:loading?"wait":"pointer",
                fontFamily:"monospace",fontSize:9}}>
              {loading ? "..." : "Re-run"}
            </button>
          )}
          {(savedAnalyses.length > 0 || showSavedList) && (
            <button onClick={() => setShowSavedList(!showSavedList)}
              style={{padding:"4px 10px",background:C.accent+"22",border:"1px solid"+C.accent+"44",
                borderRadius:6,color:C.accent,cursor:"pointer",
                fontFamily:"monospace",fontSize:9}}>
              📚 ({savedAnalyses.length})
            </button>
          )}
          <button onClick={lookupWalletsExchange} disabled={walletExchangeLookup.loading || !graph.nodes.length}
            style={{padding:"4px 10px",background:"#F0B42922",border:"1px solid #F0B42944",
              borderRadius:6,color:"#F0B429",cursor:walletExchangeLookup.loading||!graph.nodes.length?"not-allowed":"pointer",
              fontFamily:"monospace",fontSize:9}}>
            {walletExchangeLookup.loading ? "..." : "🏦 Exchange Lookup"}
          </button>
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <div style={{background:C.surfaceAlt,border:"1px solid"+C.border,borderRadius:8,padding:12,gap:8,display:"flex",flexDirection:"column"}}>
          <div style={{fontSize:12,fontWeight:600,color:C.text}}>Save This Analysis</div>
          <input
            type="text"
            placeholder="Name this analysis..."
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            style={{padding:"6px 8px",border:"1px solid"+C.border,borderRadius:4,background:C.bg,color:C.text,fontFamily:"monospace",fontSize:11}}
            onKeyDown={(e) => e.key==="Enter" && onSave(saveName, setSaveName, setShowSaveModal, setSavingAnalysis)}
          />
          <div style={{display:"flex",gap:6}}>
            <button onClick={() => onSave(saveName, setSaveName, setShowSaveModal, setSavingAnalysis)} disabled={!saveName.trim() || savingAnalysis}
              style={{flex:1,padding:"6px",background:C.success,border:"none",borderRadius:4,color:C.bg,cursor:savingAnalysis?"wait":"pointer",fontFamily:"monospace",fontSize:10,fontWeight:600}}>
              {savingAnalysis ? "Saving..." : "Save"}
            </button>
            <button onClick={() => {setShowSaveModal(false); setSaveName("");}}
              style={{flex:1,padding:"6px",background:C.border,border:"none",borderRadius:4,color:C.text,cursor:"pointer",fontFamily:"monospace",fontSize:10}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Saved Analyses List */}
      {showSavedList && savedAnalyses.length > 0 && (
        <div style={{background:C.surfaceAlt,border:"1px solid"+C.border,borderRadius:8,padding:12,maxHeight:200,overflowY:"auto"}}>
          <div style={{fontSize:11,fontWeight:600,color:C.textMid,marginBottom:8}}>Saved Analyses ({savedAnalyses.length})</div>
          {savedAnalyses.map((saved,i) => (
            <div key={i} style={{padding:"6px",background:C.bg,borderRadius:4,marginBottom:4,display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:10}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,color:C.text}}>{saved.wallet_name}</div>
                <div style={{color:C.textDim,fontSize:9}}>{saved.wallet_address.slice(0,10)}... | Risk: {saved.risk_score}</div>
              </div>
              <button onClick={() => onLoad(saved, setShowSavedList)}
                style={{padding:"3px 6px",background:C.accent,border:"none",borderRadius:3,color:C.bg,cursor:"pointer",fontFamily:"monospace",fontSize:8,whiteSpace:"nowrap"}}>
                Load
              </button>
            </div>
          ))}
        </div>
      )}

      {!node && (
        <div style={{background:C.bg,border:"1px dashed "+C.border,borderRadius:10,
          padding:"32px 20px",textAlign:"center",color:C.textDim}}>
          <div style={{fontSize:30,marginBottom:10}}>AI</div>
          <div style={{fontSize:12}}>Click any wallet node to get AI risk analysis</div>
        </div>
      )}

      {loading && (
        <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:10,
          padding:"28px 20px",textAlign:"center"}}>
          <div style={{color:C.textMid,fontSize:12,marginBottom:4}}>Analyzing wallet...</div>
          {tryingName && (
            <div style={{color:C.accent,fontSize:10,marginBottom:14}}>trying {tryingName}</div>
          )}
          <div style={{height:3,borderRadius:3,background:C.border,overflow:"hidden"}}>
            <div style={{height:"100%",width:"35%",background:C.accent,borderRadius:3,
              animation:"aitrain 1.3s ease-in-out infinite"}}/>
          </div>
          <style>{"@keyframes aitrain{0%{transform:translateX(-100%)}100%{transform:translateX(390%)}}"}</style>
        </div>
      )}

      {error && !loading && (
        <div style={{background:C.danger+"11",border:"1px solid "+C.danger+"33",
          borderRadius:8,padding:"12px 14px"}}>
          <div style={{color:C.danger,fontSize:11,fontWeight:700,marginBottom:4}}>Analysis Error</div>
          <div style={{color:C.textMid,fontSize:10,marginBottom:8}}>{error}</div>
          <button onClick={analyze}
            style={{padding:"5px 10px",background:"transparent",border:"1px solid "+C.danger+"55",
              borderRadius:5,color:C.danger,cursor:"pointer",fontSize:9,fontFamily:"monospace"}}>
            Retry
          </button>
        </div>
      )}

      {walletExchangeLookup.results.length > 0 && (
        <div style={{background:"#F0B42911",border:"1px solid #F0B42944",
          borderRadius:8,padding:"12px 14px",marginBottom:12}}>
          <div style={{color:"#F0B429",fontSize:11,fontWeight:700,marginBottom:8}}>🏦 Exchange Database Lookup</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {walletExchangeLookup.results.map((r,i)=>(
              <div key={i} style={{background:C.bg,padding:"8px 10px",borderRadius:6,fontSize:10,
                borderLeft:`3px solid #F0B429`}}>
                <div style={{fontFamily:"monospace",color:C.text,marginBottom:2}}>
                  {r.wallet_address?.substring(0,10)}...{r.wallet_address?.substring(-8)}
                </div>
                <div style={{color:"#F0B429",fontWeight:600}}>{r.exchange_name}</div>
                <div style={{color:C.textMid,fontSize:9,marginTop:2}}>
                  Confidence: {Math.round((r.confidence_score||0)*100)}% · Risk: {r.risk_level}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {walletExchangeLookup.error && (
        <div style={{background:C.danger+"11",border:"1px solid "+C.danger+"33",
          borderRadius:8,padding:"12px 14px",marginBottom:12}}>
          <div style={{color:C.danger,fontSize:10}}>{walletExchangeLookup.error}</div>
        </div>
      )}

      {result && !loading && (
        <>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:8}}>
            <button onClick={() => {
              const riskFactors = (result.risk_factors||[]).map(rf=>{
                let factor = typeof rf === 'string' ? rf : (rf.factor || '');
                let explanation = rf.explanation || '';
                let severity = rf.severity || 'low';
                return `<tr><td><span style="background:${sevColor[severity]||"#666"}22;color:${sevColor[severity]||"#666"};padding:1px 6px;border-radius:3px;font-size:9px;font-weight:700">${severity}</span> ${factor}</td><td>${explanation}</td></tr>`;
              }).join("");
              const graphNodes = graph?.nodes || [];
              const graphLinks = graph?.links || [];
              const nodesTable = graphNodes.map(n=>`<tr><td style="font-family:monospace;font-size:9px">${n.id.slice(0,12)}...</td><td>${n.tag||n.type}</td><td>${n.type}</td><td>${n.risk}/100</td></tr>`).join("");
              const linksTable = graphLinks.map(l=>{const sid=l.source?.id||l.source,tid=l.target?.id||l.target,sn=graphNodes.find(x=>x.id===sid),tn=graphNodes.find(x=>x.id===tid);return `<tr><td>${l.direction==="in"?"↓":"↑"}</td><td style="font-family:monospace;font-size:9px">${sn?.tag||sid.slice(0,10)}...</td><td style="font-family:monospace;font-size:9px">${tn?.tag||tid.slice(0,10)}...</td><td>${l.amount>0?l.amount.toFixed(6):"-"}</td></tr>`;}).join("");

              // Generate SVG graph visualization
              let graphSvg = '';
              if (graphNodes.length > 0) {
                const width = 800, height = 400;
                const centerX = width / 2, centerY = height / 2;
                const radius = 120;

                // Position nodes in a circle
                const nodePositions = {};
                graphNodes.forEach((n, i) => {
                  const angle = (i / graphNodes.length) * 2 * Math.PI;
                  nodePositions[n.id] = {
                    x: centerX + radius * Math.cos(angle),
                    y: centerY + radius * Math.sin(angle)
                  };
                });

                // Create SVG
                let links = graphLinks.map(l => {
                  const sid = l.source?.id || l.source;
                  const tid = l.target?.id || l.target;
                  const sp = nodePositions[sid] || {x: centerX, y: centerY};
                  const tp = nodePositions[tid] || {x: centerX, y: centerY};
                  return `<line x1="${sp.x}" y1="${sp.y}" x2="${tp.x}" y2="${tp.y}" stroke="#00D4FF" stroke-width="1" opacity="0.5"/>`;
                }).join('');

                let nodes = graphNodes.map((n, i) => {
                  const pos = nodePositions[n.id];
                  const isOrigin = n.type === 'origin';
                  const radius = isOrigin ? 8 : 6;
                  const color = isOrigin ? '#00D4FF' : '#666';
                  return `<circle cx="${pos.x}" cy="${pos.y}" r="${radius}" fill="${color}" opacity="0.8"/><text x="${pos.x}" y="${pos.y+20}" font-size="9" text-anchor="middle" fill="#0A2342" font-family="monospace">${n.id.slice(0,8)}</text>`;
                }).join('');

                graphSvg = `<svg width="100%" height="400" viewBox="0 0 ${width} ${height}" style="border: 1px solid #E2E8F0; border-radius: 4px; margin-bottom: 2mm; background: #F7FAFC;">
                  ${links}
                  ${nodes}
                </svg>`;
              }
              const w = window.open("","_blank");
              if(!w) return;
              w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Crypto Analysis</title><style>
              @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@700;800&display=swap');
              *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Mono',monospace;background:#fff;color:#0A2342;padding:12mm;font-size:11px}
              .header{display:flex;align-items:center;gap:10px;margin-bottom:6mm;padding-bottom:4mm;border-bottom:3px solid #00D4FF}
              .logo-hex{width:26px;height:26px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%);flex-shrink:0}
              .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:16px}
              .doc-title{font-family:'Syne',sans-serif;font-size:18px;font-weight:700;margin-bottom:1mm}
              .doc-meta{font-size:9px;color:#4A5568;margin-bottom:5mm}
              .verdict{font-size:22px;font-weight:800;padding:10px 16px;border-radius:8px;margin-bottom:4mm;display:inline-block}
              .section{margin-bottom:5mm}.section-title{font-family:'Syne',sans-serif;font-weight:700;font-size:11px;letter-spacing:0.1em;margin-bottom:2mm;padding-bottom:1mm;border-bottom:1px solid #E2E8F0}
              .text-block{font-size:11px;line-height:1.7;color:#2D3748;padding:8px 12px;background:#F7FAFC;border-radius:6px}
              .graph-summary{display:flex;gap:20px;margin-bottom:4mm;padding:8px 12px;background:#F7FAFC;border-radius:6px}
              .summary-item{text-align:center}.summary-value{font-size:18px;font-weight:700;color:#00D4FF}.summary-label{font-size:9px;color:#4A5568;margin-top:2px}
              table{width:100%;border-collapse:collapse;font-size:10px}thead td{font-weight:700;background:#E2E8F0;padding:5px 8px}td{padding:5px 8px;border-bottom:1px solid #E2E8F0;vertical-align:top}tr:nth-child(even) td{background:#F7FAFC}
              .footer{margin-top:6mm;padding-top:2mm;border-top:1px solid #E2E8F0;font-size:8px;color:#8A9BB5;display:flex;justify-content:space-between}
              @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
              </style></head><body>
              <div class="header"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
              <div class="doc-title">Crypto Analysis Report</div>
              <div class="doc-meta">Address: ${node?.address||"—"} · Risk Score: ${node?.risk||"—"}/100 · Generated: ${new Date().toLocaleString()}</div>
              <div class="verdict" style="background:${(verdictColor[result.verdict]||"#00D4FF")}18;color:${verdictColor[result.verdict]||"#00D4FF"}">${result.verdict} — ${result.confidence}% confidence</div>
              <div class="section"><div class="section-title">SUMMARY</div><div class="text-block">${result.summary||"—"}</div></div>
              <div class="section"><div class="section-title">SCORE REASONING</div><div class="text-block">${result.score_reasoning||"—"}</div></div>
              ${riskFactors?`<div class="section"><div class="section-title">RISK FACTORS (${(result.risk_factors||[]).length})</div><table><thead><tr><td>Severity</td><td>Factor</td><td>Explanation</td></tr></thead><tbody>${riskFactors}</tbody></table></div>`:""}
              ${(result.mitigating_factors && (Array.isArray(result.mitigating_factors) && result.mitigating_factors.length > 0 || typeof result.mitigating_factors === 'string' && result.mitigating_factors.trim()))?`<div class="section"><div class="section-title">MITIGATING FACTORS</div><div class="text-block">${Array.isArray(result.mitigating_factors)?result.mitigating_factors.map((f,i)=>(typeof f === 'string'?'<div style="margin-bottom:6px">'+f+'</div>':'<div style="margin-bottom:6px"><strong>'+(f.factor||'')+':</strong> '+(f.explanation||'')+'</div>')).join(""):result.mitigating_factors}</div></div>`:""}
              ${(result.recommended_actions && (Array.isArray(result.recommended_actions) && result.recommended_actions.length > 0 || typeof result.recommended_actions === 'string' && result.recommended_actions.trim()))?`<div class="section"><div class="section-title">RECOMMENDED ACTIONS</div><div class="text-block">${Array.isArray(result.recommended_actions)?result.recommended_actions.map((a,i)=>'<div style="margin-bottom:6px"><strong>'+(i+1)+'.</strong> '+(typeof a === 'string'?a:(a.action||a))+'</div>').join(""):result.recommended_actions}</div></div>`:""}
              ${result.compliance_notes?`<div class="section"><div class="section-title">COMPLIANCE NOTES</div><div class="text-block">${result.compliance_notes}</div></div>`:""}
${graphNodes.length>0?`<div class="section"><div class="section-title">TRANSACTION GRAPH</div>${graphSvg}<div class="graph-summary"><div class="summary-item"><div class="summary-value">${graphNodes.length}</div><div class="summary-label">Total Wallets</div></div><div class="summary-item"><div class="summary-value">${graphLinks.length}</div><div class="summary-label">Total Links</div></div></div></div>
              <div class="section"><div class="section-title">WALLETS (${graphNodes.length})</div><table><thead><tr><td>Address</td><td>Label</td><td>Type</td><td>Risk</td></tr></thead><tbody>${nodesTable}</tbody></table></div>
              ${graphLinks.length>0?`<div class="section"><div class="section-title">CONNECTIONS (${graphLinks.length})</div><table><thead><tr><td>Dir</td><td>From</td><td>To</td><td>Amount</td></tr></thead><tbody>${linksTable}</tbody></table></div>`:""}`:""}
              <div class="footer"><span>CrossRegGuard — Confidential</span><span>Crypto Analysis · ${new Date().toLocaleDateString()}</span></div>
              </body></html>`);
              w.document.close(); setTimeout(()=>w.print(),800);
            }} style={{background:"transparent",border:"1px solid #00D4FF",borderRadius:6,color:"#00D4FF",padding:"4px 12px",cursor:"pointer",fontSize:11,fontFamily:"'DM Mono',monospace"}}>⬇ Export PDF</button>
          </div>
          <div style={{background:(verdictColor[result.verdict]||C.accent)+"18",
            border:"1px solid "+(verdictColor[result.verdict]||C.accent)+"44",
            borderRadius:10,padding:"14px 16px",
            display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{color:verdictColor[result.verdict]||C.accent,fontSize:18,fontWeight:800}}>
                {result.verdict}
              </div>
              <div style={{color:C.textDim,fontSize:10,marginTop:3}}>
                Confidence: {result.confidence}%
                {usedName && <span style={{marginLeft:8,color:C.textDim}}>via {usedName}</span>}
              </div>
            </div>
            <svg width="56" height="56" viewBox="0 0 56 56">
              <circle cx="28" cy="28" r="22" fill="none" stroke={C.border} strokeWidth="5"/>
              <circle cx="28" cy="28" r="22" fill="none"
                stroke={verdictColor[result.verdict]||C.accent} strokeWidth="5"
                strokeDasharray={(((result.score || node.risk)/100)*138.2)+" 138.2"}
                strokeLinecap="round" transform="rotate(-90 28 28)"/>
              <text x="28" y="33" textAnchor="middle"
                fill={verdictColor[result.verdict]||C.accent}
                fontSize="13" fontWeight="700" fontFamily="monospace">
                {result.score || node.risk}
              </text>
            </svg>
          </div>

          {/* Exchange Status Box */}
          <div style={{background:walletExchangeLookup.results.length>0?"#F0B42911":"#4B555E11",
            border:`1px solid ${walletExchangeLookup.results.length>0?"#F0B42944":"#4B555E44"}`,
            borderRadius:8,padding:"12px 14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{fontSize:18}}>{walletExchangeLookup.results.length>0?"🏦":"❌"}</div>
              <div style={{flex:1}}>
                <div style={{color:walletExchangeLookup.results.length>0?"#F0B429":C.textMid,fontSize:11,fontWeight:600}}>
                  {walletExchangeLookup.results.length>0?"Exchange Connection Found":"Not Found on Known Exchanges"}
                </div>
                {walletExchangeLookup.results.length>0?(
                  <div style={{color:C.textMid,fontSize:9,marginTop:3}}>
                    Connected to: {walletExchangeLookup.results.map(r=>r.exchange_name).join(", ")}
                  </div>
                ):(
                  <div style={{color:C.textDim,fontSize:9,marginTop:3}}>
                    This wallet is not recognized as a known exchange address
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
            <div style={{color:C.textMid,fontSize:9,letterSpacing:"0.1em",marginBottom:6,fontWeight:700}}>SUMMARY</div>
            <div style={{color:C.text,fontSize:11,lineHeight:1.7}}>{result.summary}</div>
          </div>

          <div style={{background:C.bg,border:"1px solid "+C.accent+"22",borderRadius:8,padding:"12px 14px"}}>
            <div style={{color:C.accent,fontSize:9,letterSpacing:"0.1em",marginBottom:6,fontWeight:700}}>
              WHY SCORE IS {result.score || node.risk}/100
            </div>
            <div style={{color:C.text,fontSize:11,lineHeight:1.7}}>{result.score_reasoning || "Based on transaction patterns, counterparty risk assessment, and behavioral indicators."}</div>
          </div>

          {result.risk_factors?.length > 0 && (
            <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
              <div style={{color:C.textMid,fontSize:9,letterSpacing:"0.1em",marginBottom:10,fontWeight:700}}>
                RISK FACTORS ({result.risk_factors.length})
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {result.risk_factors.map((rf,i) => {
                  const sc = sevColor[rf.severity]||C.warn;
                  return (
                    <div key={i} style={{borderLeft:"3px solid "+sc,paddingLeft:10}}>
                      <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:4}}>
                        <span style={{background:sc+"22",color:sc,fontSize:8,fontWeight:700,
                          padding:"2px 7px",borderRadius:3,textTransform:"uppercase"}}>
                          {rf.severity}
                        </span>
                        <span style={{color:C.text,fontSize:11,fontWeight:600}}>{rf.factor}</span>
                      </div>
                      <div style={{color:C.textMid,fontSize:10,lineHeight:1.6}}>{rf.explanation}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {result.mitigating_factors?.length > 0 && (
            <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
              <div style={{color:C.textMid,fontSize:9,letterSpacing:"0.1em",marginBottom:10,fontWeight:700}}>
                MITIGATING FACTORS
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {result.mitigating_factors.map((mf,i) => (
                  <div key={i} style={{borderLeft:"3px solid "+C.success,paddingLeft:10}}>
                    <div style={{color:C.success,fontSize:11,fontWeight:600,marginBottom:3}}>
                      {mf.factor}
                    </div>
                    <div style={{color:C.textMid,fontSize:10,lineHeight:1.6}}>{mf.explanation}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.recommended_actions?.length > 0 && (
            <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
              <div style={{color:C.textMid,fontSize:9,letterSpacing:"0.1em",marginBottom:8,fontWeight:700}}>
                RECOMMENDED ACTIONS
              </div>
              {result.recommended_actions.map((a,i) => (
                <div key={i} style={{display:"flex",gap:9,marginBottom:7}}>
                  <span style={{color:C.accent,fontWeight:700,fontSize:10,minWidth:16}}>{i+1}.</span>
                  <span style={{color:C.text,fontSize:10,lineHeight:1.6}}>{a}</span>
                </div>
              ))}
            </div>
          )}

          {result.compliance_notes && (
            <div style={{background:C.warn+"0d",border:"1px solid "+C.warn+"33",borderRadius:8,padding:"12px 14px"}}>
              <div style={{color:C.warn,fontSize:9,letterSpacing:"0.1em",marginBottom:6,fontWeight:700}}>
                COMPLIANCE NOTES
              </div>
              <div style={{color:C.text,fontSize:10,lineHeight:1.7}}>{result.compliance_notes}</div>
            </div>
          )}

          <div style={{color:C.textDim,fontSize:8,textAlign:"center",paddingTop:4,lineHeight:1.5}}>
            AI analysis is advisory only. Verify with qualified AML compliance professionals.
          </div>
        </>
      )}

      {/* DeFi Intelligence Section */}
      {defiLoading && (
        <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
          <div style={{color:C.textMid,fontSize:10,fontWeight:700}}>Loading DeFi Intelligence...</div>
        </div>
      )}

      {defiError && !defiLoading && (
        <div style={{background:C.warn+"0d",border:"1px solid "+C.warn+"33",borderRadius:8,padding:"12px 14px"}}>
          <div style={{color:C.warn,fontSize:9,fontWeight:700,marginBottom:4}}>DeFi Intelligence Error</div>
          <div style={{color:C.text,fontSize:9}}>{defiError}</div>
        </div>
      )}

      {defiIntelligence && !defiLoading && (
        <>
          {/* Protocol Intelligence */}
          {defiIntelligence.protocolIntelligence?.protocols?.length > 0 && (
            <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
              <div style={{color:C.accent,fontSize:9,letterSpacing:"0.1em",marginBottom:8,fontWeight:700}}>
                PROTOCOL INTELLIGENCE ({defiIntelligence.protocolIntelligence.protocols.length} protocols)
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {defiIntelligence.protocolIntelligence.protocols.slice(0,5).map((p,i) => (
                  <div key={i} style={{padding:8,background:C.surfaceAlt,borderRadius:4}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                      <span style={{color:C.text,fontSize:10,fontWeight:600}}>{p.name}</span>
                      <span style={{fontSize:8,fontWeight:700,color:p.riskLevel==="low"?C.success:p.riskLevel==="medium"?"#F0B429":C.error,
                        background:(p.riskLevel==="low"?C.success:p.riskLevel==="medium"?"#F0B429":C.error)+"22",
                        padding:"2px 6px",borderRadius:3}}>{p.riskLevel.toUpperCase()}</span>
                    </div>
                    <div style={{display:"flex",gap:8,fontSize:8,color:C.textMid}}>
                      <span>TVL: ${(p.tvl/1000000).toFixed(1)}M</span>
                      {p.change7d && <span>7d: {p.change7d>0?"+":""}{p.change7d.toFixed(2)}%</span>}
                    </div>
                  </div>
                ))}
              </div>
              {defiIntelligence.protocolIntelligence.totalTvl > 0 && (
                <div style={{marginTop:8,fontSize:8,color:C.textDim}}>
                  Total TVL: ${(defiIntelligence.protocolIntelligence.totalTvl/1000000).toFixed(1)}M
                </div>
              )}
            </div>
          )}

          {/* Multi-Chain Activity */}
          {defiIntelligence.multiChainActivity && (
            <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
              <div style={{color:C.accent,fontSize:9,letterSpacing:"0.1em",marginBottom:8,fontWeight:700}}>
                MULTI-CHAIN ACTIVITY
              </div>
              {defiIntelligence.multiChainActivity.topBridges?.length > 0 && (
                <div style={{marginBottom:10}}>
                  <div style={{color:C.textMid,fontSize:8,marginBottom:4,fontWeight:700}}>Top Bridges</div>
                  {defiIntelligence.multiChainActivity.topBridges.slice(0,3).map((b,i) => (
                    <div key={i} style={{fontSize:8,color:C.text,marginBottom:2}}>
                      • {b.name} {b.volume24h ? `($${(b.volume24h/1000000).toFixed(1)}M)` : ""}
                    </div>
                  ))}
                </div>
              )}
              {defiIntelligence.multiChainActivity.topDexs?.length > 0 && (
                <div>
                  <div style={{color:C.textMid,fontSize:8,marginBottom:4,fontWeight:700}}>Top DEXs</div>
                  {defiIntelligence.multiChainActivity.topDexs.slice(0,3).map((d,i) => (
                    <div key={i} style={{fontSize:8,color:C.text,marginBottom:2}}>
                      • {d.name} {d.volume24h ? `($${(d.volume24h/1000000).toFixed(1)}M)` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Risk Metrics */}
          {defiIntelligence.riskMetrics && (
            <div style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"12px 14px"}}>
              <div style={{color:C.accent,fontSize:9,letterSpacing:"0.1em",marginBottom:8,fontWeight:700}}>
                DeFI RISK METRICS
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                <div style={{background:C.surfaceAlt,padding:8,borderRadius:4}}>
                  <div style={{color:C.textMid,fontSize:8,marginBottom:3}}>Overall Score</div>
                  <div style={{fontSize:14,fontWeight:700,color:defiIntelligence.riskMetrics.overallRiskScore>70?C.error:defiIntelligence.riskMetrics.overallRiskScore>40?"#F0B429":C.success}}>
                    {defiIntelligence.riskMetrics.overallRiskScore}
                  </div>
                </div>
                <div style={{background:C.surfaceAlt,padding:8,borderRadius:4}}>
                  <div style={{color:C.textMid,fontSize:8,marginBottom:3}}>Liquidity Status</div>
                  <div style={{fontSize:10,fontWeight:600,color:defiIntelligence.riskMetrics.liquidityRisks?.bridgeLiquidity==="adequate"?C.success:C.warn}}>
                    {defiIntelligence.riskMetrics.liquidityRisks?.bridgeLiquidity==="adequate"?"✓ Adequate":"⚠ Limited"}
                  </div>
                </div>
              </div>
              {defiIntelligence.riskMetrics.protocolRisks && (
                <div style={{fontSize:8,color:C.textDim,display:"flex",gap:12}}>
                  <span>High-risk protocols: {defiIntelligence.riskMetrics.protocolRisks.highRiskCount}</span>
                  <span>Med-risk: {defiIntelligence.riskMetrics.protocolRisks.mediumRiskCount}</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Graph Canvas ───────────────────────────────────────────────────────────────
const HOST_TYPES = [
  {id:"unknown",    label:"Unknown",    icon:"👛"},
  {id:"exchange",   label:"Exchange",   icon:"🏦"},
  {id:"defi",       label:"DeFi",       icon:"⚙️"},
  {id:"mixer",      label:"Mixer",      icon:"🌀"},
  {id:"sanctioned", label:"Sanctioned", icon:"🚫"},
  {id:"gambling",   label:"Gambling",   icon:"🎰"},
  {id:"atm",        label:"Crypto ATM", icon:"🏧"},
  {id:"scam",       label:"Scam/Fraud", icon:"⚠️"},
];

function GraphCanvas({graph, setGraph, selectedNode, setSelectedNode, network, fetchWithFallback, onOpenDrawer, onNodeClick, ethPrice=0, S, initLockPhysics=false, onAddLabel}) {
  const C = useTheme();
  const canvasRef      = useRef(null);
  const stateRef       = useRef({nodes:[], links:[], animId:null});
  const dragRef        = useRef(null);
  const linkSrcRef     = useRef(null);
  const layoutLockRef  = useRef(false); // when true, physics is paused
  const mouseRef   = useRef({x:0, y:0});
  const modeRef    = useRef("select");
  const selRef     = useRef(null);

  const [mode,        setMode]        = useState("select");
  const [physicsLocked, setPhysicsLocked] = useState(false);
  const [showAddNode, setShowAddNode] = useState(false);
  const [showEditNode,setShowEditNode]= useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkStep,    setLinkStep]    = useState(0);
  const [linkSrc,     setLinkSrc]     = useState(null);
  const [pendingPos,  setPendingPos]  = useState({x:390, y:210});
  const [newAddr,     setNewAddr]     = useState("");
  const [newType,     setNewType]     = useState("unknown");
  const [newLabel,    setNewLabel]    = useState("");
  const [newRisk,     setNewRisk]     = useState(40);
  const [editType,    setEditType]    = useState("unknown");
  const [editLabel,   setEditLabel]   = useState("");
  const [editRisk,    setEditRisk]    = useState(40);
  const [linkAmount,  setLinkAmount]  = useState("0");
  const [linkDir,     setLinkDir]     = useState("out");
  const [linkNote,    setLinkNote]    = useState("");

  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { selRef.current = selectedNode; }, [selectedNode]);
  // Sync external physics-lock prop (used when a tx hash graph is built)
  useEffect(() => {
    layoutLockRef.current = initLockPhysics;
    setPhysicsLocked(initLockPhysics);
  }, [initLockPhysics]);

  useEffect(() => {
    const W=1400, H=500;
    const prev = Object.fromEntries(stateRef.current.nodes.map(n=>[n.id,n]));
    if (!graph) { stateRef.current.nodes=[]; stateRef.current.links=[]; return; }
    // Hard cap: never render more than 5 exchange nodes regardless of graph state
    const capped = capUnknowns(capExchanges(graph));
    stateRef.current.nodes = capped.nodes.map((n,i) => {
      const p = prev[n.id];
      if (p) return {...n, x:p.x, y:p.y, vx:0, vy:0};
      // Honour pre-set positions (e.g. tx-hash graphs that are pre-laid-out)
      if (n.x != null && n.y != null) return {...n, vx:0, vy:0};
      if (i===0) return {...n, x:W/2, y:H/2, vx:0, vy:0};
      const ring=Math.floor((i-1)/12), angle=((i-1)%12)/12*Math.PI*2, radius=140+ring*110;
      return {...n, x:W/2+Math.cos(angle)*radius, y:H/2+Math.sin(angle)*radius, vx:0, vy:0};
    });
    stateRef.current.links = capped.links.map(l=>({...l}));
  }, [graph]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;

    function tick() {
      const {nodes, links} = stateRef.current;
      // Cap at 60 nodes for performance — show origin + highest-risk nodes
      const visNodes = nodes.length > 60
        ? [nodes[0], ...nodes.slice(1).sort((a,b)=>(b.risk||0)-(a.risk||0)).slice(0,59)]
        : nodes;
      const nm = Object.fromEntries(nodes.map(n=>[n.id,n]));

      // Physics — skip entirely when layout is locked (flow layout mode)
      let totalKE = 0;
      if (!layoutLockRef.current) {
        const MIN_D2 = (NODE_W+20)*(NODE_W+20);
        for (let i=0; i<visNodes.length; i++) {
          for (let j=i+1; j<visNodes.length; j++) {
            const a=visNodes[i], b=visNodes[j];
            const dx=b.x-a.x, dy=b.y-a.y;
            const d2=Math.max(dx*dx+dy*dy, 1);
            const f = d2 < MIN_D2 ? 6000/d2 : 2000/d2;
            a.vx-=f*dx; a.vy-=f*dy; b.vx+=f*dx; b.vy+=f*dy;
          }
        }
        links.forEach(l => {
          const s=nm[l.source?.id||l.source], t=nm[l.target?.id||l.target];
          if (!s||!t) return;
          const dx=t.x-s.x, dy=t.y-s.y;
          const d=Math.sqrt(dx*dx+dy*dy)||1, f=(d-180)*0.025;
          s.vx+=f*dx/d; s.vy+=f*dy/d; t.vx-=f*dx/d; t.vy-=f*dy/d;
        });
        visNodes.forEach(n => {
          n.vx += (W/2-n.x)*0.0008; n.vy += (H/2-n.y)*0.0008;
          n.vx *= 0.72; n.vy *= 0.72;
          if (!n.fixed) { n.x+=n.vx; n.y+=n.vy; }
          n.x = Math.max(NODE_W/2+4, Math.min(W-NODE_W/2-4, n.x));
          n.y = Math.max(NODE_H/2+4, Math.min(H-NODE_H/2-4, n.y));
          totalKE += n.vx*n.vx + n.vy*n.vy;
        });
      }
      // Once settled (low kinetic energy), slow down the animation
      stateRef.current.settled = totalKE < 0.5;

      ctx.clearRect(0,0,W,H);
      const selId = selRef.current?.id||"";
      const srcId = linkSrcRef.current?.id||"";

      links.forEach(l => {
        const s=nm[l.source?.id||l.source], t=nm[l.target?.id||l.target];
        if (!s||!t) return;
        // Connect from rectangle edge to rectangle edge
        const sp = getRectEdge(s.x, s.y, t.x, t.y);
        const tp = getRectEdge(t.x, t.y, s.x, s.y);
        const dx=tp.x-sp.x, dy=tp.y-sp.y, d=Math.sqrt(dx*dx+dy*dy)||1;
        const isIn = l.direction==="in";
        const lineColor = isIn ? "rgba(0,230,118,0.55)" : "rgba(0,212,255,0.45)";
        const arrowColor = isIn ? "rgba(0,230,118,0.85)" : "rgba(0,212,255,0.75)";
        ctx.beginPath(); ctx.moveTo(sp.x, sp.y); ctx.lineTo(tp.x, tp.y);
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = Math.max(1.5, Math.min(4.5, 1.5+(l.amount||0)*0.4));
        ctx.stroke();
        // Arrowhead at target end
        const ang=Math.atan2(dy,dx);
        ctx.beginPath(); ctx.moveTo(tp.x,tp.y);
        ctx.lineTo(tp.x-10*Math.cos(ang-0.38), tp.y-10*Math.sin(ang-0.38));
        ctx.lineTo(tp.x-10*Math.cos(ang+0.38), tp.y-10*Math.sin(ang+0.38));
        ctx.closePath(); ctx.fillStyle=arrowColor; ctx.fill();
        if (l.amount > 0) {
          const mx=(sp.x+tp.x)/2, my=(sp.y+tp.y)/2;
          const ethAmt = parseFloat(l.amount)||0;
          const usdStr = ethPrice>0&&ethAmt>0 ? " ("+fmtUSD(ethAmt,ethPrice)+")" : "";
          const lbl = fmtNative(ethAmt,network)+usdStr+(l.count>1?" ×"+l.count:"");
          ctx.font = "9px monospace";
          const tw = ctx.measureText(lbl).width;
          ctx.fillStyle = "rgba(8,13,20,0.85)";
          ctx.beginPath(); ctx.roundRect(mx-tw/2-5, my-14, tw+10, 15, 3); ctx.fill();
          ctx.fillStyle = isIn ? "rgba(0,230,118,0.95)" : "rgba(0,212,255,0.95)";
          ctx.textAlign = "center"; ctx.textBaseline = "alphabetic"; ctx.fillText(lbl, mx, my-2);
        }
      });

      if (modeRef.current==="addLink" && linkSrcRef.current) {
        const s = nm[linkSrcRef.current.id];
        if (s) {
          ctx.beginPath(); ctx.moveTo(s.x,s.y);
          ctx.lineTo(mouseRef.current.x, mouseRef.current.y);
          ctx.strokeStyle = "rgba(255,170,0,0.6)"; ctx.lineWidth=2;
          ctx.setLineDash([6,4]); ctx.stroke(); ctx.setLineDash([]);
        }
      }

      visNodes.forEach(n => {
        const col = NODE_COLORS[n.type]||NODE_COLORS.unknown;
        const isSel=n.id===selId, isSrc=n.id===srcId;
        const meta = CATEGORY_META[n.type]||CATEGORY_META.unknown;
        const NW = NODE_W, NH = NODE_H, cr = 8;
        const nx = n.x-NW/2, ny = n.y-NH/2;

        // ── Outer glow for selected / high-risk ──
        if (isSel || isSrc || n.risk>=80) {
          ctx.save();
          ctx.shadowColor = isSrc ? "#FFAA00" : col;
          ctx.shadowBlur  = isSel ? 20 : 12;
          ctx.strokeStyle = isSrc ? "#FFAA00" : col;
          ctx.lineWidth   = isSel ? 2.5 : 1.5;
          ctx.beginPath(); ctx.roundRect(nx-2, ny-2, NW+4, NH+4, cr+2);
          ctx.stroke();
          ctx.restore();
        }

        // ── Pulsing threat ring (sanctioned / darknet / ransomware) ──
        if (["sanctioned","darknet","ransomware"].includes(n.type)) {
          const pulse = 0.5+0.5*Math.sin(Date.now()/380);
          ctx.beginPath(); ctx.roundRect(nx-4-pulse*3, ny-4-pulse*3, NW+8+pulse*6, NH+8+pulse*6, cr+4);
          ctx.strokeStyle = `rgba(255,34,68,${0.3+pulse*0.5})`; ctx.lineWidth=2; ctx.stroke();
        }

        // ── Card background ──
        ctx.beginPath(); ctx.roundRect(nx, ny, NW, NH, cr);
        ctx.fillStyle = "rgba(10,16,28,0.93)"; ctx.fill();
        ctx.strokeStyle = isSel ? col : isSrc ? "#FFAA00" : col+"55";
        ctx.lineWidth = isSel||isSrc ? 2 : 1; ctx.stroke();

        // ── Left accent stripe ──
        ctx.save();
        ctx.beginPath(); ctx.roundRect(nx, ny, 5, NH, [cr,0,0,cr]);
        ctx.fillStyle = col; ctx.fill();
        ctx.restore();

        // ── Entity icon (left zone) ──
        ctx.font = "16px sans-serif";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(meta.icon, nx+18, n.y);

        // ── Main label (truncated) ──
        const rawLabel = n.tag||(n.id.slice(0,6)+"..."+n.id.slice(-4));
        ctx.font = (isSel?"bold ":"")+"10px monospace";
        ctx.textAlign = "left"; ctx.textBaseline = "middle";
        ctx.fillStyle = "#E8EDF5";
        const maxLW = NW - 58;
        let lbl = rawLabel;
        while (ctx.measureText(lbl).width > maxLW && lbl.length > 5) lbl = lbl.slice(0,-1);
        if (lbl !== rawLabel) lbl += "…";

        // Show exchange sub-type on separate line only for exchange nodes
        const isExchange = n.type === "exchange";
        const sub = isExchange ? getExchangeSubtype(n.tag||"") : null;
        const labelY = isExchange ? n.y - 10 : n.y - 5;

        ctx.fillText(lbl, nx+30, labelY);

        // ── Type / sub-type label ──
        if (isExchange && sub) {
          ctx.font = "8px monospace"; ctx.fillStyle = sub.color;
          ctx.fillText(sub.icon+" "+sub.sub, nx+30, n.y+5);
          ctx.font = "7px sans-serif"; ctx.fillStyle = col+"99";
          ctx.fillText(meta.label, nx+30, n.y+16);
        } else if (n.type !== "unknown" && n.type !== "origin") {
          ctx.font = "8px sans-serif"; ctx.fillStyle = col+"bb";
          ctx.fillText(meta.icon+" "+meta.label, nx+30, n.y+7);
        }

        // ── Risk badge (top-right corner) ──
        if (n.type !== "origin") {
          const riskCol = n.risk>=80?"#FF2244":n.risk>=50?"#F59E0B":"#10B981";
          const bx = nx+NW-30, by = ny+3;
          ctx.beginPath(); ctx.roundRect(bx, by, 26, 14, 3);
          ctx.fillStyle = riskCol+"28"; ctx.fill();
          ctx.strokeStyle = riskCol+"55"; ctx.lineWidth=1; ctx.stroke();
          ctx.font = "bold 8px monospace"; ctx.textAlign = "center";
          ctx.textBaseline = "middle"; ctx.fillStyle = riskCol;
          ctx.fillText(n.risk, bx+13, by+7);
        }

        // ── Origin star badge ──
        if (n.type === "origin") {
          ctx.font = "bold 9px monospace"; ctx.textAlign = "center";
          ctx.textBaseline = "middle"; ctx.fillStyle = "#00D4FF";
          ctx.fillText("ORIGIN", nx+NW/2, ny+NH+9);
        }
      });

      stateRef.current.animId = requestAnimationFrame(tick);
    }

    stateRef.current.settled = false;
    stateRef.current.animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(stateRef.current.animId);
  }, []); // eslint-disable-line

  function xy(e) {
    const r=canvasRef.current.getBoundingClientRect();
    const sx=canvasRef.current.width/r.width, sy=canvasRef.current.height/r.height;
    return {x:(e.clientX-r.left)*sx, y:(e.clientY-r.top)*sy};
  }
  function hitNode(mx,my) {
    // Iterate reversed so topmost-drawn node wins on overlap
    const nodes = stateRef.current.nodes;
    for (let i=nodes.length-1; i>=0; i--) {
      const n = nodes[i];
      if (mx >= n.x-NODE_W/2-3 && mx <= n.x+NODE_W/2+3 &&
          my >= n.y-NODE_H/2-3 && my <= n.y+NODE_H/2+3) return n;
    }
    return null;
  }

  function onMD(e) {
    const {x,y}=xy(e), n=hitNode(x,y);
    if (mode==="addNode" && !n) {
      setPendingPos({x,y}); setNewAddr(""); setNewLabel(""); setNewType("unknown"); setNewRisk(40);
      setShowAddNode(true); return;
    }
    if (mode==="addLink") {
      if (!linkSrcRef.current) {
        if (n) { linkSrcRef.current=n; setLinkSrc(n); setLinkStep(1); }
      } else {
        if (n && n.id!==linkSrcRef.current.id) {
          setLinkAmount("0"); setLinkDir("out"); setLinkNote("");
          setShowAddLink({src:linkSrcRef.current, tgt:n});
          linkSrcRef.current=null; setLinkSrc(null); setLinkStep(0);
        } else if (!n) { linkSrcRef.current=null; setLinkSrc(null); setLinkStep(0); }
      }
      return;
    }
    if (n) { n.fixed=true; dragRef.current={node:n, moved:false, sx:x, sy:y}; }
  }
  function onMM(e) {
    const {x,y}=xy(e); mouseRef.current={x,y};
    if (dragRef.current) { dragRef.current.node.x=x; dragRef.current.node.y=y; dragRef.current.moved=true; }
  }
  function onMU(e) {
    const {x,y}=xy(e), n=hitNode(x,y);
    if (dragRef.current) {
      dragRef.current.node.fixed = false;
      const dist = Math.abs(x-dragRef.current.sx)+Math.abs(y-dragRef.current.sy);
      const wasDrag = dragRef.current.moved && dist>6;
      if (!wasDrag && n) {
        if (mode==="select") {
          setSelectedNode({...n}); setEditType(n.type); setEditLabel(n.tag||""); setEditRisk(n.risk);
          if (onNodeClick) onNodeClick(n);
        }
        if (mode==="delete") { doDelNode(n.id); }
      }
      dragRef.current = null;
    }
  }
  // Double-click a node to open its transaction drawer
  function onDblClick(e) {
    const {x,y}=xy(e), n=hitNode(x,y);
    if (n && mode==="select" && onOpenDrawer) onOpenDrawer(n);
  }
  // Right-click a node to expand trace into the graph
  function onContextMenu(e) {
    e.preventDefault();
    const {x,y}=xy(e), n=hitNode(x,y);
    if (n && n.type!=="origin" && mode==="select") {
      fetchWithFallback(n.id);
    }
  }

  function doAddNode() {
    const id = newAddr.trim() || "manual-"+Date.now();
    const n = {id, label:newLabel.trim()||id.slice(0,6)+"..."+id.slice(-4), tag:newLabel.trim()||null,
      type:newType, risk:+newRisk||40, value:60, x:pendingPos.x, y:pendingPos.y, vx:0, vy:0};
    stateRef.current.nodes.push(n);
    setGraph(g => capUnknowns(capExchanges({nodes:[...(g?.nodes||[]),n], links:g?.links||[]})));
    setShowAddNode(false);
  }
  function doEditNode() {
    stateRef.current.nodes = stateRef.current.nodes.map(n =>
      n.id===selectedNode.id ? {...n, type:editType, tag:editLabel||null, risk:+editRisk||n.risk} : n
    );
    setGraph(g => g ? capUnknowns(capExchanges({nodes:g.nodes.map(n=>n.id===selectedNode.id?{...n,type:editType,tag:editLabel||null,risk:+editRisk||n.risk}:n), links:g.links})) : null);
    setSelectedNode(n=>({...n, type:editType, tag:editLabel||null, risk:+editRisk||n.risk}));
    setShowEditNode(false);
  }
  function doDelNode(id) {
    stateRef.current.nodes = stateRef.current.nodes.filter(n=>n.id!==id);
    stateRef.current.links = stateRef.current.links.filter(l=>(l.source?.id||l.source)!==id && (l.target?.id||l.target)!==id);
    setGraph(g=> g ? ({
      nodes:g.nodes.filter(n=>n.id!==id),
      links:g.links.filter(l=>(l.source?.id||l.source)!==id && (l.target?.id||l.target)!==id)
    }) : null);
    if (selectedNode?.id===id) { setSelectedNode(null); setShowEditNode(false); }
  }
  function doAddLink({src,tgt}) {
    const l = {source:src.id, target:tgt.id, amount:parseFloat(linkAmount)||0, direction:linkDir, note:linkNote.trim()||null, manual:true, count:1};
    stateRef.current.links.push(l);
    setGraph(g=> g ? ({nodes:g.nodes, links:[...(g?.links||[]),l]}) : null);
    setShowAddLink(false);
  }
  function doDelLink(i) {
    stateRef.current.links.splice(i,1);
    setGraph(g=> g ? ({nodes:g.nodes, links:g.links.filter((_,j)=>j!==i)}) : null);
  }

  // ── Flow Layout — arranges nodes left→right by hop distance from origin ────
  function applyFlowLayout() {
    const nodes = stateRef.current.nodes;
    const links = stateRef.current.links;
    if (!nodes.length) return;
    const W = 1200, H = 480;
    const origin = nodes.find(n => n.type==="origin") || nodes[0];
    // BFS to assign hop depth
    const depth = new Map([[origin.id, 0]]);
    const queue = [origin.id];
    while (queue.length) {
      const curr = queue.shift();
      const d = depth.get(curr);
      links.forEach(l => {
        const s=l.source?.id||l.source, t=l.target?.id||l.target;
        const neighbor = s===curr ? t : t===curr ? s : null;
        if (neighbor && !depth.has(neighbor)) { depth.set(neighbor, d+1); queue.push(neighbor); }
      });
    }
    const maxDepth = Math.max(0, ...[...depth.values()]);
    nodes.forEach(n => { if (!depth.has(n.id)) depth.set(n.id, maxDepth+1); });
    // Group into columns
    const cols = new Map();
    nodes.forEach(n => {
      const d = depth.get(n.id)??0;
      if (!cols.has(d)) cols.set(d, []);
      cols.get(d).push(n);
    });
    const totalCols = Math.max(...cols.keys())+1;
    const colW = Math.min(NODE_W+60, (W-100)/Math.max(totalCols-1,1));
    cols.forEach((colNodes, d) => {
      const x = 70 + d*colW;
      const rowH = H / (colNodes.length+1);
      colNodes.forEach((n,i) => {
        n.x = x; n.y = rowH*(i+1); n.vx=0; n.vy=0;
      });
    });
    // Lock physics so the layout stays — user can drag individual nodes freely
    layoutLockRef.current = true;
    setPhysicsLocked(true);
  }

  const G = graph||{nodes:[],links:[]};

  const M = {
    ov:{position:"fixed",inset:0,background:"rgba(2,4,8,0.85)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center"},
    bx:{background:C.surface,border:"1px solid "+C.border,borderRadius:14,padding:24,width:360,maxWidth:"92vw"},
    ti:{color:C.text,fontSize:15,fontWeight:700,marginBottom:16},
    f: {marginBottom:12},
    l: {color:C.textMid,fontSize:10,letterSpacing:"0.08em",display:"block",marginBottom:5},
    i: {background:C.bg,border:"1px solid "+C.border,borderRadius:7,color:C.text,fontSize:12,padding:"9px 12px",fontFamily:"monospace",outline:"none",width:"100%",boxSizing:"border-box"},
    rw:{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16},
    pr:{padding:"9px 18px",background:C.accent,border:"none",borderRadius:7,color:C.bg,fontWeight:700,cursor:"pointer",fontFamily:"monospace",fontSize:12},
    sc:{padding:"9px 18px",background:"transparent",border:"1px solid "+C.border,borderRadius:7,color:C.textMid,cursor:"pointer",fontFamily:"monospace",fontSize:12},
    de:{padding:"9px 18px",background:C.danger+"22",border:"1px solid "+C.danger+"55",borderRadius:7,color:C.danger,cursor:"pointer",fontFamily:"monospace",fontSize:12},
  };

  const TOOLS = [
    {id:"select",  l:"Select"},
    {id:"addNode", l:"+ Add Node"},
    {id:"addLink", l:"Add Link"},
    {id:"delete",  l:"Delete"},
  ];

  return (
    <>
      {/* ── Toolbar row 1: mode tools + flow layout ── */}
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
        {TOOLS.map(t=>(
          <button key={t.id}
            onClick={()=>{setMode(t.id); linkSrcRef.current=null; setLinkSrc(null); setLinkStep(0);}}
            style={S.btn(mode===t.id, t.id==="delete"?C.danger:C.accent)}>
            {t.l}
          </button>
        ))}

        <div style={{width:1,height:20,background:C.border,margin:"0 2px"}}/>

        {/* Flow Layout — arranges by transaction hop order */}
        <button onClick={applyFlowLayout} title="Arrange nodes left→right by transaction order"
          style={S.btn(physicsLocked,"#A855F7")}>
          ⟶ Flow Layout
        </button>
        {physicsLocked && (
          <button
            onClick={()=>{ layoutLockRef.current=false; setPhysicsLocked(false); stateRef.current.nodes.forEach(n=>{n.vx=0;n.vy=0;}); }}
            title="Re-enable physics simulation"
            style={S.btn(false,C.warn)}>
            ↺ Free Physics
          </button>
        )}

        {mode==="addLink" && (
          <span style={{color:C.warn,fontSize:11,marginLeft:4}}>
            {linkStep===0 ? "Click SOURCE node" : "Click TARGET node"}
          </span>
        )}
        {mode==="select" && (
          <span style={{color:C.textDim,fontSize:10,marginLeft:4}}>
            Drag to reorder · Double-click to view txns · Right-click to expand trace
          </span>
        )}
        <div style={{marginLeft:"auto", display:"flex", gap:8}}>
          {/* Item 19: Add custom wallet label button */}
          {onAddLabel && (
            <button onClick={onAddLabel} style={{...S.btn(false), color:C.accent, border:`1px solid ${C.accent}55`}}>
              + Label Wallet
            </button>
          )}
          <button
            onClick={()=>{ stateRef.current.links=stateRef.current.links.filter(l=>!l.manual); setGraph(g=> g ? ({nodes:g.nodes,links:g.links.filter(l=>!l.manual)}) : null); }}
            style={S.btn(false)}>
            Clear manual links
          </button>
        </div>
      </div>


      <div style={{...S.card, position:"relative", marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
          <span style={{color:C.textMid,fontSize:11,letterSpacing:"0.08em",paddingTop:2}}>
            TRANSACTION GRAPH
            {mode==="addLink" && <span style={{color:C.warn,marginLeft:8}}>LINK MODE</span>}
            {mode==="delete"  && <span style={{color:C.danger,marginLeft:8}}>DELETE MODE</span>}
          </span>
          {/* Legend — shows all entity types present + exchange sub-type breakdown */}
          <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"flex-end",maxWidth:"70%"}}>
            {Object.entries(capUnknowns(capExchanges(G)).nodes.reduce((a,n)=>({...a,[n.type]:(a[n.type]||0)+1}),{})).map(([type,cnt])=>{
              const m = CATEGORY_META[type]||CATEGORY_META.unknown;
              const col = NODE_COLORS[type]||C.textDim;
              // For exchange nodes, group sub-types in the legend
              if (type==="exchange") {
                const exNodes = G.nodes.filter(n=>n.type==="exchange");
                const subCounts = {};
                exNodes.forEach(n => {
                  const s = getExchangeSubtype(n.tag||"");
                  subCounts[s.sub] = (subCounts[s.sub]||0)+1;
                });
                return (
                  <div key={type} style={{display:"flex",flexDirection:"column",gap:2,alignItems:"flex-end"}}>
                    <span style={{background:col+"18",border:"1px solid "+col+"44",
                      borderRadius:5,padding:"2px 8px",fontSize:9,color:col,fontWeight:700}}>
                      {m.icon} Exchange ({cnt})
                    </span>
                    <div style={{display:"flex",gap:3,flexWrap:"wrap",justifyContent:"flex-end"}}>
                      {Object.entries(subCounts).map(([sub,c])=>{
                        const sd = getExchangeSubtype(sub==="Hot Wallet"?"Hot Wallet":sub==="Cold Wallet"?"Cold Wallet":sub==="Custody"?"custody":sub==="Prime / Inst"?"prime":sub);
                        return (
                          <span key={sub} style={{background:sd.color+"15",border:"1px solid "+sd.color+"40",
                            borderRadius:4,padding:"1px 6px",fontSize:8,color:sd.color}}>
                            {sd.icon} {sub} ({c})
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return (
                <span key={type} style={{background:col+"18",border:"1px solid "+col+"44",
                  borderRadius:5,padding:"2px 8px",fontSize:9,color:col,fontWeight:700}}>
                  {m.icon} {m.label} ({cnt})
                </span>
              );
            })}
          </div>
        </div>
        {G.nodes.length===0 && (
          <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",
            justifyContent:"center",flexDirection:"column",gap:10,color:C.textDim,fontSize:13,pointerEvents:"none"}}>
            <div style={{fontSize:34}}>🕸</div>
            <div>Trace a wallet — or use Add Node to build manually</div>
          </div>
        )}
        <canvas ref={canvasRef} width={1400} height={500}
          style={{width:"100%",height:500,borderRadius:8,display:"block",
            cursor:mode==="addNode"?"crosshair":mode==="delete"?"not-allowed":mode==="addLink"?"cell":"default"}}
          onMouseDown={onMD} onMouseMove={onMM} onMouseUp={onMU}
          onDoubleClick={onDblClick} onContextMenu={onContextMenu}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:14,marginBottom:14}}>
        <div style={S.card}>
          <span style={S.label}>SELECTED NODE</span>
          {!selectedNode
            ? <div style={{color:C.textDim,fontSize:11,padding:"10px 0"}}>Click a node to inspect it.</div>
            : (
              <>
                <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:8}}>
                  <span style={{fontSize:16}}>{CATEGORY_META[selectedNode.type]?.icon||"👛"}</span>
                  <div>
                    <div style={{color:NODE_COLORS[selectedNode.type]||C.accent,fontSize:11,fontWeight:700}}>
                      {selectedNode.tag||CATEGORY_META[selectedNode.type]?.label||selectedNode.type}
                    </div>
                    {selectedNode.type==="exchange" && (()=>{
                      const sub = getExchangeSubtype(selectedNode.tag||"");
                      return (
                        <div style={{fontSize:9,color:sub.color,fontWeight:600,marginTop:1}}>
                          {sub.icon} {sub.sub}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div style={{color:C.textMid,fontSize:9,fontFamily:"monospace",wordBreak:"break-all",marginBottom:12}}>
                  {selectedNode.id}
                </div>
                <div style={{marginBottom:12}}>
                  <RiskGauge risk={selectedNode.risk} C={C} />
                </div>
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  {[
                    {l:"Edit",  fn:()=>{setEditType(selectedNode.type);setEditLabel(selectedNode.tag||"");setEditRisk(selectedNode.risk);setShowEditNode(true);}},
                    {l:"View",  fn:()=>{const e=EXPLORERS.find(e=>e.network===network);if(e)window.open("https://"+e.url+"/address/"+selectedNode.id,"_blank");}},
                    {l:"Trace", fn:()=>fetchWithFallback(selectedNode.id)},
                    {l:"Txns",  fn:()=>onOpenDrawer&&onOpenDrawer(selectedNode)},
                  ].map(a=>(
                    <button key={a.l} onClick={a.fn}
                      style={{padding:"5px 9px",background:C.bg,border:"1px solid "+C.border,borderRadius:5,color:C.accent,cursor:"pointer",fontFamily:"monospace",fontSize:9}}>
                      {a.l}
                    </button>
                  ))}
                  <button onClick={()=>doDelNode(selectedNode.id)}
                    style={{padding:"5px 9px",background:C.danger+"11",border:"1px solid "+C.danger+"44",borderRadius:5,color:C.danger,cursor:"pointer",fontFamily:"monospace",fontSize:9}}>
                    Remove
                  </button>
                </div>
              </>
            )
          }
        </div>

        <div style={S.card}>
          <span style={S.label}>LINKS ({G.links.length})</span>
          <div style={{maxHeight:160,overflowY:"auto",display:"flex",flexDirection:"column",gap:5}}>
            {G.links.length===0
              ? <div style={{fontSize:11,padding:"12px 8px",color:C.textDim}}>No connections yet. Add nodes and link them together.</div>
              : G.links.map((l,i)=>{
                  const sid=l.source?.id||l.source, tid=l.target?.id||l.target;
                  const sn=G.nodes.find(n=>n.id===sid), tn=G.nodes.find(n=>n.id===tid);
                  const col=l.direction==="in"?C.success:C.accent;
                  return (
                    <div key={i} style={{background:C.bg,border:"1px solid "+C.border,borderRadius:5,padding:"6px 9px",display:"flex",alignItems:"center",gap:6}}>
                      <span style={{color:col,fontSize:13}}>{l.direction==="in"?"↓":"↑"}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{color:C.text,fontSize:9,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {sn?.tag||sid.slice(0,10)+"..."} to {tn?.tag||tid.slice(0,10)+"..."}
                        </div>
                        {l.amount>0 && (
                          <div style={{color:C.textDim,fontSize:8}}>
                            {fmtNative(l.amount,network)}{ethPrice>0&&l.amount>0?" · "+fmtUSD(l.amount,ethPrice):""}{l.manual?" (manual)":""}
                          </div>
                        )}
                      </div>
                      <button onClick={()=>doDelLink(i)}
                        style={{background:"transparent",border:"none",color:C.danger+"77",cursor:"pointer",fontSize:11,padding:"0 2px"}}>
                        x
                      </button>
                    </div>
                  );
                })
            }
          </div>
        </div>
      </div>

      {showAddNode && (
        <div style={M.ov} onClick={e=>{if(e.target===e.currentTarget)setShowAddNode(false);}}>
          <div style={M.bx}>
            <div style={M.ti}>Add Node</div>
            <div style={M.f}><span style={M.l}>ADDRESS (optional)</span><input style={M.i} value={newAddr} onChange={e=>setNewAddr(e.target.value)} placeholder="0x... or leave blank"/></div>
            <div style={M.f}><span style={M.l}>DISPLAY LABEL</span><input style={M.i} value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="e.g. Suspect Exchange"/></div>
            <div style={M.f}>
              <span style={M.l}>HOST TYPE</span>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5}}>
                {HOST_TYPES.map(ht=>(
                  <button key={ht.id} onClick={()=>setNewType(ht.id)}
                    style={{padding:"6px 8px",borderRadius:5,cursor:"pointer",
                      background:newType===ht.id?(NODE_COLORS[ht.id]||C.accent)+"22":"transparent",
                      border:"1px solid "+(newType===ht.id?(NODE_COLORS[ht.id]||C.accent):C.border),
                      color:newType===ht.id?(NODE_COLORS[ht.id]||C.accent):C.textMid,
                      fontFamily:"monospace",fontSize:9,textAlign:"left"}}>
                    {ht.icon} {ht.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={M.f}>
              <span style={M.l}>RISK SCORE: {newRisk}</span>
              <input type="range" min={0} max={100} value={newRisk} onChange={e=>setNewRisk(e.target.value)} style={{width:"100%",accentColor:C.accent}}/>
            </div>
            <div style={M.rw}>
              <button style={M.sc} onClick={()=>setShowAddNode(false)}>Cancel</button>
              <button style={M.pr} onClick={doAddNode}>Add Node</button>
            </div>
          </div>
        </div>
      )}

      {showEditNode && selectedNode && (
        <div style={M.ov} onClick={e=>{if(e.target===e.currentTarget)setShowEditNode(false);}}>
          <div style={M.bx}>
            <div style={M.ti}>Edit Node</div>
            <div style={{color:C.textDim,fontSize:9,fontFamily:"monospace",marginBottom:14,wordBreak:"break-all"}}>
              {selectedNode.id}
            </div>
            <div style={M.f}><span style={M.l}>LABEL</span><input style={M.i} value={editLabel} onChange={e=>setEditLabel(e.target.value)} placeholder="e.g. Suspect Exchange"/></div>
            <div style={M.f}>
              <span style={M.l}>HOST TYPE</span>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5}}>
                {HOST_TYPES.map(ht=>(
                  <button key={ht.id} onClick={()=>setEditType(ht.id)}
                    style={{padding:"6px 8px",borderRadius:5,cursor:"pointer",
                      background:editType===ht.id?(NODE_COLORS[ht.id]||C.accent)+"22":"transparent",
                      border:"1px solid "+(editType===ht.id?(NODE_COLORS[ht.id]||C.accent):C.border),
                      color:editType===ht.id?(NODE_COLORS[ht.id]||C.accent):C.textMid,
                      fontFamily:"monospace",fontSize:9,textAlign:"left"}}>
                    {ht.icon} {ht.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={M.f}>
              <span style={M.l}>RISK SCORE: {editRisk}</span>
              <input type="range" min={0} max={100} value={editRisk} onChange={e=>setEditRisk(e.target.value)} style={{width:"100%",accentColor:C.accent}}/>
            </div>
            <div style={M.rw}>
              <button style={M.de} onClick={()=>{doDelNode(selectedNode.id);setShowEditNode(false);}}>Remove</button>
              <button style={M.sc} onClick={()=>setShowEditNode(false)}>Cancel</button>
              <button style={M.pr} onClick={doEditNode}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showAddLink && (
        <div style={M.ov} onClick={e=>{if(e.target===e.currentTarget)setShowAddLink(false);}}>
          <div style={M.bx}>
            <div style={M.ti}>Add Link</div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"10px 12px"}}>
              <div style={{flex:1,textAlign:"center"}}>
                <div style={{color:NODE_COLORS[showAddLink.src.type]||C.accent,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>
                  {showAddLink.src.type}
                </div>
                <div style={{color:C.text,fontSize:9,fontFamily:"monospace"}}>
                  {showAddLink.src.tag||showAddLink.src.id.slice(0,10)+"..."}
                </div>
              </div>
              <div style={{color:C.textDim,fontSize:16}}>to</div>
              <div style={{flex:1,textAlign:"center"}}>
                <div style={{color:NODE_COLORS[showAddLink.tgt.type]||C.accent,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>
                  {showAddLink.tgt.type}
                </div>
                <div style={{color:C.text,fontSize:9,fontFamily:"monospace"}}>
                  {showAddLink.tgt.tag||showAddLink.tgt.id.slice(0,10)+"..."}
                </div>
              </div>
            </div>
            <div style={M.f}>
              <span style={M.l}>DIRECTION</span>
              <div style={{display:"flex",gap:8}}>
                {["out","in"].map(d=>(
                  <button key={d} onClick={()=>setLinkDir(d)}
                    style={{flex:1,padding:"8px",borderRadius:6,cursor:"pointer",
                      background:linkDir===d?C.accentGlow:"transparent",
                      border:"1px solid "+(linkDir===d?C.accent:C.border),
                      color:linkDir===d?C.accent:C.textMid,fontFamily:"monospace",fontSize:11}}>
                    {d==="out" ? "Outgoing" : "Incoming"}
                  </button>
                ))}
              </div>
            </div>
            <div style={M.f}><span style={M.l}>AMOUNT (ETH)</span><input type="number" min="0" step="0.001" style={M.i} value={linkAmount} onChange={e=>setLinkAmount(e.target.value)}/></div>
            <div style={M.f}><span style={M.l}>NOTE (optional)</span><input style={M.i} value={linkNote} onChange={e=>setLinkNote(e.target.value)} placeholder="0xabc... or description"/></div>
            <div style={M.rw}>
              <button style={M.sc} onClick={()=>setShowAddLink(false)}>Cancel</button>
              <button style={M.pr} onClick={()=>doAddLink(showAddLink)}>Add Link</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ─── OSINT Research Databases ────────────────────────────────────────────────
const OSINT_RESOURCES = [
  {
    category: "Blockchain & Crypto",
    color: "#627EEA",
    icon: "⛓",
    tools: [
      { name: "Etherscan",          url: "https://etherscan.io",                desc: "Ethereum blockchain explorer & address labels" },
      { name: "BscScan",            url: "https://bscscan.com",                 desc: "BNB Smart Chain explorer" },
      { name: "PolygonScan",        url: "https://polygonscan.com",             desc: "Polygon network explorer" },
      { name: "Solscan",            url: "https://solscan.io",                  desc: "Solana blockchain explorer" },
      { name: "Blockchain.com",     url: "https://www.blockchain.com/explorer", desc: "Bitcoin & multi-chain explorer" },
      { name: "Blockchair",         url: "https://blockchair.com",              desc: "Privacy-focused multi-chain explorer with analytics" },
      { name: "OXT Research",       url: "https://oxt.me",                      desc: "Bitcoin transaction graph & cluster analysis" },
      { name: "Breadcrumbs",        url: "https://www.breadcrumbs.app",         desc: "Visual blockchain investigation & transaction tracing" },
      { name: "WalletExplorer",     url: "https://www.walletexplorer.com",      desc: "Bitcoin wallet clustering & exchange identification" },
      { name: "Nansen",             url: "https://www.nansen.ai",               desc: "On-chain analytics with entity labels (40M+ wallets)" },
      { name: "DeBank",             url: "https://debank.com",                  desc: "DeFi wallet tracker & protocol interaction history" },
      { name: "Zerion",             url: "https://zerion.io",                   desc: "DeFi portfolio & transaction history explorer" },
      { name: "Bitquery",           url: "https://explorer.bitquery.io",        desc: "Multi-chain SQL-based blockchain data & graph explorer" },
      { name: "AMLBot",             url: "https://amlbot.com",                  desc: "AML risk scoring for crypto addresses & transactions" },
      { name: "Crystal Blockchain", url: "https://crystalblockchain.com",       desc: "Enterprise blockchain analytics & compliance platform" },
      { name: "Chainalysis Reactor",url: "https://www.chainalysis.com/chainalysis-reactor", desc: "Industry-standard crypto investigation platform" },
      { name: "TRM Labs",           url: "https://www.trmlabs.com",             desc: "Real-time blockchain intelligence & risk monitoring" },
      { name: "Elliptic Investigator",url:"https://www.elliptic.co",            desc: "Crypto compliance & blockchain forensics platform" },
      { name: "MistTrack",          url: "https://misttrack.io",                desc: "SlowMist's free on-chain tracking & AML tool" },
      { name: "Scorechain",         url: "https://www.scorechain.com",          desc: "Crypto risk & compliance scoring" },
    ],
  },
  {
    category: "Sanctions & Watchlists",
    color: "#FF2244",
    icon: "🚫",
    tools: [
      { name: "OFAC SDN List",      url: "https://sanctionssearch.ofac.treas.gov", desc: "US Treasury Office of Foreign Assets Control sanctions search" },
      { name: "UN Sanctions",       url: "https://www.un.org/securitycouncil/sanctions/information", desc: "UN Security Council consolidated sanctions list" },
      { name: "EU Sanctions Map",   url: "https://www.sanctionsmap.eu",         desc: "Interactive EU sanctions list & search" },
      { name: "HM Treasury (UK)",   url: "https://www.gov.uk/government/publications/financial-sanctions-consolidated-list-of-targets", desc: "UK financial sanctions consolidated list" },
      { name: "FinCEN",             url: "https://www.fincen.gov/resources/advisories", desc: "FinCEN advisories, alerts & SARs guidance" },
      { name: "World-Check",        url: "https://www.refinitiv.com/en/financial-crime-risk-management", desc: "Refinitiv sanctions & PEP screening database" },
      { name: "Interpol Notices",   url: "https://www.interpol.int/How-we-work/Notices/Red-Notices", desc: "International law enforcement red notices" },
    ],
  },
  {
    category: "Financial & Business Intelligence",
    color: "#10B981",
    icon: "🏢",
    tools: [
      { name: "OpenCorporates",     url: "https://opencorporates.com",          desc: "World's largest open database of company information" },
      { name: "Offshore Leaks",     url: "https://offshoreleaks.icij.org",      desc: "ICIJ Panama Papers, Pandora Papers & offshore entity search" },
      { name: "SEC EDGAR",          url: "https://www.sec.gov/cgi-bin/browse-edgar", desc: "US SEC company filings, ownership & financial disclosures" },
      { name: "Investigative Dashboard", url: "https://id.occrp.org",           desc: "OCCRP company & person search across leaked databases" },
      { name: "Companies House (UK)", url: "https://find-and-update.company-information.service.gov.uk", desc: "UK company registrations, directors & filings" },
      { name: "Orbis (Bureau van Dijk)", url: "https://orbis.bvdinfo.com",      desc: "Global private company financials & ownership structures" },
      { name: "Global LEI Index",   url: "https://www.gleif.org/en/lei-data/global-lei-index", desc: "Legal Entity Identifier lookup for financial institutions" },
      { name: "ClarityFly",         url: "https://www.clarityfly.com",          desc: "Corporate ownership structure & UBO investigation" },
      { name: "X-rates.com",        url: "https://www.x-rates.com",             desc: "Historical exchange rates for transaction value conversion" },
    ],
  },
  {
    category: "Domain, IP & Infrastructure",
    color: "#00B4FF",
    icon: "🌐",
    tools: [
      { name: "Shodan",             url: "https://www.shodan.io",               desc: "Internet-connected device search engine — IPs, ports, banners" },
      { name: "Censys",             url: "https://censys.io",                   desc: "Internet-wide scanning & certificate transparency search" },
      { name: "BuiltWith",          url: "https://builtwith.com",               desc: "Website technology stack & hosting fingerprinting" },
      { name: "DomainTools",        url: "https://www.domaintools.com",         desc: "WHOIS history, domain ownership & passive DNS" },
      { name: "WHOIS (ICANN)",      url: "https://lookup.icann.org",            desc: "Authoritative WHOIS registrant information lookup" },
      { name: "crt.sh",             url: "https://crt.sh",                      desc: "Certificate transparency log — find subdomains via SSL certs" },
      { name: "DNSlytics",          url: "https://dnslytics.com",               desc: "DNS history, reverse IP & hosting intelligence" },
      { name: "Centralops.net",     url: "https://centralops.net",              desc: "Domain & IP investigation: WHOIS, DNS, traceroute" },
      { name: "ViewDNS.info",       url: "https://viewdns.info",                desc: "Reverse IP, DNS history, port scan & email tools" },
      { name: "SecurityTrails",     url: "https://securitytrails.com",          desc: "DNS & domain history, subdomain enumeration" },
      { name: "URLScan.io",         url: "https://urlscan.io",                  desc: "Website screenshot, DOM & network request analysis" },
    ],
  },
  {
    category: "Identity & People Research",
    color: "#A78BFA",
    icon: "👤",
    tools: [
      { name: "WhatsMyName",        url: "https://whatsmyname.app",             desc: "Username enumeration across 500+ social platforms" },
      { name: "Pipl",               url: "https://pipl.com",                    desc: "Deep web people search — email, phone, social profiles" },
      { name: "TruePeopleSearch",   url: "https://www.truepeoplesearch.com",    desc: "US people search — address, phone, relatives" },
      { name: "ZabaSearch",         url: "https://www.zabasearch.com",          desc: "US public records — address history & phone lookup" },
      { name: "TrueCaller",         url: "https://www.truecaller.com",          desc: "Global phone number & caller ID lookup" },
      { name: "Spokeo",             url: "https://www.spokeo.com",              desc: "US people search aggregator — social, address, phone" },
      { name: "Social Searcher",    url: "https://www.social-searcher.com",     desc: "Real-time social media search across all major platforms" },
      { name: "Namechk",            url: "https://namechk.com",                 desc: "Username availability & existence check across platforms" },
      { name: "KnowEm",             url: "https://knowem.com",                  desc: "Username search across 500+ social networks & domains" },
    ],
  },
  {
    category: "Hacked & Leaked Data",
    color: "#FF8C00",
    icon: "🔓",
    tools: [
      { name: "Have I Been Pwned",  url: "https://haveibeenpwned.com",          desc: "Check if email/phone appeared in known data breaches" },
      { name: "DeHashed",           url: "https://www.dehashed.com",            desc: "Search hashed & plaintext credential leaks" },
      { name: "Hacked-Emails",      url: "https://hacked-emails.com",           desc: "Email breach database search" },
      { name: "Leaked Source",      url: "https://www.leakedsource.com",        desc: "Aggregated breach database for OSINT investigation" },
      { name: "WikiLeaks",          url: "https://wikileaks.org",               desc: "Leaked government, diplomatic & corporate documents" },
      { name: "Offshore Leaks DB",  url: "https://offshoreleaks.icij.org",      desc: "Panama Papers, Pandora Papers leaked financial entities" },
      { name: "ScatteredSecrets",   url: "https://scatteredsecrets.com",        desc: "Breach data search & exposed credential monitoring" },
    ],
  },
  {
    category: "Cyber Threat Intelligence",
    color: "#EF4444",
    icon: "⚡",
    tools: [
      { name: "VirusTotal",         url: "https://www.virustotal.com",          desc: "File, URL & IP reputation scanning across 70+ AV engines" },
      { name: "PulseDive",          url: "https://pulsedive.com",               desc: "Threat intelligence platform — IPs, domains, IoCs" },
      { name: "AbuseIPDB",          url: "https://www.abuseipdb.com",           desc: "Community IP reputation & abuse reporting database" },
      { name: "Malware Traffic Analysis", url: "https://www.malware-traffic-analysis.net", desc: "Packet captures & malware infection traffic samples" },
      { name: "Cybercrime Tracker", url: "https://cybercrime-tracker.net",      desc: "C2 panel tracker — botnet & RAT infrastructure" },
      { name: "Feodo Tracker",      url: "https://feodotracker.abuse.ch",       desc: "Emotet/Feodo botnet C2 IP blocklist & tracking" },
      { name: "GreyNoise",          url: "https://www.greynoise.io",            desc: "Internet background noise — distinguish scanning from targeting" },
    ],
  },
  {
    category: "Transport & Asset Tracking",
    color: "#F0B429",
    icon: "✈️",
    tools: [
      { name: "FlightAware",        url: "https://flightaware.com",             desc: "Real-time & historical flight tracking — tail numbers & owners" },
      { name: "FlightRadar24",      url: "https://www.flightradar24.com",       desc: "Live global flight tracking map" },
      { name: "MarineTraffic",      url: "https://www.marinetraffic.com",       desc: "Global AIS ship tracking — vessel positions & history" },
      { name: "VesselFinder",       url: "https://www.vesselfinder.com",        desc: "Real-time vessel tracking & port activity" },
      { name: "VINDecoder",         url: "https://www.vindecoder.net",          desc: "Vehicle identification number lookup & history" },
      { name: "OpenSanctions Vessels", url: "https://www.opensanctions.org",    desc: "Sanctioned vessels, aircraft & asset search" },
    ],
  },
  {
    category: "General OSINT & Aggregators",
    color: "#6B7280",
    icon: "🔎",
    tools: [
      { name: "OSINT Framework",    url: "https://osintframework.com",          desc: "Structured OSINT tool directory organized by category" },
      { name: "IntelTechniques",    url: "https://inteltechniques.com/tools",   desc: "Michael Bazzell's curated OSINT search tools" },
      { name: "Google Dorks",       url: "https://www.exploit-db.com/google-hacking-database", desc: "Advanced Google search operators for targeted investigations" },
      { name: "Maltego",            url: "https://www.maltego.com",             desc: "Graph-based OSINT & link analysis platform" },
      { name: "Recon-ng",           url: "https://github.com/lanmaster53/recon-ng", desc: "Web reconnaissance framework for open source intel" },
      { name: "OCCRP Aleph",        url: "https://aleph.occrp.org",             desc: "Investigative journalism data platform — leaks & public records" },
      { name: "Google Trends",      url: "https://trends.google.com",           desc: "Track search interest over time for entities & keywords" },
      { name: "Wayback Machine",    url: "https://web.archive.org",             desc: "Historical website snapshots — deleted pages & old content" },
    ],
  },
];

function OSINTResources({ C }) {
  const [openCat, setOpenCat] = useState(null);
  const [searchQ, setSearchQ] = useState("");

  const filtered = OSINT_RESOURCES.map(cat => ({
    ...cat,
    tools: searchQ
      ? cat.tools.filter(t =>
          t.name.toLowerCase().includes(searchQ.toLowerCase()) ||
          t.desc.toLowerCase().includes(searchQ.toLowerCase())
        )
      : cat.tools,
  })).filter(cat => cat.tools.length > 0);

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div>
          <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 16, fontWeight: 700 }}>OSINT Research Databases</div>
          <div style={{ color: C.textDim, fontSize: 11, fontFamily: "'DM Mono',monospace", marginTop: 2 }}>
            {OSINT_RESOURCES.reduce((n, c) => n + c.tools.length, 0)} curated investigation resources across {OSINT_RESOURCES.length} categories
          </div>
        </div>
        <input
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder="Filter tools…"
          style={{ marginLeft: "auto", padding: "7px 12px", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, fontFamily: "'DM Mono',monospace", outline: "none", width: 200 }}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.map(cat => {
          const isOpen = openCat === cat.category || !!searchQ;
          return (
            <div key={cat.category} style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              {/* Category header */}
              <button
                onClick={() => setOpenCat(isOpen && !searchQ ? null : cat.category)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", background: isOpen ? `${cat.color}10` : C.bg, border: "none", cursor: "pointer", textAlign: "left" }}
              >
                <span style={{ fontSize: 16 }}>{cat.icon}</span>
                <span style={{ color: isOpen ? cat.color : C.text, fontWeight: 600, fontSize: 13, fontFamily: "'DM Mono',monospace", flex: 1 }}>{cat.category}</span>
                <span style={{ color: C.textDim, fontSize: 10, fontFamily: "'DM Mono',monospace" }}>{cat.tools.length} tools</span>
                <span style={{ color: C.textDim, fontSize: 12 }}>{isOpen ? "▲" : "▼"}</span>
              </button>

              {/* Tools grid */}
              {isOpen && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 8, padding: "10px 12px 12px" }}>
                  {cat.tools.map(tool => (
                    <a
                      key={tool.name}
                      href={tool.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ display: "block", padding: "10px 14px", background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${cat.color}`, borderRadius: 8, textDecoration: "none", transition: "border-color 0.15s, background 0.15s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.background = `${cat.color}0d`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface; e.currentTarget.style.borderLeftColor = cat.color; }}
                    >
                      <div style={{ color: C.text, fontWeight: 600, fontSize: 12, marginBottom: 4 }}>{tool.name} ↗</div>
                      <div style={{ color: C.textDim, fontSize: 10, fontFamily: "'DM Mono',monospace", lineHeight: 1.5 }}>{tool.desc}</div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── OSINT Panel ─────────────────────────────────────────────────────────────
const OSINT_SOURCES = [
  { id:"etherscan_label", name:"Etherscan Labels",    icon:"Ξ", color:"#627EEA", desc:"On-chain address tags" },
  { id:"ofac",            name:"OFAC SDN List",       icon:"🚫", color:"#FF2244", desc:"US Treasury sanctions" },
  { id:"interpol",        name:"Interpol Notices",    icon:"🔴", color:"#CC0000", desc:"International law enforcement" },
  { id:"darkweb",         name:"Darkweb Exposure",    icon:"🕶️", color:"#8B5CF6", desc:"Darknet market mentions" },
  { id:"social",          name:"Social Footprint",    icon:"📡", color:"#00D4FF", desc:"Twitter/Telegram/Discord links" },
  { id:"news",            name:"News & Reports",      icon:"📰", color:"#F0B429", desc:"Media coverage & advisories" },
  { id:"threatintel",     name:"Threat Intel",        icon:"⚡", color:"#FF6B35", desc:"CERTs, security researchers" },
  { id:"blockchain_id",   name:"Blockchain Identity", icon:"🪪", color:"#10B981", desc:"ENS, Lens, on-chain identity" },
];

function OSINTPanel({ address, txList, graph, onLog }) {
  const C = useTheme();
  const [result,  setResult]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const [checks,  setChecks]  = useState({});
  const prevAddr = useRef(null);

  // Auto-run when address changes
  useEffect(() => {
    if (!address || address === prevAddr.current) return;
    prevAddr.current = address;
    if (address.startsWith("0x") && address.length === 42) runOSINT();
  }, [address]); // eslint-disable-line

  async function runOSINT() {
    if (!address || !(address.startsWith("0x") && address.length === 42)) return;
    setLoading(true); setError(null); setResult(null);

    // Simulate progressive checks
    for (const src of OSINT_SOURCES) {
      setChecks(prev => ({ ...prev, [src.id]: "checking" }));
      await new Promise(r => setTimeout(r, 180 + Math.random() * 200));
    }

    // Check hardcoded KNOWN_ENTITIES first, then database for sanctioned status
    let known = KNOWN_ENTITIES[address.toLowerCase()];
    let dbSanctioned = null;

    // If not in hardcoded list, check database for sanctioned status
    if (!known) {
      try {
        console.log('🔍 Checking OFAC database for:', address);
        const dbRes = await fetch(`/api/crypto/sanctioned-lookup?address=${encodeURIComponent(address)}`, {
          signal: AbortSignal.timeout(8000)
        });
        if (dbRes.ok) {
          const sanctionedList = await dbRes.json();
          console.log('Database response:', sanctionedList);
          if (sanctionedList.length > 0) {
            // Found in OFAC database - treat as sanctioned
            dbSanctioned = sanctionedList[0];
            known = {
              type: "sanctioned",
              label: sanctionedList[0].entity_name || "OFAC Sanctioned",
              risk: 100,
              source: "OFAC",
              chain: sanctionedList[0].chain
            };
            console.log('✅ FOUND IN OFAC DATABASE:', known);
          } else {
            console.log('No OFAC records found for this address');
          }
        } else {
          console.warn('Database lookup returned status:', dbRes.status);
        }
      } catch (e) {
        console.warn('⚠️ Database lookup error:', e.message);
      }
    }
    const addrTxs = txList.filter(tx =>
      tx.from?.toLowerCase() === address.toLowerCase() ||
      tx.to?.toLowerCase()   === address.toLowerCase()
    );
    const highRiskTxs   = addrTxs.filter(t => t.risk === "HIGH");
    const mixerTxs      = addrTxs.filter(t =>
      KNOWN_ENTITIES[t.to?.toLowerCase()]?.type === "mixer" ||
      KNOWN_ENTITIES[t.from?.toLowerCase()]?.type === "mixer"
    );
    const exchangeTxs   = addrTxs.filter(t =>
      KNOWN_ENTITIES[t.to?.toLowerCase()]?.type === "exchange" ||
      KNOWN_ENTITIES[t.from?.toLowerCase()]?.type === "exchange"
    );
    const sanctionedTxs = addrTxs.filter(t =>
      KNOWN_ENTITIES[t.to?.toLowerCase()]?.type === "sanctioned" ||
      KNOWN_ENTITIES[t.from?.toLowerCase()]?.type === "sanctioned"
    );

    const G = graph || { nodes: [], links: [] };
    const neighbors = G.nodes.filter(n =>
      G.links.some(l =>
        (l.source?.id || l.source) === address.toLowerCase() ||
        (l.target?.id || l.target) === address.toLowerCase()
      ) && n.id !== address.toLowerCase()
    );
    const highRiskNeighbors = neighbors.filter(n => n.risk >= 70);

    // If address itself is sanctioned, force CRITICAL
    const isSanctionedAddress = known?.type === "sanctioned";

    const prompt = `You are an OSINT and blockchain forensics expert. Analyze this wallet address and return ONLY valid JSON (no markdown, no extra text).

ADDRESS: ${address}
KNOWN ENTITY: ${known ? JSON.stringify(known) : "Unknown / untagged"}
${isSanctionedAddress ? "⚠️ CRITICAL: THIS ADDRESS IS LISTED AS SANCTIONED/OFAC. MUST return threat_level: CRITICAL" : ""}
TRANSACTION COUNT: ${addrTxs.length}
HIGH RISK TXS: ${highRiskTxs.length}
MIXER INTERACTIONS: ${mixerTxs.length}
EXCHANGE INTERACTIONS: ${exchangeTxs.length}
SANCTIONED INTERACTIONS: ${sanctionedTxs.length}
HIGH RISK NEIGHBORS: ${highRiskNeighbors.map(n => n.tag || n.id.slice(0,10)).join(", ") || "None"}

Produce an OSINT intelligence report. Return this exact JSON:
{
  "threat_level": "${isSanctionedAddress ? "CRITICAL" : "CRITICAL|HIGH|MEDIUM|LOW|CLEAN"}",
  "identity_assessment": "${isSanctionedAddress ? "OFAC SANCTIONED ADDRESS - " : ""}What type of entity likely controls this wallet based on behavior patterns.",
  "ofac_exposure": { "status": "${isSanctionedAddress ? "CONFIRMED" : "CONFIRMED|SUSPECTED|NONE"}", "details": "${isSanctionedAddress ? "Address is listed as OFAC sanctioned" : "explanation"}" },
  "darkweb_exposure": { "status": "CONFIRMED|SUSPECTED|NONE", "details": "explanation based on mixer/darknet interactions" },
  "social_footprint": { "likely_platforms": ["list based on entity type"], "notes": "explanation" },
  "threat_intel": { "classification": "threat actor type or none", "ttps": ["tactic1", "tactic2"] },
  "blockchain_identity": { "ens": null, "known_labels": ["labels"], "cluster_type": "description" },
  "news_exposure": { "likely_coverage": "HIGH|MEDIUM|LOW|NONE", "reason": "why" },
  "key_findings": ["finding 1", "finding 2", "finding 3"],
  "investigative_leads": ["lead 1", "lead 2"],
  "confidence": 75,
  "data_sources_checked": ["OFAC SDN", "Chainalysis", "on-chain labels", "transaction patterns"]
}`;

    try {
      const res = await fetch(`${PROXY}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: [{ role: "user", content: prompt }], max_tokens: 1400 }),
      });
      if (!res.ok) throw new Error("AI proxy " + res.status);
      const d = await res.json();
      let raw = d.content?.[0]?.text || "";

      // Check if response is an error message
      if (raw.startsWith("API Error") || raw.startsWith("Error")) {
        throw new Error(raw);
      }

      // Extract JSON with proper brace matching
      let jsonStr = null;
      let braceCount = 0;
      let startIdx = -1;
      let endIdx = -1;

      for (let i = 0; i < raw.length; i++) {
        if (raw[i] === '{') {
          if (startIdx === -1) startIdx = i;
          braceCount++;
        } else if (raw[i] === '}') {
          braceCount--;
          if (braceCount === 0 && startIdx !== -1) {
            endIdx = i + 1;
            break;
          }
        }
      }

      if (startIdx === -1 || endIdx === -1) {
        throw new Error(`No JSON object found in response: ${raw.substring(0, 100)}`);
      }

      jsonStr = raw.substring(startIdx, endIdx);

      let parsed;
      try {
        // Remove BOM if present
        if (jsonStr.charCodeAt(0) === 0xFEFF) {
          jsonStr = jsonStr.slice(1);
        }
        parsed = JSON.parse(jsonStr);
      } catch (parseErr) {
        // Suppress verbose logging - fallback extraction works fine
        console.debug("[OSINTPanel] JSON parse failed, using fallback extraction");

        let fixed = jsonStr
          .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
          .replace(/:\s*undefined/g, ': null') // Replace undefined with null
          .replace(/\n\s*\n/g, '\n') // Remove extra blank lines
          .replace(/[\x00-\x1F\x7F]/g, ' '); // Remove control characters

        // Fix unescaped quotes in strings
        fixed = fixed.replace(/": "([^"]*)"([^"]*)"([^"]*)"/, '": "$1 $2 $3"');

        try {
          // Remove BOM again if present
          if (fixed.charCodeAt(0) === 0xFEFF) {
            fixed = fixed.slice(1);
          }
          parsed = JSON.parse(fixed);
        } catch (retryErr) {
          // Last resort: extract essential fields using pattern matching
          // (Minimal fallback when AI returns malformed JSON)

          // Extract threat_level
          const threatMatch = jsonStr.match(/"threat_level"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/);

          // Extract identity_assessment with escaped quote handling
          let identity_assessment = "Unknown";
          const idStart = jsonStr.indexOf('"identity_assessment"');
          if (idStart !== -1) {
            const afterLabel = jsonStr.indexOf(':', idStart);
            if (afterLabel !== -1) {
              const afterColon = jsonStr.substring(afterLabel + 1).trim();
              if (afterColon.startsWith('"')) {
                let i = 1;
                let escaped = false;
                while (i < afterColon.length) {
                  if (escaped) {
                    escaped = false;
                  } else if (afterColon[i] === '\\') {
                    escaped = true;
                  } else if (afterColon[i] === '"') {
                    identity_assessment = afterColon.substring(1, i).replace(/\\"/g, '"');
                    break;
                  }
                  i++;
                }
              }
            }
          }

          // Extract key_findings array
          let key_findings = [];
          const kfStart = jsonStr.indexOf('"key_findings"');
          if (kfStart !== -1) {
            const arrayStart = jsonStr.indexOf('[', kfStart);
            if (arrayStart !== -1) {
              const arrayEnd = jsonStr.indexOf(']', arrayStart);
              if (arrayEnd !== -1) {
                const arrayStr = jsonStr.substring(arrayStart + 1, arrayEnd);
                const findingMatches = arrayStr.match(/"[^"\\]*(?:\\.[^"\\]*)*"/g);
                if (findingMatches) {
                  key_findings = findingMatches.map(m => m.slice(1, -1).replace(/\\"/g, '"'));
                }
              }
            }
          }

          if (threatMatch) {
            // Derive risk score from threat level
            const threatText = threatMatch[1];
            let score = 50; // Default to medium
            if (threatText.includes("CRITICAL")) score = 95;
            else if (threatText.includes("HIGH")) score = 75;
            else if (threatText.includes("MEDIUM")) score = 50;
            else if (threatText.includes("LOW")) score = 25;
            else if (threatText.includes("CLEAN")) score = 5;

            parsed = {
              threat_level: threatText,
              identity_assessment: identity_assessment,
              ofac_exposure: { status: "NONE", details: "" },
              key_findings: key_findings,
              confidence: 60,
              score: score, // Add numeric risk score
              data_sources_checked: ["OFAC SDN", "on-chain labels"]
            };
          } else {
            throw new Error(`Unable to extract threat_level. Original error: ${parseErr.message}`);
          }
        }
      }
      setResult(parsed);
      setChecks(Object.fromEntries(OSINT_SOURCES.map(s => [s.id, "done"])));
      if (onLog) onLog({
        action: "crypto_osint_report",
        module: "crypto_monitoring",
        entityName: address.slice(0,10)+"...",
        meta: { riskLevel: parsed.risk_level, address },
      });
    } catch (e) {
      setError(e.message);
      setChecks(Object.fromEntries(OSINT_SOURCES.map(s => [s.id, "error"])));
    }
    setLoading(false);
  }

  const tlColor = { CRITICAL: "#FF2244", HIGH: "#FF6B35", MEDIUM: "#F0B429", LOW: "#00E676", CLEAN: "#10B981" };

  if (!address || !(address.startsWith("0x") && address.length === 42)) {
    return (
      <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
        <div style={{ color: C.textMid, fontSize: 13 }}>Trace a wallet address first to run OSINT analysis.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Bitcoin Analysis (if available) */}
      {typeof window !== 'undefined' && window.bitcoinAnalysis && (
        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 18 }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 12 }}>🔗 Bitcoin Analysis</div>

          {/* Metrics Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
            <div style={{ background: C.bg, borderRadius: 8, padding: 12, border: "1px solid " + C.border }}>
              <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>Transactions</div>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{window.bitcoinAnalysis.metrics.transactionCount}</div>
              <div style={{ color: C.textMid, fontSize: 9 }}>↓ {window.bitcoinAnalysis.metrics.incomingCount} / ↑ {window.bitcoinAnalysis.metrics.outgoingCount}</div>
            </div>

            <div style={{ background: C.bg, borderRadius: 8, padding: 12, border: "1px solid " + C.border }}>
              <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>Total Flow (BTC)</div>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>
                {window.bitcoinAnalysis.metrics.netFlowDirection === 'OUT' ? '↑' : '↓'} {Math.abs(parseFloat(window.bitcoinAnalysis.metrics.netFlowBTC)).toFixed(2)}
              </div>
              <div style={{ color: C.textMid, fontSize: 9 }}>Out: {window.bitcoinAnalysis.metrics.totalOutBTC}</div>
            </div>

            <div style={{ background: C.bg, borderRadius: 8, padding: 12, border: "1px solid " + C.border }}>
              <div style={{ color: C.textDim, fontSize: 10, marginBottom: 4 }}>Activity Status</div>
              <div style={{ color: window.bitcoinAnalysis.activityStatus.includes('ACTIVE') ? C.success : C.warn, fontSize: 12, fontWeight: 700 }}>
                {window.bitcoinAnalysis.activityStatus}
              </div>
              <div style={{ color: C.textMid, fontSize: 9 }}>{window.bitcoinAnalysis.daysSinceLastTx} days ago</div>
            </div>
          </div>

          {/* Detected Patterns */}
          {window.bitcoinAnalysis.patterns.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ color: C.textMid, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>⚠️ DETECTED PATTERNS</div>
              {window.bitcoinAnalysis.patterns.map((p, i) => (
                <div key={i} style={{ background: C.bg, borderRadius: 8, padding: 10, marginBottom: 6, border: "1px solid " + (p.severity === 'HIGH' ? C.danger : C.warn) + "33" }}>
                  <div style={{ color: p.severity === 'HIGH' ? C.danger : C.warn, fontSize: 11, fontWeight: 600 }}>
                    {p.severity} · {p.type.toUpperCase().replace(/_/g, ' ')}
                  </div>
                  <div style={{ color: C.textMid, fontSize: 10, marginTop: 4 }}>{p.description}</div>
                </div>
              ))}
            </div>
          )}

          {/* Compliance Status */}
          {window.bitcoinAnalysis.compliance.status !== 'NONE' && (
            <div style={{ background: C.danger + "18", border: "1px solid " + C.danger + "44", borderRadius: 8, padding: 12 }}>
              <div style={{ color: C.danger, fontSize: 11, fontWeight: 700, marginBottom: 6 }}>🚫 COMPLIANCE ALERT</div>
              {window.bitcoinAnalysis.compliance.notes.map((note, i) => (
                <div key={i} style={{ color: C.textMid, fontSize: 10, marginBottom: 4 }}>{note}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ color: C.text, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>OSINT Intelligence Report</div>
            <div style={{ color: C.textDim, fontSize: 10, fontFamily: "monospace" }}>{address}</div>
          </div>
          <button onClick={runOSINT} disabled={loading}
            style={{ padding: "8px 16px", background: loading ? C.border : C.accent, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "monospace", fontSize: 11 }}>
            {loading ? "Scanning..." : "Re-scan"}
          </button>
        </div>

        {/* Source check grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
          {OSINT_SOURCES.map(src => {
            const st = checks[src.id];
            const col = st === "done" ? C.success : st === "error" ? C.danger : st === "checking" ? C.warn : C.textDim;
            return (
              <div key={src.id} style={{ background: C.bg, border: "1px solid " + col + "33", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 2 }}>
                  <span style={{ fontSize: 12 }}>{src.icon}</span>
                  <span style={{ color: col, fontSize: 9, fontWeight: 700 }}>
                    {st === "checking" ? "CHECKING..." : st === "done" ? "✓ DONE" : st === "error" ? "✗ ERROR" : "QUEUED"}
                  </span>
                </div>
                <div style={{ color: C.text, fontSize: 10, fontWeight: 600 }}>{src.name}</div>
                <div style={{ color: C.textDim, fontSize: 9 }}>{src.desc}</div>
              </div>
            );
          })}
        </div>
      </div>

      {error && (
        <div style={{ background: "rgba(255,34,68,0.1)", border: "1px solid rgba(255,34,68,0.3)", borderRadius: 8, padding: 14, color: C.danger, fontSize: 12 }}>
          {error}
        </div>
      )}

      {result && (
        <>
          {/* Threat level banner */}
          <div style={{
            background: (tlColor[result.threat_level] || C.accent) + "18",
            border: "2px solid " + (tlColor[result.threat_level] || C.accent) + "55",
            borderRadius: 12, padding: "16px 20px",
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ color: C.textDim, fontSize: 10, letterSpacing: "0.1em", marginBottom: 4 }}>THREAT LEVEL</div>
              <div style={{ color: tlColor[result.threat_level] || C.accent, fontSize: 28, fontWeight: 800, fontFamily: "monospace" }}>
                {result.threat_level}
              </div>
              <div style={{ color: C.textMid, fontSize: 12, marginTop: 4 }}>{result.identity_assessment}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: C.textDim, fontSize: 10 }}>CONFIDENCE</div>
              <div style={{ color: C.accent, fontSize: 24, fontWeight: 700 }}>{result.confidence}%</div>
              <div style={{ color: C.textDim, fontSize: 9, marginTop: 4 }}>Sources: {result.data_sources_checked?.join(", ")}</div>
            </div>
          </div>

          {/* Intel cards grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* OFAC */}
            {result.ofac_exposure && (
              <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span>🚫</span>
                  <span style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700 }}>OFAC / SANCTIONS</span>
                  <span style={{
                    marginLeft: "auto", fontSize: 9, padding: "2px 7px", borderRadius: 4, fontWeight: 700,
                    background: result.ofac_exposure.status === "CONFIRMED" ? "rgba(255,34,68,0.15)" : result.ofac_exposure.status === "SUSPECTED" ? "rgba(255,107,53,0.15)" : "rgba(0,230,118,0.15)",
                    color: result.ofac_exposure.status === "CONFIRMED" ? "#FF2244" : result.ofac_exposure.status === "SUSPECTED" ? "#FF6B35" : "#00E676",
                  }}>{result.ofac_exposure.status}</span>
                </div>
                <div style={{ color: C.text, fontSize: 12 }}>{result.ofac_exposure.details}</div>
              </div>
            )}

            {/* Darkweb */}
            {result.darkweb_exposure && (
              <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span>🕶️</span>
                  <span style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700 }}>DARKWEB EXPOSURE</span>
                  <span style={{
                    marginLeft: "auto", fontSize: 9, padding: "2px 7px", borderRadius: 4, fontWeight: 700,
                    background: result.darkweb_exposure.status === "CONFIRMED" ? "rgba(255,34,68,0.15)" : result.darkweb_exposure.status === "SUSPECTED" ? "rgba(139,92,246,0.15)" : "rgba(0,230,118,0.15)",
                    color: result.darkweb_exposure.status === "CONFIRMED" ? "#FF2244" : result.darkweb_exposure.status === "SUSPECTED" ? "#8B5CF6" : "#00E676",
                  }}>{result.darkweb_exposure.status}</span>
                </div>
                <div style={{ color: C.text, fontSize: 12 }}>{result.darkweb_exposure.details}</div>
              </div>
            )}

            {/* Social footprint */}
            {result.social_footprint && (
              <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span>📡</span>
                  <span style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700 }}>SOCIAL FOOTPRINT</span>
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                  {(result.social_footprint.likely_platforms || []).map(p => (
                    <span key={p} style={{ background: C.accentGlow, color: C.accent, fontSize: 9, padding: "2px 7px", borderRadius: 4, fontWeight: 600 }}>{p}</span>
                  ))}
                </div>
                <div style={{ color: C.text, fontSize: 12 }}>{result.social_footprint.notes}</div>
              </div>
            )}

            {/* Blockchain identity */}
            {result.blockchain_identity && (
              <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span>🪪</span>
                  <span style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700 }}>BLOCKCHAIN IDENTITY</span>
                </div>
                {result.blockchain_identity.ens && (
                  <div style={{ color: C.accent, fontSize: 13, fontFamily: "monospace", marginBottom: 4 }}>{result.blockchain_identity.ens}</div>
                )}
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                  {(result.blockchain_identity.known_labels || []).map(l => (
                    <span key={l} style={{ background: "rgba(16,185,129,0.1)", color: "#10B981", fontSize: 9, padding: "2px 7px", borderRadius: 4 }}>{l}</span>
                  ))}
                </div>
                <div style={{ color: C.text, fontSize: 12 }}>{result.blockchain_identity.cluster_type}</div>
              </div>
            )}
          </div>

          {/* Threat intel */}
          {result.threat_intel && (
            <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                <span>⚡</span>
                <span style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700 }}>THREAT INTELLIGENCE</span>
                <span style={{ marginLeft: "auto", color: C.textDim, fontSize: 11 }}>{result.threat_intel.classification}</span>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(result.threat_intel.ttps || []).map(t => (
                  <span key={t} style={{ background: "rgba(255,107,53,0.1)", border: "1px solid rgba(255,107,53,0.3)", color: "#FF6B35", fontSize: 10, padding: "3px 9px", borderRadius: 5 }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Key findings */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {result.key_findings?.length > 0 && (
              <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 14 }}>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 10 }}>🔎 KEY FINDINGS</div>
                {result.key_findings.map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12 }}>
                    <span style={{ color: C.accent, flexShrink: 0 }}>→</span>
                    <span style={{ color: C.text }}>{f}</span>
                  </div>
                ))}
              </div>
            )}
            {result.investigative_leads?.length > 0 && (
              <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 14 }}>
                <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", fontWeight: 700, marginBottom: 10 }}>🧭 INVESTIGATIVE LEADS</div>
                {result.investigative_leads.map((l, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12 }}>
                    <span style={{ color: "#F0B429", flexShrink: 0 }}>→</span>
                    <span style={{ color: C.text }}>{l}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {!result && !loading && !error && (
        <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 10, padding: 24, textAlign: "center", color: C.textDim, fontSize: 12 }}>
          OSINT scan will run automatically when you trace a wallet address.
        </div>
      )}
    </div>
  );
}


// ─── High-Risk Exchanges by Country ──────────────────────────────────────────
// Sources: FATF, FinCEN advisories, public enforcement actions
const HIGH_RISK_EXCHANGES = [
  {
    country: "Russia", flag: "🇷🇺", riskLevel: "critical",
    exchanges: [
      { name: "Garantex",       status: "OFAC Sanctioned",  note: "Sanctioned Feb 2022, processed $100M+ darknet funds",       wallet: null },
      { name: "SUEX",           status: "OFAC Sanctioned",  note: "Sanctioned Sep 2021, facilitated ransomware payments",       wallet: null },
      { name: "Chatex",         status: "OFAC Sanctioned",  note: "Sanctioned Nov 2021, linked to ransomware actors",           wallet: null },
      { name: "BTC-e",          status: "Shut down",        note: "Seized 2017, $4B laundered, operator convicted",             wallet: null },
    ],
  },
  {
    country: "Iran", flag: "🇮🇷", riskLevel: "critical",
    exchanges: [
      { name: "Nobitex",        status: "High risk",        note: "Largest Iranian exchange, used to evade SWIFT sanctions",    wallet: null },
      { name: "Exir",           status: "High risk",        note: "Operates under Iranian jurisdiction, no AML compliance",     wallet: null },
      { name: "BitPin",         status: "High risk",        note: "Iranian P2P exchange, limited KYC enforcement",              wallet: null },
    ],
  },
  {
    country: "North Korea", flag: "🇰🇵", riskLevel: "critical",
    exchanges: [
      { name: "Lazarus Group wallets", status: "OFAC Sanctioned", note: "State-sponsored hacking group, $2B+ stolen",          wallet: "0x098b716b8aaf21512996dc57eb0615e2383e2f96" },
      { name: "Tornado Cash",  status: "OFAC Sanctioned",  note: "Used by Lazarus to launder stolen crypto",                   wallet: "0xd90e2f925da726b50c4ed8d0fb90ad053324f31b" },
      { name: "Blender.io",    status: "OFAC Sanctioned",  note: "First mixer sanctioned by OFAC, May 2022",                  wallet: null },
    ],
  },
  {
    country: "China", flag: "🇨🇳", riskLevel: "high",
    exchanges: [
      { name: "Huobi (HTX)",    status: "Restricted",      note: "Banned from mainland China, KYC gaps in offshore ops",       wallet: null },
      { name: "OKX",            status: "Watch list",       note: "Chinese founders, multiple jurisdictional issues",           wallet: null },
      { name: "Binance (former)", status: "Regulatory action", note: "DOJ settlement 2023, $4.3B fine, weak historical AML",   wallet: null },
    ],
  },
  {
    country: "Venezuela", flag: "🇻🇪", riskLevel: "high",
    exchanges: [
      { name: "Paxful P2P",     status: "High risk",       note: "P2P flows used for bolivar/USD arbitrage, limited AML",      wallet: null },
      { name: "LocalBitcoins (VE)", status: "High risk",   note: "Heavy P2P usage for capital controls evasion",               wallet: null },
    ],
  },
  {
    country: "Myanmar", flag: "🇲🇲", riskLevel: "high",
    exchanges: [
      { name: "KBZ Pay (crypto)", status: "FATF grey list", note: "FATF grey-listed jurisdiction, junta-linked flows",          wallet: null },
      { name: "Huione Pay",      status: "OFAC Sanctioned", note: "Sanctioned Jul 2024, facilitated scam compound payments",   wallet: null },
    ],
  },
  {
    country: "Cambodia", flag: "🇰🇭", riskLevel: "high",
    exchanges: [
      { name: "Huione Guarantee", status: "OFAC Sanctioned", note: "Pig butchering scam marketplace, $11B+ in flows",          wallet: null },
      { name: "JPEX (linked)",   status: "Fraud",           note: "HK/SEA-linked fraudulent exchange, $194M victim losses",     wallet: null },
    ],
  },
  {
    country: "UAE (Unregulated)", flag: "🇦🇪", riskLevel: "medium",
    exchanges: [
      { name: "BitOasis",        status: "Monitor",         note: "Regional exchange, improving compliance posture",            wallet: null },
      { name: "Bybit",           status: "Monitor",         note: "Dubai-based, large volume, watch for sanctions evasion",     wallet: null },
    ],
  },
  {
    country: "Nigeria", flag: "🇳🇬", riskLevel: "medium",
    exchanges: [
      { name: "Binance Nigeria",  status: "Regulatory action", note: "EFCC detention of executives 2024, regulatory dispute",  wallet: null },
      { name: "Luno",            status: "Monitor",         note: "Active in high-risk jurisdictions, AML gaps noted",          wallet: null },
      { name: "Patricia",        status: "Fraud",           note: "Customer funds frozen 2023, suspected insider fraud",        wallet: null },
    ],
  },
  {
    country: "Belarus", flag: "🇧🇾", riskLevel: "high",
    exchanges: [
      { name: "Currency.com",    status: "Sanctions risk",  note: "Belarusian-linked, Lukashenko regime sanctions exposure",    wallet: null },
      { name: "FREE2EX",         status: "High risk",       note: "Minimal AML, used for rouble/crypto conversion",            wallet: null },
    ],
  },
];

const RISK_LEVEL_CFG = {
  critical: { color: "#FF2244", label: "CRITICAL",  bg: "rgba(255,34,68,0.12)"  },
  high:     { color: "#FF8C00", label: "HIGH",       bg: "rgba(255,140,0,0.12)"  },
  medium:   { color: "#F0B429", label: "MEDIUM",     bg: "rgba(240,180,41,0.12)" },
};


// ─── High-Risk Exchanges Sidebar ─────────────────────────────────────────────
function HighRiskSidebar({ onSelectAddress, onSearch }) {
  const C = useTheme();
  const [expanded,   setExpanded]   = useState({});
  const [filterRisk, setFilterRisk] = useState("all");

  function toggle(country) {
    setExpanded(p => ({ ...p, [country]: !p[country] }));
  }

  const filtered = filterRisk === "all"
    ? HIGH_RISK_EXCHANGES
    : HIGH_RISK_EXCHANGES.filter(c => c.riskLevel === filterRisk);

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 12, padding: "14px 0", maxHeight: "320px", overflowY: "auto",
      display: "flex", flexDirection: "column" }}>

      {/* Header */}
      <div style={{ padding: "0 14px 12px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ color: C.danger, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}>
          ⚠ HIGH-RISK EXCHANGES
        </div>
        <div style={{ color: C.textDim, fontSize: 9, marginTop: 2 }}>
          FATF · OFAC · FinCEN sources
        </div>
      </div>

      {/* Risk filter */}
      <div style={{ display: "flex", gap: 4, padding: "10px 14px", borderBottom: `1px solid ${C.border}` }}>
        {["all", "critical", "high", "medium"].map(r => {
          const cfg = r === "all" ? { color: C.accent, label: "All" } : RISK_LEVEL_CFG[r];
          return (
            <button key={r} onClick={() => setFilterRisk(r)}
              style={{
                flex: 1, padding: "3px 0", borderRadius: 5, cursor: "pointer", fontSize: 9,
                fontFamily: "monospace", fontWeight: 700,
                background: filterRisk === r ? `${cfg.color}22` : "transparent",
                border: `1px solid ${filterRisk === r ? cfg.color : C.border}`,
                color: filterRisk === r ? cfg.color : C.textDim,
              }}>
              {cfg.label || r.toUpperCase()}
            </button>
          );
        })}
      </div>

      {/* Country list */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {filtered.map(({ country, flag, riskLevel, exchanges }) => {
          const cfg = RISK_LEVEL_CFG[riskLevel];
          const isOpen = expanded[country];
          return (
            <div key={country} style={{ borderBottom: `1px solid ${C.border}` }}>
              {/* Country row */}
              <button onClick={() => toggle(country)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8,
                  padding: "9px 14px", background: "transparent", border: "none",
                  cursor: "pointer", textAlign: "left" }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{flag}</span>
                <span style={{ flex: 1, color: C.text, fontSize: 12, fontWeight: 600 }}>{country}</span>
                <span style={{
                  background: cfg.bg, color: cfg.color,
                  fontSize: 8, padding: "1px 5px", borderRadius: 3, fontWeight: 700,
                  letterSpacing: "0.06em", flexShrink: 0,
                }}>
                  {cfg.label}
                </span>
                <span style={{ color: C.textDim, fontSize: 10, flexShrink: 0, marginLeft: 4 }}>
                  {isOpen ? "▲" : "▼"}
                </span>
              </button>

              {/* Exchanges dropdown */}
              {isOpen && (
                <div style={{ background: C.bg, borderTop: `1px solid ${C.border}` }}>
                  {exchanges.map((ex, i) => (
                    <div key={i} style={{ padding: "8px 14px 8px 20px",
                      borderBottom: i < exchanges.length - 1 ? `1px solid ${C.border}22` : "none" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6, marginBottom: 3 }}>
                        <span style={{ color: C.text, fontSize: 11, fontWeight: 600, flex: 1 }}>{ex.name}</span>
                        <span style={{
                          fontSize: 8, padding: "1px 5px", borderRadius: 3, fontWeight: 700,
                          flexShrink: 0, whiteSpace: "nowrap",
                          background: ex.status.includes("Sanctioned") ? `${C.danger}20` :
                                      ex.status.includes("Shut") || ex.status.includes("Fraud") ? `${C.danger}15` :
                                      `${C.warn}15`,
                          color: ex.status.includes("Sanctioned") || ex.status.includes("Shut") || ex.status.includes("Fraud")
                            ? C.danger : C.warn,
                        }}>
                          {ex.status}
                        </span>
                      </div>
                      <div style={{ color: C.textDim, fontSize: 9, lineHeight: 1.5, marginBottom: ex.wallet ? 6 : 0 }}>
                        {ex.note}
                      </div>
                      {ex.wallet && (
                        <button
                          onClick={() => onSearch(ex.wallet)}
                          style={{ marginTop: 4, padding: "3px 8px", background: `${C.accent}15`,
                            border: `1px solid ${C.accent}44`, borderRadius: 4,
                            color: C.accent, cursor: "pointer", fontFamily: "monospace",
                            fontSize: 8, fontWeight: 700 }}>
                          Trace wallet →
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: "8px 14px", borderTop: `1px solid ${C.border}`,
        color: C.textDim, fontSize: 8, lineHeight: 1.6 }}>
        Sources: OFAC SDN · FATF High-Risk Jurisdictions · FinCEN advisories · Public enforcement actions
      </div>
    </div>
  );
}

// ─── Platforms Panel ──────────────────────────────────────────────────────────
const PLATFORM_EXPLORERS = {
  exchange: {
    "Binance":    { url: "https://www.binance.com",    icon: "⧩", color: "#F3BA2F", desc: "Centralized exchange" },
    "Coinbase":   { url: "https://www.coinbase.com",   icon: "🔵", color: "#0052FF", desc: "Centralized exchange" },
    "Kraken":     { url: "https://www.kraken.com",     icon: "🐙", color: "#5741D9", desc: "Centralized exchange" },
    "KuCoin":     { url: "https://www.kucoin.com",     icon: "🟢", color: "#23AF91", desc: "Centralized exchange" },
    "OKX":        { url: "https://www.okx.com",        icon: "⚫", color: "#C0C0C0", desc: "Centralized exchange" },
  },
  defi: {
    "Uniswap":    { url: "https://app.uniswap.org",    icon: "🦄", color: "#FF007A", desc: "Decentralized exchange" },
    "SushiSwap":  { url: "https://www.sushi.com",      icon: "🍣", color: "#FA52A0", desc: "Decentralized exchange" },
    "Balancer":   { url: "https://app.balancer.fi",    icon: "⚖️", color: "#1E1E1E", desc: "AMM liquidity pool" },
    "Aave":       { url: "https://app.aave.com",       icon: "👻", color: "#B6509E", desc: "Lending protocol" },
    "Compound":   { url: "https://app.compound.finance",icon: "🏛️", color: "#00D395", desc: "Lending protocol" },
  },
  mixer: {
    "Tornado Cash": { url: "https://etherscan.io/address/0xd90e2f925da726b50c4ed8d0fb90ad053324f31b", icon: "🌀", color: "#FF6B35", desc: "OFAC sanctioned mixer" },
    "Sinbad":       { url: "https://etherscan.io/address/0x8576acc5c05d6ce88f4e49bf65bdf0c62f91353c", icon: "🌀", color: "#FF6B35", desc: "Sanctioned mixer" },
  },
  bridge: {
    "Wormhole":   { url: "https://wormhole.com",       icon: "🌉", color: "#00D4FF", desc: "Cross-chain bridge" },
    "Polygon Bridge": { url: "https://wallet.polygon.technology/bridge", icon: "◆", color: "#8247E5", desc: "Polygon bridge" },
    "Arbitrum":   { url: "https://bridge.arbitrum.io", icon: "🔵", color: "#28A0F0", desc: "L2 bridge" },
    "Optimism":   { url: "https://app.optimism.io/bridge", icon: "🔴", color: "#FF0420", desc: "L2 bridge" },
  },
  nft: {
    "OpenSea":    { url: "https://opensea.io",         icon: "🖼️", color: "#2081E2", desc: "NFT marketplace" },
    "Blur":       { url: "https://blur.io",            icon: "⚡", color: "#FF8700", desc: "NFT marketplace" },
  },
  gambling: {
    "Rollbit":    { url: "https://rollbit.com",        icon: "🎰", color: "#F59E0B", desc: "Crypto casino" },
    "Roobet":     { url: "https://roobet.com",         icon: "🦘", color: "#27AE60", desc: "Crypto casino" },
  },
};

function PlatformsPanel({ txList, graph, address, network, ethPrice=0 }) {
  const C = useTheme();
  const G = graph || { nodes: [], links: [] };

  // Build interaction map from transactions
  const interactions = {};
  txList.forEach(tx => {
    const addrs = [tx.from?.toLowerCase(), tx.to?.toLowerCase()].filter(Boolean);
    addrs.forEach(addr => {
      const known = KNOWN_ENTITIES[addr];
      if (!known || addr === address?.toLowerCase()) return;
      const key = known.label || addr;
      if (!interactions[key]) {
        interactions[key] = {
          label: known.label || addr.slice(0, 10) + "...",
          type: known.type,
          addr,
          risk: known.risk,
          txCount: 0,
          totalIn: 0,
          totalOut: 0,
          firstSeen: null,
          lastSeen: null,
        };
      }
      interactions[key].txCount++;
      const val = parseFloat(tx.value) || 0;
      if (tx.direction === "in")  interactions[key].totalIn  += val;
      if (tx.direction === "out") interactions[key].totalOut += val;
    });
  });

  // Also pull from graph nodes
  G.nodes.forEach(n => {
    if (n.type === "origin" || n.id === address?.toLowerCase()) return;
    if (!interactions[n.tag || n.id]) {
      const linkCount = G.links.filter(l =>
        (l.source?.id || l.source) === n.id || (l.target?.id || l.target) === n.id
      ).length;
      if (linkCount === 0) return;
      interactions[n.tag || n.id] = {
        label: n.tag || n.id.slice(0, 10) + "...",
        type: n.type,
        addr: n.id,
        risk: n.risk,
        txCount: linkCount,
        totalIn: 0,
        totalOut: 0,
      };
    }
  });

  const grouped = {};
  Object.values(interactions).forEach(item => {
    const t = item.type || "unknown";
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(item);
  });

  const typeOrder = ["sanctioned", "mixer", "darknet", "ransomware", "exchange", "defi", "bridge", "gambling", "nft", "staking", "atm", "unknown"];
  const sortedTypes = typeOrder.filter(t => grouped[t]?.length > 0);

  // Blockchain explorer URLs
  const explorerBase = {
    ethereum: "https://etherscan.io/address/",
    binance:  "https://bscscan.com/address/",
    polygon:  "https://polygonscan.com/address/",
    solana:   "https://solscan.io/account/",
    tron:     "https://tronscan.org/#/address/",
  };
  const baseUrl = explorerBase[network] || explorerBase.ethereum;

  if (txList.length === 0 && G.nodes.length === 0) {
    return (
      <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 32, textAlign: "center" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🏦</div>
        <div style={{ color: C.textMid, fontSize: 13 }}>Trace a wallet to see platform interactions.</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Summary bar */}
      <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 18 }}>
        <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 12 }}>PLATFORM INTERACTION SUMMARY</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {sortedTypes.map(t => {
            const meta = CATEGORY_META[t] || { icon: "👛", label: t };
            const col  = NODE_COLORS[t] || C.textDim;
            const items = grouped[t];
            return (
              <div key={t} style={{ background: col + "15", border: "1px solid " + col + "44", borderRadius: 8, padding: "8px 14px", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 14 }}>{meta.icon}</span>
                <div>
                  <div style={{ color: col, fontSize: 11, fontWeight: 700 }}>{meta.label}</div>
                  <div style={{ color: C.textDim, fontSize: 9 }}>{items.length} platform{items.length > 1 ? "s" : ""}</div>
                </div>
              </div>
            );
          })}
          {sortedTypes.length === 0 && (
            <div style={{ color: C.textDim, fontSize: 12 }}>No known platforms detected in transaction history.</div>
          )}
        </div>
      </div>

      {/* Platform cards by type */}
      {sortedTypes.map(type => {
        const meta = CATEGORY_META[type] || { icon: "👛", label: type };
        const col  = NODE_COLORS[type] || C.textDim;
        const items = grouped[type].sort((a, b) => b.txCount - a.txCount);
        const isDangerous = ["sanctioned", "mixer", "darknet", "ransomware"].includes(type);

        return (
          <div key={type} style={{ background: C.surface, border: "1px solid " + (isDangerous ? col + "55" : C.border), borderRadius: 12, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <span style={{ fontSize: 18 }}>{meta.icon}</span>
              <span style={{ color: col, fontWeight: 700, fontSize: 14 }}>{meta.label}</span>
              {isDangerous && (
                <span style={{ background: "rgba(255,34,68,0.15)", color: C.danger, fontSize: 9, padding: "2px 8px", borderRadius: 4, fontWeight: 700, marginLeft: 4 }}>
                  ⚠ HIGH RISK CATEGORY
                </span>
              )}
              <span style={{ marginLeft: "auto", color: C.textDim, fontSize: 11 }}>{items.length} platform{items.length > 1 ? "s" : ""}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map((item, i) => {
                const riskCol = item.risk >= 80 ? C.danger : item.risk >= 50 ? C.warn : C.success;
                // Find extra platform info if available
                // Find matching platform info by checking if item label contains any known platform name
                const platformKey = Object.keys(PLATFORM_EXPLORERS[type] || {}).find(k =>
                  item.label.toLowerCase().includes(k.toLowerCase())
                );
                const platformInfo = platformKey ? (PLATFORM_EXPLORERS[type] || {})[platformKey] : null;

                return (
                  <div key={i} style={{
                    background: C.bg, border: "1px solid " + col + "33",
                    borderRadius: 9, padding: "12px 14px",
                    display: "flex", alignItems: "center", gap: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>{item.label}</span>
                        <span style={{ background: riskCol + "22", color: riskCol, fontSize: 9, padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>
                          Risk {item.risk}
                        </span>
                      </div>
                      <div style={{ color: C.textDim, fontSize: 10, fontFamily: "monospace", marginBottom: 6 }}>{item.addr}</div>
                      <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
                        <span style={{ color: C.textMid }}>
                          <span style={{ color: C.textDim }}>Txs: </span>
                          <span style={{ color: C.text, fontWeight: 600 }}>{item.txCount}</span>
                        </span>
                        {item.totalIn > 0 && (
                          <span>
                            <span style={{ color: C.textDim }}>In: </span>
                            <span style={{ color: C.success, fontWeight: 600 }}>{fmtNative(item.totalIn,network)}{ethPrice>0&&item.totalIn>0?" · "+fmtUSD(item.totalIn,ethPrice):""}</span>
                          </span>
                        )}
                        {item.totalOut > 0 && (
                          <span>
                            <span style={{ color: C.textDim }}>Out: </span>
                            <span style={{ color: C.accent, fontWeight: 600 }}>{fmtNative(item.totalOut,network)}{ethPrice>0&&item.totalOut>0?" · "+fmtUSD(item.totalOut,ethPrice):""}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={() => window.open(baseUrl + item.addr, "_blank")}
                        style={{ padding: "5px 10px", background: "transparent", border: "1px solid " + C.border, borderRadius: 6, color: C.accent, cursor: "pointer", fontFamily: "monospace", fontSize: 10 }}>
                        Explorer ↗
                      </button>
                      {platformInfo && (
                        <button
                          onClick={() => window.open(platformInfo.url, "_blank")}
                          style={{ padding: "5px 10px", background: col + "18", border: "1px solid " + col + "44", borderRadius: 6, color: col, cursor: "pointer", fontFamily: "monospace", fontSize: 10 }}>
                          {platformInfo.icon} Platform ↗
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Quick access to all blockchain explorers */}
      <div style={{ background: C.surface, border: "1px solid " + C.border, borderRadius: 12, padding: 18 }}>
        <div style={{ color: C.textMid, fontSize: 10, letterSpacing: "0.1em", marginBottom: 12 }}>OPEN IN BLOCKCHAIN EXPLORERS</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            { name: "Etherscan",   url: "https://etherscan.io/address/",   color: "#627EEA", icon: "Ξ" },
            { name: "BscScan",     url: "https://bscscan.com/address/",    color: "#F3BA2F", icon: "⧩" },
            { name: "PolygonScan", url: "https://polygonscan.com/address/",color: "#8247E5", icon: "◆" },
            { name: "Arbiscan",    url: "https://arbiscan.io/address/",    color: "#28A0F0", icon: "🔵" },
            { name: "Optimistic",  url: "https://optimistic.etherscan.io/address/", color: "#FF0420", icon: "🔴" },
            { name: "Snowtrace",   url: "https://snowtrace.io/address/",   color: "#E84142", icon: "❄️" },
            { name: "Solscan",     url: "https://solscan.io/account/",     color: "#14F195", icon: "☉" },
            { name: "Tron Scan",   url: "https://tronscan.org/#/address/", color: "#EF0027", icon: "T" },
          ].map(e => (
            <button key={e.name}
              onClick={() => address && window.open(e.url + address, "_blank")}
              disabled={!address}
              style={{
                padding: "7px 14px", borderRadius: 7, cursor: address ? "pointer" : "not-allowed",
                background: e.color + "15", border: "1px solid " + e.color + "44",
                color: address ? e.color : C.textDim, fontFamily: "monospace", fontSize: 11, fontWeight: 600,
                opacity: address ? 1 : 0.5,
              }}>
              {e.icon} {e.name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// CSS for animations
const animationStyle = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('crypto-analysis-styles')) {
  const style = document.createElement('style');
  style.id = 'crypto-analysis-styles';
  style.textContent = animationStyle;
  document.head.appendChild(style);
}

// Performance: Cache for wallet traces (address -> tx list)
const traceCache = new Map();
const CACHE_DURATION = 300000; // 5 minutes

// Utility: Get cached trace or mark as fetching
const getCachedTrace = (address, network) => {
  const key = `${address.toLowerCase()}:${network}`;
  const cached = traceCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

// Utility: Set cached trace
const setCachedTrace = (address, network, txList) => {
  const key = `${address.toLowerCase()}:${network}`;
  traceCache.set(key, { data: txList, fetchedAt: Date.now() });
};

// ENS name resolution (with cache)
const resolveENS = async (address) => {
  if (!address || address.length !== 42 || !address.startsWith("0x")) return null;
  try {
    const res = await fetch(`https://api.ensdata.net/reverse/?address=${address}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.name || null;
  } catch {
    return null;
  }
};

const ensCache = new Map();
const getENSName = async (address) => {
  const key = address.toLowerCase();
  if (ensCache.has(key)) return ensCache.get(key);
  const name = await resolveENS(address);
  ensCache.set(key, name);
  return name;
};

// Utility: Batch lookup intelligence for multiple addresses
const batchLookupIntelligence = async (addresses) => {
  const results = {};
  const toFetch = addresses.filter(a => !KNOWN_ENTITIES[a.toLowerCase()]);

  // Group into batches of 10
  for (let i = 0; i < toFetch.length; i += 10) {
    const batch = toFetch.slice(i, i + 10);
    const promises = batch.map(a =>
      lookupIntelligence(a).catch(() => null)
    );
    const batchResults = await Promise.all(promises);
    batch.forEach((a, idx) => {
      results[a.toLowerCase()] = batchResults[idx] || KNOWN_ENTITIES[a.toLowerCase()];
    });
  }

  return results;
};

// Fetch DefiLlama intelligence for wallet analysis
const fetchDefiIntelligence = async (walletAddresses, tokenAddresses, chain = 'ethereum') => {
  try {
    const response = await fetch('/api/crypto/defillama-intelligence', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        walletAddresses: Array.isArray(walletAddresses) ? walletAddresses : [walletAddresses],
        tokenAddresses: Array.isArray(tokenAddresses) ? tokenAddresses : [tokenAddresses],
        chain
      })
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return await response.json();
  } catch (err) {
    console.error('[DefiLlama]', err.message);
    return null;
  }
};

// ─── Main Page ──────────────────────────────────────────────────────────────────
export default function CryptoAnalysisPage({initialSearch, company, currentUser}) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const [tab,            setTab]           = useState("graph");
  const [searchQuery,    setSearchQuery]   = useState(initialSearch?.query||"");
  const [network,        setNetwork]       = useState(initialSearch?.network||"ethereum");
  const [loading,        setLoading]       = useState(false);
  const [searchFocused,  setSearchFocused] = useState(false);
  const [copyFeedback,   setCopyFeedback]  = useState(null);
  const [txFilter,       setTxFilter]      = useState("all"); // "all"|"in"|"out"|"high"
  const [txSortBy,       setTxSortBy]      = useState("date"); // "date"|"value"|"risk"
  const searchInputRef = useRef(null);
  const [graph,          setGraph]         = useState(null);
  const [txList,         setTxList]        = useState([]);
  const [selectedNode,   setSelectedNode]  = useState(null);
  const [saved,          setSaved]         = useState([]);
  const [providerStatus, setProviderStatus]= useState(PROVIDERS.reduce((a,p)=>({...a,[p.id]:"idle"}),{}));
  // Trace history — breadcrumb of wallets investigated
  const [traceHistory,   setTraceHistory]  = useState([]);
  // Node intel panel — shows txs, balance, counterparty breakdown for a clicked node
  const [drawerNode,     setDrawerNode]    = useState(null);
  const [drawerTxs,      setDrawerTxs]    = useState([]);
  const [drawerLoading,  setDrawerLoading] = useState(false);
  const [drawerBalance,  setDrawerBalance] = useState(null);
  const [drawerTxFilter, setDrawerTxFilter]= useState("all"); // "all"|"in"|"out"|"high"
  // Live native token price (ETH/BNB/POL/SOL depending on selected network)
  const [ethPrice,       setEthPrice]      = useState(0); // kept as ethPrice for prop compat
  // Single transaction detail view (when searching by hash)
  const [txHashDetail,   setTxHashDetail]  = useState(null);
  const [txHashLock,     setTxHashLock]    = useState(false); // lock physics for tx-hash graphs
  // Wallet balance (native + token list)
  const [walletBalance,  setWalletBalance] = useState(null);
  const [showExchangeSidebar, setShowExchangeSidebar] = useState(true);
  // Item 19: Custom wallet labels loaded from Supabase
  const [customEntities, setCustomEntities] = useState({});
  const [showAddLabelModal, setShowAddLabelModal] = useState(false);
  const [addLabelForm, setAddLabelForm] = useState({ address: "", label: "", type: "unknown", risk: 50, notes: "" });
  const [showBlockchainSelector, setShowBlockchainSelector] = useState(false);
  const [blockchainSearch, setBlockchainSearch] = useState("");
  const [narrative, setNarrative] = useState("");
  const [narrativeSaved, setNarrativeSaved] = useState(false);
  const [showNarrativePanel, setShowNarrativePanel] = useState(false);
  // Saved analyses
  const [savedAnalyses, setSavedAnalyses] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [showSavedList, setShowSavedList] = useState(false);

  // Enhancement tabs
  const [activeTab, setActiveTab] = useState("analysis");
  const [watchlist, setWatchlist] = useState([]);
  const [customRules, setCustomRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [portfolioData, setPortfolioData] = useState(null);
  const [patternDetections, setPatternDetections] = useState([]);
  const [addressTags, setAddressTags] = useState([]);
  const [complianceReports, setComplianceReports] = useState([]);

  // Form states
  const [showAddWatchlist, setShowAddWatchlist] = useState(false);
  const [watchlistForm, setWatchlistForm] = useState({ address: '', label: '', riskThreshold: 40 });
  const [showAddRule, setShowAddRule] = useState(false);
  const [ruleForm, setRuleForm] = useState({ name: '', ruleType: 'threshold', conditions: '{}', riskAdjustment: 0 });
  const [comparisonAddresses, setComparisonAddresses] = useState(['', '']);

  // Standard rules
  const STANDARD_RULES = [
    { id: 'mixer_detection', name: 'Mixer/Tumbler Detection', ruleType: 'pattern', enabled: true, riskAdjustment: 50, description: 'Detects interactions with known mixers like Tornado Cash' },
    { id: 'sanctioned_check', name: 'OFAC Sanctioned Address', ruleType: 'threshold', enabled: true, riskAdjustment: 100, description: 'Flags addresses on OFAC sanctions list' },
    { id: 'high_velocity', name: 'High Velocity Transactions', ruleType: 'velocity', enabled: true, riskAdjustment: 30, description: 'Detects unusual transaction frequency spikes' },
    { id: 'structuring', name: 'Structuring Detection', ruleType: 'pattern', enabled: true, riskAdjustment: 40, description: 'Identifies artificial transaction splitting below thresholds' },
    { id: 'wash_trading', name: 'Wash Trading', ruleType: 'pattern', enabled: true, riskAdjustment: 35, description: 'Detects circular or repeating transaction patterns' },
    { id: 'darknet_market', name: 'Darknet Market Interaction', ruleType: 'counterparty', enabled: true, riskAdjustment: 60, description: 'Connections to known darknet marketplaces' },
    { id: 'ransomware_check', name: 'Ransomware Wallet', ruleType: 'threshold', enabled: true, riskAdjustment: 90, description: 'Known ransomware payment addresses' },
    { id: 'gambling_site', name: 'Gambling Site Interaction', ruleType: 'counterparty', enabled: false, riskAdjustment: 20, description: 'Connections to gambling platforms' },
  ];

  // DefiLlama Intelligence
  const [defiIntelligence, setDefiIntelligence] = useState(null);
  const [defiLoading, setDefiLoading] = useState(false);
  const [defiError, setDefiError] = useState(null);

  // Load enhancement tab data
  useEffect(() => {
    if (!company?.id) return;
    loadWatchlist();
    loadRules();
    loadAlerts();
    loadPortfolio();
    loadPatterns();
    loadReports();
  }, [company?.id]);

  // Load saved analyses on mount
  useEffect(() => {
    if (!company?.id) return;
    const loadSavedAnalyses = async () => {
      try {
        const url = `/api/crypto/saved-analyses?company_id=${company.id}${currentUser?.id ? `&user_id=${currentUser.id}` : ''}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          setSavedAnalyses(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.warn('[CryptoAnalysis] Failed to load saved analyses:', err.message);
      }
    };
    loadSavedAnalyses();
  }, [company?.id, currentUser?.id]);

  // Tab data loading functions
  const loadWatchlist = async () => {
    try {
      const response = await fetch(`/api/crypto/watchlist?companyId=${company.id}`);
      if (response.ok) {
        const data = await response.json();
        setWatchlist(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Failed to load watchlist:', err.message);
    }
  };

  const loadRules = async () => {
    try {
      const response = await fetch(`/api/crypto/rules?companyId=${company.id}`);
      if (response.ok) {
        const data = await response.json();
        setCustomRules(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Failed to load rules:', err.message);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await fetch(`/api/crypto/alerts?companyId=${company.id}&status=active`);
      if (response.ok) {
        const data = await response.json();
        setAlerts(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Failed to load alerts:', err.message);
    }
  };

  const loadPortfolio = async () => {
    try {
      const response = await fetch(`/api/crypto/portfolio?companyId=${company.id}`);
      if (response.ok) {
        const data = await response.json();
        setPortfolioData(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Failed to load portfolio:', err.message);
    }
  };

  const loadPatterns = async () => {
    try {
      const response = await fetch(`/api/crypto/patterns?companyId=${company.id}`);
      if (response.ok) {
        const data = await response.json();
        setPatternDetections(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Failed to load patterns:', err.message);
    }
  };

  const loadReports = async () => {
    try {
      const response = await fetch(`/api/crypto/reports?companyId=${company.id}`);
      if (response.ok) {
        const data = await response.json();
        setComplianceReports(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('Failed to load reports:', err.message);
    }
  };

  // Tab CRUD operations
  const addToWatchlist = async () => {
    if (!watchlistForm.address.trim()) return;
    try {
      const response = await fetch('/api/crypto/watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company.id,
          userId: currentUser?.id,
          address: watchlistForm.address,
          chain: 'ethereum',
          label: watchlistForm.label,
          riskThreshold: watchlistForm.riskThreshold,
          alertEnabled: true
        })
      });
      if (response.ok) {
        setWatchlistForm({ address: '', label: '', riskThreshold: 40 });
        setShowAddWatchlist(false);
        loadWatchlist();
      }
    } catch (err) {
      console.error('Failed to add watchlist:', err);
    }
  };

  const removeFromWatchlist = async (id) => {
    try {
      await fetch(`/api/crypto/watchlist?id=${id}`, { method: 'DELETE' });
      loadWatchlist();
    } catch (err) {
      console.error('Failed to remove from watchlist:', err);
    }
  };

  const addCustomRule = async () => {
    if (!ruleForm.name.trim()) return;
    try {
      const response = await fetch('/api/crypto/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company.id,
          userId: currentUser?.id,
          name: ruleForm.name,
          ruleType: ruleForm.ruleType,
          conditions: JSON.parse(ruleForm.conditions || '{}'),
          riskAdjustment: parseInt(ruleForm.riskAdjustment)
        })
      });
      if (response.ok) {
        setRuleForm({ name: '', ruleType: 'threshold', conditions: '{}', riskAdjustment: 0 });
        setShowAddRule(false);
        loadRules();
      }
    } catch (err) {
      console.error('Failed to add rule:', err);
    }
  };

  const dismissAlert = async (id) => {
    try {
      await fetch('/api/crypto/alerts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: 'dismissed' })
      });
      loadAlerts();
    } catch (err) {
      console.error('Failed to dismiss alert:', err);
    }
  };

  // Handle save analysis - called from AIRiskPanel with state setters
  async function handleSaveAnalysis(saveName, setSaveName, setShowSaveModal, setSavingAnalysis) {
    if (!drawerNode || !saveName.trim() || !company?.id || !currentUser?.id) return;
    setSavingAnalysis(true);
    try {
      const analysisData = {
        company_id: company.id,
        user_id: currentUser.id,
        wallet_address: drawerNode.id,
        wallet_name: saveName.trim(),
        analysis_data: { node: drawerNode, balance: drawerBalance },
        risk_score: drawerNode.risk || 0,
      };
      const response = await fetch('/api/crypto/saved-analyses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analysisData),
      });
      if (response.ok) {
        const saved = await response.json();
        setSavedAnalyses(prev => [...prev, saved]);
        setShowSaveModal(false);
        setSaveName("");
        alert('Analysis saved successfully!');
      } else {
        const error = await response.json();
        alert('Failed to save: ' + (error.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('[CryptoAnalysis] Save failed:', err);
      alert('Failed to save analysis: ' + err.message);
    }
    setSavingAnalysis(false);
  }

  // Handle load saved analysis - called from AIRiskPanel
  function handleLoadSavedAnalysis(analysis, setShowSavedList) {
    if (analysis.analysis_data?.node) {
      setDrawerNode(analysis.analysis_data.node);
      setDrawerBalance(analysis.analysis_data.balance);
      setShowSavedList(false);
    }
  }

  // Resolve an address — custom labels take priority over KNOWN_ENTITIES
  const resolveEntity = (addr) => {
    const a = (addr || "").toLowerCase();
    return customEntities[a] || KNOWN_ENTITIES[a] || null;
  };

  // Find blockchain by name or symbol
  const findBlockchain = (query) => {
    const q = query.toLowerCase().trim();
    return BLOCKCHAIN_NETWORKS.find(b =>
      b.name.toLowerCase().includes(q) ||
      b.symbol.toLowerCase().includes(q) ||
      b.id.toLowerCase().includes(q)
    );
  };

  // Save narrative to localStorage
  const saveNarrative = async () => {
    if (!searchQuery) {
      alert("Please search for a wallet or transaction first");
      return;
    }
    const key = `crypto_narrative_${searchQuery.toLowerCase()}_${network}`;
    localStorage.setItem(key, narrative);
    setNarrativeSaved(true);
    setTimeout(() => setNarrativeSaved(false), 2000);
    await log({
      action: "crypto_narrative_saved",
      module: "crypto_monitoring",
      entityName: searchQuery.slice(0,20)+"...",
      meta: { network, length: narrative.length, address: searchQuery }
    });
  };

  // Load narrative from localStorage
  useEffect(() => {
    if (searchQuery) {
      const key = `crypto_narrative_${searchQuery.toLowerCase()}_${network}`;
      const saved = localStorage.getItem(key) || "";
      setNarrative(saved);
    }
  }, [searchQuery, network]);

  // Export analysis with narrative
  const exportComprehensive = () => {
    if (!txList.length && !graph) {
      alert("No data to export. Please run an analysis first.");
      return;
    }

    const timestamp = new Date().toISOString();
    const date = new Date().toLocaleDateString();
    const networkName = BLOCKCHAIN_NETWORKS.find(b => b.id === network)?.name || network;

    // Find current analyzed node for AI analysis data
    const nodeId = graph?.selectedNode || txList[0]?.from;
    const nodeData = graph?.nodes.find(n => n.id === nodeId);

    // Build HTML export document
    let html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Crypto Analysis Report - ${searchQuery}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Syne:wght@700;800&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'DM Mono', monospace;
      background: #fff;
      color: #0A2342;
      padding: 12mm;
      font-size: 11px;
      line-height: 1.6;
    }
    .header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 8mm;
      padding-bottom: 4mm;
      border-bottom: 3px solid #00D4FF;
    }
    .logo-hex {
      width: 26px;
      height: 26px;
      background: #00D4FF;
      clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
      flex-shrink: 0;
    }
    .logo-text {
      font-family: 'Syne', sans-serif;
      font-weight: 800;
      font-size: 16px;
    }
    .doc-title {
      font-family: 'Syne', sans-serif;
      font-size: 20px;
      font-weight: 800;
      margin-bottom: 2mm;
      color: #0A2342;
    }
    .doc-meta {
      font-size: 9px;
      color: #4A5568;
      margin-bottom: 6mm;
      line-height: 1.4;
    }
    .section {
      margin-bottom: 6mm;
    }
    .section-title {
      font-family: 'Syne', sans-serif;
      font-weight: 800;
      font-size: 12px;
      letter-spacing: 0.1em;
      margin-bottom: 2mm;
      padding-bottom: 1mm;
      border-bottom: 2px solid #00D4FF;
      color: #0A2342;
    }
    .subsection-title {
      font-weight: 700;
      font-size: 11px;
      margin-top: 2mm;
      margin-bottom: 1mm;
      color: #2D3748;
    }
    .text-block {
      font-size: 10px;
      line-height: 1.7;
      color: #2D3748;
      padding: 8px 12px;
      background: #F7FAFC;
      border-radius: 4px;
      margin-bottom: 1mm;
    }
    .verdict-box {
      font-size: 16px;
      font-weight: 800;
      padding: 10px 14px;
      border-radius: 6px;
      margin-bottom: 3mm;
      display: inline-block;
      background: #00D4FF22;
      color: #00D4FF;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
      margin-bottom: 2mm;
    }
    th {
      background: #0A2342;
      color: #fff;
      padding: 5px 7px;
      text-align: left;
      font-size: 9px;
      font-weight: 700;
    }
    td {
      padding: 4px 7px;
      border-bottom: 1px solid #E2E8F0;
      vertical-align: top;
    }
    tr:nth-child(even) td {
      background: #F7FAFC;
    }
    .risk-factor {
      margin-bottom: 2mm;
      padding-left: 10px;
      border-left: 3px solid #FF6B6B;
    }
    .risk-factor .label {
      font-weight: 700;
      color: #FF6B6B;
      font-size: 10px;
    }
    .risk-factor .text {
      color: #2D3748;
      font-size: 9px;
      margin-top: 1mm;
    }
    .mitigating-factor {
      margin-bottom: 2mm;
      padding-left: 10px;
      border-left: 3px solid #00D81B;
    }
    .mitigating-factor .label {
      font-weight: 700;
      color: #00D81B;
      font-size: 10px;
    }
    .mitigating-factor .text {
      color: #2D3748;
      font-size: 9px;
      margin-top: 1mm;
    }
    .action-item {
      margin-bottom: 1.5mm;
      padding-left: 8px;
      color: #2D3748;
      font-size: 10px;
    }
    .action-item .num {
      font-weight: 700;
      color: #00D4FF;
      margin-right: 4px;
    }
    .footer {
      margin-top: 8mm;
      padding-top: 2mm;
      border-top: 1px solid #E2E8F0;
      font-size: 8px;
      color: #8A9BB5;
      display: flex;
      justify-content: space-between;
    }
    .narrative-box {
      background: #F0F9FF;
      border-left: 3px solid #00D4FF;
      padding: 8px 12px;
      margin-bottom: 3mm;
      font-size: 10px;
      color: #2D3748;
    }
    .graph-summary {
      background: #F7FAFC;
      border: 1px solid #E2E8F0;
      padding: 8px 12px;
      margin-bottom: 3mm;
      font-size: 10px;
      border-radius: 4px;
    }
    .osint-item {
      margin-bottom: 2mm;
      padding: 6px 10px;
      background: #F0F9FF;
      border-radius: 3px;
      border-left: 3px solid #00D4FF;
      font-size: 10px;
      color: #2D3748;
    }
    .page-break { page-break-after: always; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page-break { page-break-after: always; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo-hex"></div>
    <span class="logo-text">CrossRegGuard</span>
  </div>

  <div class="doc-title">Cryptocurrency Analysis Report</div>
  <div class="doc-meta">
    Address: <strong>${searchQuery}</strong><br>
    Network: <strong>${networkName}</strong><br>
    Date: ${date} · Time: ${timestamp}<br>
    Analyst: ${currentUser?.email || 'Unknown'}
  </div>`;

    // Add narrative if present
    if (narrative.trim()) {
      html += `
  <div class="section">
    <div class="section-title">Analysis Narrative</div>
    <div class="narrative-box">${narrative.replace(/\n/g, '<br>')}</div>
  </div>`;
    }

    // Add AI risk analysis if available (from the AI analysis section above)
    // Note: This is only available if user ran the AI analysis, not from ChainTrace export
    // The graph and narrative data are the primary exports from this section

    // Add graph summary
    if (graph && graph.nodes) {
      html += `
  <div class="section">
    <div class="section-title">Transaction Graph Summary</div>
    <div class="graph-summary">
      <div><strong>Total Wallets:</strong> ${graph.nodes.length}</div>
      <div><strong>Total Links:</strong> ${graph.links?.length || 0}</div>
    </div>

    <div class="subsection-title">Wallets (${graph.nodes.length})</div>
    <table>
      <thead><tr><th>Address</th><th>Type</th><th>Risk</th><th>Label</th></tr></thead>
      <tbody>`;
      graph.nodes.forEach(node => {
        html += `<tr><td>${node.id.substring(0, 10)}...</td><td>${node.type}</td><td>${node.risk}</td><td>${node.label || node.tag || '—'}</td></tr>`;
      });
      html += `</tbody></table>`;

      if (graph.links && graph.links.length > 0) {
        html += `
    <div class="subsection-title">Connections (${graph.links.length})</div>
    <table>
      <thead><tr><th>Direction</th><th>From</th><th>To</th><th>Amount</th><th>Notes</th></tr></thead>
      <tbody>`;
        graph.links.forEach(link => {
          const sourceId = link.source?.id || link.source || '—';
          const targetId = link.target?.id || link.target || '—';
          const sourceName = graph.nodes.find(n => n.id === sourceId)?.tag || sourceId.substring(0, 10);
          const targetName = graph.nodes.find(n => n.id === targetId)?.tag || targetId.substring(0, 10);
          html += `<tr><td>${link.direction === 'in' ? '↓' : '↑'}</td><td>${sourceName}</td><td>${targetName}</td><td>${link.amount > 0 ? link.amount.toFixed(6) : '—'}</td><td>${link.note || (link.manual ? 'manual' : '')}</td></tr>`;
        });
        html += `</tbody></table>`;
      }

      html += `
  </div>`;
    }

    // Add transactions
    if (Array.isArray(txList) && txList.length > 0) {
      html += `
  <div class="section">
    <div class="section-title">Recent Transactions (Last 25)</div>
    <table>
      <thead><tr><th>Date</th><th>Hash</th><th>From</th><th>To</th><th>Value</th><th>Risk</th></tr></thead>
      <tbody>`;
      txList.slice(0, 25).forEach(tx => {
        html += `<tr>
          <td>${tx.date ? new Date(tx.date).toLocaleDateString() : 'N/A'}</td>
          <td>${tx.hash ? tx.hash.substring(0, 8) : 'N/A'}...</td>
          <td>${tx.from ? tx.from.substring(0, 8) : 'N/A'}...</td>
          <td>${tx.to ? tx.to.substring(0, 8) : 'N/A'}...</td>
          <td>${tx.displayVal || tx.value || 'N/A'}</td>
          <td>${tx.risk || 'N/A'}</td>
        </tr>`;
      });
      html += `</tbody></table>
  </div>`;
    }

    html += `
  <div class="footer">
    <span>CrossRegGuard — Confidential</span>
    <span>Crypto Analysis Report · ${date}</span>
  </div>
</body>
</html>`;

    // Download as HTML (can be printed to PDF)
    const fileName = `CryptoAnalysis_${searchQuery.slice(0, 10)}_${Date.now()}.html`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportWithNarrative = exportComprehensive;

  // Load custom wallet labels from Neon on mount
  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      try {
        const { data } = await db.from("crypto_wallet_labels")
          .select("*")
          .eq("company_id", company.id)
          .execute();
        if (!data?.length) return;
        const map = {};
        data.forEach(r => { map[r.address.toLowerCase()] = { type: r.type, label: r.label, risk: r.risk_score || 50, custom: true }; });
        setCustomEntities(map);
      } catch (err) {
        console.error("[Crypto] Load labels error:", err.message);
      }
    })();
  }, [company?.id]);

  async function saveCustomLabel() {
    const { address, label, type, risk, notes } = addLabelForm;
    if (!address || !label) return;
    const addr = address.toLowerCase().trim();
    const payload = { company_id: company.id, address: addr, label, type, risk_score: Number(risk), notes };

    try {
      // Try insert first
      await db.from("crypto_wallet_labels").insert(payload).execute();
    } catch (err) {
      // If conflict (duplicate), update instead
      if (err.message?.includes("duplicate") || err.message?.includes("conflict")) {
        try {
          await db.from("crypto_wallet_labels")
            .update({ label, type, risk_score: Number(risk), notes })
            .eq("company_id", company.id)
            .eq("address", addr)
            .execute();
        } catch (updateErr) {
          console.error("[Crypto] Label update error:", updateErr.message);
          return;
        }
      } else {
        console.error("[Crypto] Label insert error:", err.message);
        return;
      }
    }

    setCustomEntities(prev => ({ ...prev, [addr]: { type, label, risk: Number(risk), custom: true } }));
    setShowAddLabelModal(false);
    setAddLabelForm({ address: "", label: "", type: "unknown", risk: 50, notes: "" });
    await log({ action: "crypto_wallet_label_added", module: "crypto", entityName: label, meta: { address: addr, type } });
  }

  // Support multiple address formats (Ethereum, Bitcoin, TRON, etc.)
  const isAddress = q => {
    if (!q) return false;
    // Ethereum (0x...)
    if (q.startsWith("0x") && q.length === 42) return true;
    // Bitcoin (P2PKH/P2SH/Bech32)
    if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(q)) return true;
    if (/^bc1[a-z0-9]{39,59}$/.test(q)) return true;
    // TRON (T...)
    if (/^T[a-zA-Z0-9]{33}$/.test(q)) return true;
    return false;
  };
  const isHash    = q => (q.startsWith("0x") && q.length===66) || q.length===88;

  // Auto-detect network for an address by trying all networks
  async function autoDetectNetwork(address, signal) {
    const networks = ["ethereum", "binance", "polygon"];
    for (const net of networks) {
      try {
        const ctrl = new AbortController();
        const timeout = setTimeout(() => ctrl.abort(), 5000);
        const tx = await fetchZerion(address, net, ctrl.signal).catch(() => null);
        clearTimeout(timeout);
        if (tx && tx.length > 0) {
          console.log(`[CryptoAnalysis] Auto-detected network: ${net}`);
          return net;
        }
      } catch (e) {
        // Continue to next network
      }
    }
    return "ethereum"; // Default fallback
  }

  useEffect(() => {
    (async () => {
      const p = await getNetworkPrice(network);
      setEthPrice(p);
    })();
    const interval = setInterval(async () => {
      const p = await getNetworkPrice(network);
      setEthPrice(p);
    }, 60000);
    return () => clearInterval(interval);
  }, [network]); // re-fetch whenever network changes

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K: Focus search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Escape: Clear search & focus
      if (e.key === 'Escape' && searchFocused) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchFocused]);

  async function fetchWithFallback(address) {
    if (!PROXY) {
      alert("Backend URL not configured. Set VITE_PROXY_URL in your Vercel environment variables to your backend server URL (e.g., https://your-backend.railway.app).");
      return;
    }
    // Log proxy configuration info for debugging
    if (!console.__cryptoDebugLogged) {
      console.info("[CryptoAnalysis] Backend proxy: " + (PROXY.slice(0, 50) + (PROXY.length > 50 ? "..." : "")));
      console.info("[CryptoAnalysis] Note: If you see 404 errors for /api/blockchain/*, your backend may not have blockchain API endpoints configured.");
      console.__cryptoDebugLogged = true;
    }
    // If it's a tx hash — fetch the transaction, build a from→to graph, and show on graph tab
    if (isHash(address)) {
      setLoading(true); setGraph(null); setTxList([]); setTxHashLock(false);
      setProviderStatus(PROVIDERS.reduce((a,p)=>({...a,[p.id]:"idle"}),{}));
      try {
        const ctrl = new AbortController();
        const tx = await fetchTxByHash(address, network, ctrl.signal);
        setTxList([tx]);
        setTxHashDetail(tx);

        if (tx.from && tx.to && !tx.error) {
          const fromAddr = tx.from.toLowerCase();
          const toAddr   = tx.to.toLowerCase();
          const W = 1400, H = 500;

          // Look up both addresses in parallel for entity labels/types
          const [fromIntel, toIntel] = await Promise.allSettled([
            lookupIntelligence(fromAddr),
            lookupIntelligence(toAddr),
          ]);
          const fi = fromIntel.status==="fulfilled" ? fromIntel.value : null;
          const ti = toIntel.status==="fulfilled"   ? toIntel.value   : null;

          const nodes = [
            {
              id:    fromAddr,
              label: fromAddr.slice(0,6)+"..."+fromAddr.slice(-4),
              tag:   fi?.label || null,
              type:  fi?.type  || "unknown",
              risk:  fi?.risk  ?? 40,
              value: 80,
              x: 220,    // pre-position LEFT (sender)
              y: H / 2,
            },
            {
              id:    toAddr,
              label: toAddr.slice(0,6)+"..."+toAddr.slice(-4),
              tag:   ti?.label || null,
              type:  ti?.type  || "unknown",
              risk:  ti?.risk  ?? 40,
              value: 80,
              x: W - 220, // pre-position RIGHT (receiver)
              y: H / 2,
            },
          ];

          const amount = parseFloat(tx.value) || 0;
          const txGraph = {
            nodes,
            links: [{
              source: fromAddr, target: toAddr,
              amount, direction: "out", count: 1,
            }],
          };
          setGraph(txGraph);
          setTxHashLock(true); // lock physics so nodes stay left→right
          setTab("graph");
        } else {
          setTab("txhash");
        }

        setSaved(prev=>[{id:Date.now(),query:address,network,provider:"Direct",timestamp:new Date().toLocaleTimeString()},...prev.slice(0,9)]);
        log({
          action: "crypto_tx_hash_looked_up",
          module: "crypto_monitoring",
          entityName: address.slice(0,10)+"...",
          meta: { network, from: tx.from, to: tx.to, value: tx.value, status: tx.status, hash: address },
        });
      } catch(e) {
        console.error("[ChainTrace] fetchTxByHash failed:", e.message);
        setTxHashDetail({ error: e.message, hash: address });
        setTab("txhash");
      }
      setLoading(false);
      return;
    }

    setLoading(true); setGraph(null); setTxList([]);
    setTxHashDetail(null); setTxHashLock(false); // clear any previous hash result

    // Performance: Check cache first
    const cached = getCachedTrace(address, network);
    if (cached) {
      console.log("[ChainTrace] Using cached results");
      setTxList(cached);
      setGraph(buildGraph(address, cached, customEntities));
      setWalletBalance(null);
      setSaved(prev=>[{id:Date.now(),query:address,network,provider:"Cache",timestamp:new Date().toLocaleTimeString()},...prev.slice(0,9)]);
      log({action:"crypto_address_looked_up_cached",module:"crypto_monitoring",entityName:address.slice(0,10)+"...",meta:{network,cached:true,address}});
      setLoading(false);
      return;
    }

    const st = PROVIDERS.reduce((a,p)=>({...a,[p.id]:"idle"}),{});
    setProviderStatus({...st});
    const ctrl = new AbortController();
    const {signal} = ctrl;
    let raw = null, usedProvider = null;
    const cm = {ethereum:"eth", binance:"bsc", polygon:"polygon"};

    // 1. Zerion (primary — best multi-chain, richest data including token balances)
    try {
      const zerionTxs = await fetchZerion(address, network, signal);
      if (zerionTxs.length > 0) {
        raw = zerionTxs.map(t => normZerion(t, address)).filter(Boolean);
        usedProvider = "Zerion";
        console.log(`[ChainTrace] Zerion: ${raw.length} txs`);
      }
    } catch(e) { console.warn("[ChainTrace] Zerion failed:", e.message); }

    // 2. Moralis (excellent multi-chain coverage, token transfers)
    if (!raw || raw.length === 0) {
      const p = PROVIDERS[4]; st[p.id]="trying"; setProviderStatus({...st});
      try {
        const moralisTxs = await fetchMoralis(address, cm[network]||"eth", signal);
        if (moralisTxs.length > 0) {
          raw = moralisTxs.map(t => normTx(t,"moralis",address)).filter(Boolean);
          st[p.id]="ok"; usedProvider="Moralis";
          console.log(`[ChainTrace] Moralis: ${raw.length} txs`);
        } else { st[p.id]="ok(0)"; }
      } catch(e) { st[p.id]="error"; console.warn("[ChainTrace] Moralis failed:", e.message); }
      setProviderStatus({...st});
    }

    // 3. Etherscan V2 (ETH + BSC + Polygon via chainid)
    if (!raw || raw.length === 0) {
      const evmMap = {ethereum:PROVIDERS[0], binance:PROVIDERS[1], polygon:PROVIDERS[2]};
      const primary = evmMap[network];
      if (primary) {
        st[primary.id]="trying"; setProviderStatus({...st});
        try {
          const rawTxs = await fetchEVMTxs(address, primary.id, signal);
          raw = rawTxs.map(t => normTx(t,"evm",address)).filter(Boolean);
          st[primary.id] = raw.length > 0 ? "ok" : "ok(0)";
          usedProvider = primary.name;
          console.log(`[ChainTrace] ${primary.name}: ${raw.length} txs`);
        } catch(e) { st[primary.id]="error"; console.warn(`[ChainTrace] ${primary.name} failed:`, e.message); }
        setProviderStatus({...st});
      }
    }

    // 4. Alchemy (ETH mainnet, rich token data)
    if (!raw || raw.length === 0) {
      const p = PROVIDERS[3]; st[p.id]="trying"; setProviderStatus({...st});
      try {
        const transfers = await fetchAlchemy(address, p.url, signal);
        raw = transfers.map(t => ({
          hash:t.hash, from:t.from, to:t.to,
          value: t.value != null ? parseFloat(t.value).toFixed(6) : "0",
          age: t.metadata?.blockTimestamp ? new Date(t.metadata.blockTimestamp).toLocaleString() : "?",
          method: t.category||"Transfer", risk:"LOW", riskScore:30,
          fromLabel: KNOWN_ENTITIES[t.from?.toLowerCase()]?.label || null,
          toLabel:   KNOWN_ENTITIES[t.to?.toLowerCase()]?.label   || null,
          direction: t.from?.toLowerCase()===address.toLowerCase() ? "out" : "in",
        }));
        st[p.id]="ok"; usedProvider="Alchemy";
        console.log(`[ChainTrace] Alchemy: ${raw.length} txs`);
      } catch(e) { st[p.id]="error"; }
      setProviderStatus({...st});
    }

    // 5. Tron — via GetBlock RPC (dedicated provider for Tron/TRC-20)
    if (network === "tron") {
      if (!raw || raw.length === 0) {
        try {
          const tronTxs = await fetchTron(address, signal);
          if (tronTxs.length > 0) {
            raw = tronTxs.map(t => normTron(t, address)).filter(Boolean);
            usedProvider = "GetBlock (Tron)";
            console.log(`[ChainTrace] Tron: ${raw.length} txs`);
          }
        } catch(e) { console.warn("[ChainTrace] Tron failed:", e.message); }
      }
    }

    // 6. Bitquery (last resort — GraphQL multi-chain)
    if (!raw || raw.length === 0) {
      try {
        const bqTxs = await fetchBitquery(address, network, signal);
        if (bqTxs.length > 0) {
          raw = bqTxs.map(t => normBitquery(t, address)).filter(Boolean);
          usedProvider = "Bitquery";
          console.log(`[ChainTrace] Bitquery: ${raw.length} txs`);
        }
      } catch(e) { console.warn("[ChainTrace] Bitquery failed:", e.message); }
    }

    PROVIDERS.forEach(p=>{ if(st[p.id]==="idle") st[p.id]="skip"; });
    setProviderStatus({...st});

    const final = (raw||[]).filter(Boolean);
    final.forEach(tx => {
      if (tx.from) { const s=scoreAddr(tx.from,final); if(!tx.fromLabel) tx.fromLabel=s.label; tx.fromType=s.type; }
      if (tx.to)   {
        const s=scoreAddr(tx.to,final);
        if(!tx.toLabel) tx.toLabel=s.label;
        tx.toType=s.type;
        if (["mixer","sanctioned","darknet","ransomware"].includes(s.type)) {
          tx.risk="HIGH"; tx.riskScore=95;
        }
      }
    });

    const g = capUnknowns(capExchanges(buildGraph(address, final, customEntities)));
    setGraph(g); setTxList(final); setLoading(false);

    // Performance: Cache the results
    setCachedTrace(address, network, final);

    setSaved(prev=>[{id:Date.now(),query:address,network,provider:usedProvider,timestamp:new Date().toLocaleTimeString()},...prev.slice(0,9)]);
    setTraceHistory(prev=>{
      const entry={address,network,timestamp:new Date().toLocaleTimeString(),txCount:(raw||[]).length};
      return [entry,...prev.filter(h=>h.address!==address).slice(0,19)];
    });

    // Audit log
    const highRiskCount = final.filter(tx=>tx.risk==="HIGH").length;
    const knownEntity = KNOWN_ENTITIES[address.toLowerCase()];
    log({
      action: "crypto_wallet_traced",
      module: "crypto_monitoring",
      entityName: knownEntity?.label || address.slice(0,10)+"...",
      meta: { network, txCount: final.length, highRiskTxs: highRiskCount, provider: usedProvider, nodeCount: g.nodes.length, address },
    });

    // Enrich unknown graph nodes via live eth-labels API
    g.nodes.filter(n=>n.type==="unknown").map(n=>n.id).slice(0,20).forEach(async addr => {
      const intel = await lookupIntelligence(addr);
      if (!intel || intel.type==="unknown") return;
      setGraph(prev => {
        if (!prev) return prev;
        const updated = {...prev, nodes: prev.nodes.map(n => n.id===addr ? {...n,...intel} : n)};
        return capUnknowns(capExchanges(updated));
      });
    });

    // Enrich tx fromLabel/toLabel for addresses missing labels — fires in background
    // Collect unique unknown addresses across all transactions (cap at 30 to avoid hammering)
    const unlabelledAddrs = [...new Set(
      final.flatMap(tx => [tx.from, tx.to].filter(a => a && !KNOWN_ENTITIES[a?.toLowerCase()]))
    )].slice(0, 30);
    unlabelledAddrs.forEach(async addr => {
      const intel = await lookupIntelligence(addr);
      if (!intel || intel.type === "unknown" || !intel.label) return;
      // Update txList in place — React will re-render the transactions table
      setTxList(prev => prev.map(tx => {
        const updated = { ...tx };
        if (tx.from?.toLowerCase() === addr.toLowerCase() && !tx.fromLabel)
          updated.fromLabel = intel.label;
        if (tx.to?.toLowerCase() === addr.toLowerCase() && !tx.toLabel)
          updated.toLabel = intel.label;
        return updated;
      }));
    });

    setWalletBalance(null);
    try {
      const bal = await fetchWalletBalance(address, network, new AbortController().signal);
      setWalletBalance({ address, ...bal });
    } catch(e) { console.warn("[balance]", e.message); }
  }

  // Opens the node intel panel — loads txs + balance for a clicked node
  async function openNodeDrawer(node) {
    if (!node?.id || node.id.startsWith("manual-")) return;
    setDrawerNode(node);
    setDrawerLoading(true);
    setDrawerTxs([]);
    setDrawerBalance(null);
    setDrawerTxFilter("all");
    setTab("graph");
    setDefiIntelligence(null);
    setDefiError(null);

    // Fetch balance in parallel
    (async () => {
      try {
        const b = await fetchWalletBalance(node.id, network, new AbortController().signal);
        setDrawerBalance(b);
      } catch (err) {}
    })();

    // Fetch DefiLlama intelligence in parallel
    (async () => {
      try {
        setDefiLoading(true);
        const result = await fetchDefiIntelligence([node.id], [], network);
        if (result) {
          setDefiIntelligence(result);
        } else {
          setDefiError('Failed to fetch DeFi intelligence');
        }
      } catch (err) {
        console.error('[DefiIntel]', err);
        setDefiError(err.message);
      } finally {
        setDefiLoading(false);
      }
    })();

    // Filter txs from current list first (instant)
    const local = txList.filter(tx =>
      tx.from?.toLowerCase()===node.id.toLowerCase() ||
      tx.to?.toLowerCase()===node.id.toLowerCase()
    );
    if (local.length) { setDrawerTxs(local); setDrawerLoading(false); return; }

    // Otherwise fetch from chain
    const ctrl = new AbortController();
    const {signal} = ctrl;
    const evmMap = {ethereum:PROVIDERS[0], binance:PROVIDERS[1], polygon:PROVIDERS[2]};
    const primary = evmMap[network];
    try {
      if (primary) {
        const raw = await fetchEVMTxs(node.id, primary.id, signal);
        const normed = raw.map(t=>normTx(t,"evm",node.id)).filter(Boolean);
        setDrawerTxs(normed);
      }
    } catch(e) {
      setDrawerTxs([]);
    }
    setDrawerLoading(false);
  }

  // Adds a counterparty from the intel panel into the graph
  async function addTxToGraph(tx, refNodeId) {
    const counterAddr = tx.from?.toLowerCase() === refNodeId?.toLowerCase() ? tx.to : tx.from;
    const counterLabel = tx.from?.toLowerCase() === refNodeId?.toLowerCase() ? tx.toLabel : tx.fromLabel;
    if (!counterAddr) return;
    const already = graph?.nodes?.find(n => n.id.toLowerCase() === counterAddr.toLowerCase());
    const intel = KNOWN_ENTITIES[counterAddr.toLowerCase()] || await lookupIntelligence(counterAddr);
    const newNode = already || {
      id: counterAddr,
      label: intel?.label || counterLabel || counterAddr.slice(0,8)+"...",
      tag: intel?.label || counterLabel || null,
      type: intel?.type || "unknown",
      risk: intel?.risk || 30,
      value: 50, vx:0, vy:0,
    };
    setGraph(prev => {
      if (!prev) return prev;
      const nodes = already ? prev.nodes : [...prev.nodes, newNode];
      const linkExists = prev.links.some(l => {
        const s = l.source?.id||l.source, t = l.target?.id||l.target;
        return (s===refNodeId&&t===counterAddr)||(s===counterAddr&&t===refNodeId);
      });
      const links = linkExists ? prev.links : [...prev.links, {source:refNodeId, target:counterAddr, value:parseFloat(tx.value)||0}];
      return capUnknowns(capExchanges({nodes, links}));
    });
    if (!already) {
      log({
        action: "crypto_node_added_to_graph",
        module: "crypto_monitoring",
        entityName: newNode.tag || counterAddr.slice(0,10)+"...",
        meta: { type: newNode.type, risk: newNode.risk, fromNode: refNodeId, txHash: tx.hash, value: tx.value, network, nodeAddress: counterAddr },
      });
    }
  }

  async function handleSearch() {
    const q = searchQuery.trim(); if (!q) return;
    if (isHash(q)) {
      fetchWithFallback(q);
    } else if (isAddress(q)) {
      setSearchQuery(q);

      // First, check local compliance databases for any address
      console.log('🔍 Checking compliance databases for:', q);
      try {
        const complianceRes = await fetch(`/api/crypto/sanctioned-lookup?address=${encodeURIComponent(q)}`);
        if (complianceRes.ok) {
          const sanctionedList = await complianceRes.json();
          if (sanctionedList.length > 0) {
            console.log('⚠️ FOUND IN COMPLIANCE DATABASE:', sanctionedList[0]);
            // Address is sanctioned - add to graph with warning
            const node = {
              id: q,
              address: q,
              tag: sanctionedList[0].entity_name || 'OFAC Sanctioned',
              type: 'sanctioned',
              risk: 100
            };
            setGraph({
              nodes: [node],
              links: []
            });
            setTab("graph");
            console.log('🚫 Found in OFAC database - flagged as sanctioned');
            return;
          }
        }
      } catch (e) {
        console.debug('Compliance check failed:', e.message);
      }

      // If not in compliance DB, add as unknown wallet to graph
      console.log('✅ Address not in compliance databases - adding as unknown wallet');
      const unknownNode = {
        id: q,
        address: q,
        tag: 'Unknown Wallet',
        type: 'unknown',
        risk: 40
      };

      setGraph({
        nodes: [unknownNode],
        links: []
      });

      setTab("graph");
      console.log('📌 Address added to graph - click to run compliance analysis');
    } else {
      const e = EXPLORERS.find(e=>e.network===network);
      if (e) window.open("https://"+e.url+"/search?q="+q, "_blank");
    }
  }

  const S = {
    page:  {background:C.bg, fontFamily:"monospace", color:C.text},
    card:  {background:C.surface, border:"1px solid "+C.border, borderRadius:12, padding:18},
    label: {color:C.textMid, fontSize:10, letterSpacing:"0.08em", display:"block", marginBottom:6},
    btn:   (active, col="") => ({
      padding:"8px 14px", borderRadius:7, cursor:"pointer",
      fontFamily:"monospace", fontSize:11, fontWeight:600,
      border:"1px solid "+(active?(col||C.accent):C.border),
      background:active?((col||C.accent)+"18"):"transparent",
      color:active?(col||C.accent):C.textMid,
      transition:"all 0.15s",
    }),
  };

  const TABS = [
    {id:"graph",    label:"Graph"},
    {id:"trail",    label:"Money Trail"},
    {id:"txlist",   label:"Transactions"},
    {id:"txhash",   label:"Tx Detail"},
    {id:"osint",    label:"OSINT"},
    {id:"platforms",label:"Platforms"},
    {id:"explorer", label:"Explorers"},
    {id:"recent",   label:"Recent"},
    {id:"history",  label:"📚 History"},
  ];

  function handleSidebarSearch(wallet) {
    setSearchQuery(wallet);
    fetchWithFallback(wallet);
    setTab("graph");
  }

  return (
    <div style={{ background: C.bg, fontFamily: "monospace", color: C.text, padding: 24 }}>
      <div>
      <div style={{marginBottom:20}}>
        <h1 style={{fontSize:24,fontWeight:800,margin:0,marginBottom:3}}>
          Chain<span style={{color:C.accent}}>Trace</span>
        </h1>
        <p style={{color:C.textMid,fontSize:12,margin:0}}>Blockchain intelligence with AI risk reasoning</p>
      </div>

      {/* Multi-chain explorer trace with fallback */}
      {searchQuery && (
        <div style={{marginBottom:14}}>
          {(() => {
            const detectedChain = detectAddressChain(searchQuery);
            const relevantExplorers = detectedChain
              ? EXPLORERS.filter(e => e.network === detectedChain).slice(0, 5)
              : EXPLORERS.slice(0, 5);

            const handleTraceAllChains = () => {
              const detectedChain = detectAddressChain(searchQuery);

              if (detectedChain) {
                // Address format detected - show options
                const explorersForChain = EXPLORERS.filter(e => e.network === detectedChain);
                console.log(`✓ Detected chain: ${detectedChain} (${explorersForChain.length} explorers available)`);

                const explorerList = explorersForChain.map(e => `• ${e.icon} ${e.name}`).join('\n');
                const addToGraph = () => {
                  // Add as unknown wallet to graph
                  const unknownNode = {
                    id: searchQuery,
                    address: searchQuery,
                    tag: 'Unknown Wallet',
                    type: 'unknown',
                    risk: 40
                  };
                  setGraph({ nodes: [unknownNode], links: [] });
                  setTab("graph");
                  console.log('📌 Added to graph as unknown wallet');
                };

                const message = `🔍 Detected: ${detectedChain.toUpperCase()}\n\nAvailable explorers:\n${explorerList}\n\n💡 Options:\n1. Click explorer buttons below to verify\n2. Add to graph as "Unknown Wallet" (for compliance analysis)\n\nWhich would you prefer?`;

                // Show simple confirmation
                if (confirm(message + '\n\nClick OK to add to graph, or Cancel to try explorers')) {
                  addToGraph();
                }
              } else {
                // Chain not detected
                console.log('⚠️ Could not detect blockchain from address format');
                const message = `❌ Could not identify blockchain from address format.\n\n💡 Options:\n• Try specific explorers below\n• Add to graph as "Unknown Wallet"\n• Run compliance analysis (works on any address)`;
                alert(message);
              }
            };

            return (
              <div style={{display:'flex',gap:6,marginBottom:8,overflowX:'auto',paddingBottom:4,alignItems:'center'}}>
                {/* Trace All Blockchains Button */}
                <button
                  onClick={handleTraceAllChains}
                  style={{
                    padding:'8px 14px',
                    background:C.accent,
                    color:'#fff',
                    border:'1px solid '+C.accent,
                    borderRadius:6,
                    cursor:'pointer',
                    fontSize:11,
                    fontWeight:700,
                    whiteSpace:'nowrap',
                    transition:'all 0.2s',
                    flexShrink:0
                  }}
                  title="Search across all blockchains"
                >
                  🔍 Trace All Chains
                </button>

                {/* Relevant explorers */}
                {relevantExplorers.map(e=>(
                  <button
                    key={e.name}
                    onClick={()=>{
                      const url = e.url.endsWith('/') ? e.url + searchQuery : e.url + '/' + searchQuery;
                      window.open('https://' + url, '_blank');
                    }}
                    style={{
                      padding:'6px 10px',
                      background:C.border,
                      color:C.textMid,
                      border:'1px solid '+C.border,
                      borderRadius:4,
                      cursor:'pointer',
                      fontSize:10,
                      fontWeight:600,
                      whiteSpace:'nowrap',
                      transition:'all 0.2s'
                    }}
                    title={`View on ${e.name}`}
                  >
                    {e.icon} {e.name}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Enhancement Tabs */}
      <div style={{display:'flex',gap:8,marginBottom:16,overflowX:'auto',paddingBottom:8}}>
        {[
          {id:'analysis',label:'🔍 Address Analysis'},
          {id:'watchlist',label:'👁️ Watchlist'},
          {id:'portfolio',label:'💼 Portfolio'},
          {id:'patterns',label:'🔄 Patterns'},
          {id:'rules',label:'⚙️ Rules'},
          {id:'alerts',label:'🔔 Alerts'},
          {id:'comparison',label:'⚖️ Compare'},
          {id:'reports',label:'📄 Reports'}
        ].map(tab=>(
          <button
            key={tab.id}
            onClick={()=>setActiveTab(tab.id)}
            style={{
              padding:'8px 14px',
              background:activeTab===tab.id?C.accent:C.surface,
              color:activeTab===tab.id?'#fff':C.text,
              border:'1px solid '+(activeTab===tab.id?C.accent:C.border),
              borderRadius:6,
              cursor:'pointer',
              fontSize:11,
              fontWeight:600,
              whiteSpace:'nowrap',
              transition:'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{...S.card,marginBottom:18}}>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10,alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {EXPLORERS.slice(0,5).map(e=>(
              <button key={e.network} onClick={()=>{setNetwork(e.network);setShowBlockchainSelector(false);}} style={S.btn(network===e.network,e.color)}>
                {e.icon} {e.name}
              </button>
            ))}
          </div>
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowBlockchainSelector(!showBlockchainSelector)}
              style={{padding:"6px 12px",background:C.surface,border:"1px solid "+C.border,borderRadius:6,color:C.text,fontSize:11,cursor:"pointer",fontWeight:600}}>
              + {BLOCKCHAIN_NETWORKS.length} Blockchains ▼
            </button>
            {showBlockchainSelector && (
              <div style={{position:"absolute",top:"100%",right:0,marginTop:4,background:C.surface,border:"1px solid "+C.border,borderRadius:6,padding:8,zIndex:1000,maxWidth:280,maxHeight:300,overflowY:"auto",boxShadow:"0 8px 24px rgba(0,0,0,0.3)"}}>
                <input
                  type="text"
                  value={blockchainSearch}
                  onChange={e=>setBlockchainSearch(e.target.value)}
                  placeholder="Search blockchains..."
                  style={{width:"100%",padding:"6px 8px",background:C.bg,border:"1px solid "+C.border,borderRadius:4,color:C.text,fontSize:11,marginBottom:8,fontFamily:"monospace",outline:"none"}}
                />
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {BLOCKCHAIN_NETWORKS.filter(b=>
                    !blockchainSearch || b.name.toLowerCase().includes(blockchainSearch.toLowerCase()) || b.symbol.toLowerCase().includes(blockchainSearch.toLowerCase()) || b.id.toLowerCase().includes(blockchainSearch.toLowerCase())
                  ).map(b=>(
                    <button
                      key={b.id}
                      onClick={()=>{setNetwork(b.id);setShowBlockchainSelector(false);setBlockchainSearch("");}}
                      style={{textAlign:"left",padding:"8px 10px",background:network===b.id?C.accentGlow:C.bg,border:"1px solid "+(network===b.id?C.accent:C.border),borderRadius:4,color:network===b.id?C.accent:C.text,fontSize:11,cursor:"pointer",fontFamily:"monospace",transition:"all 0.2s"}}
                    >
                      <span style={{marginRight:8}}>{b.icon}</span>
                      <strong>{b.name}</strong>
                      <span style={{color:C.textDim,fontSize:9,marginLeft:6}}>({b.symbol})</span>
                      {b.type==="non-evm"&&<span style={{marginLeft:8,fontSize:9,color:C.warn}}>⚠️ Non-EVM</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{flex:1,position:"relative"}}>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={e=>setSearchQuery(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleSearch()}
              onFocus={()=>setSearchFocused(true)}
              onBlur={()=>setSearchFocused(false)}
              placeholder="Bitcoin, Ethereum, TRON, or other address • or tx hash"
              style={{
                flex:1,
                background:C.bg,
                border:"1px solid "+(searchFocused?C.accent:C.border),
                borderRadius:7,
                color:C.text,
                fontSize:12,
                padding:"11px 13px",
                fontFamily:"monospace",
                outline:"none",
                transition:"border-color 0.2s",
                paddingRight:"32px"
              }}
            />
            {searchQuery && (
              <button
                onClick={()=>setSearchQuery("")}
                title="Clear search (Esc)"
                style={{
                  position:"absolute",
                  right:10,
                  top:"50%",
                  transform:"translateY(-50%)",
                  background:"transparent",
                  border:"none",
                  color:C.textDim,
                  cursor:"pointer",
                  fontSize:16,
                  padding:4
                }}
              >
                ✕
              </button>
            )}
            {searchFocused && (
              <div style={{
                position:"absolute",
                right:8,
                bottom:-18,
                fontSize:9,
                color:C.textDim,
                whiteSpace:"nowrap"
              }}>
                ⌘K to focus • Esc to clear
              </div>
            )}
          </div>
          <button onClick={handleSearch} disabled={!searchQuery.trim()||loading}
            style={{padding:"11px 20px",background:searchQuery.trim()&&!loading?C.accent:C.border,
              border:"none",borderRadius:7,color:C.bg,fontWeight:700,cursor:"pointer",fontFamily:"monospace",fontSize:12,whiteSpace:"nowrap",transition:"background 0.2s"}}>
            {loading?"...":"Trace"}
          </button>
        </div>
        <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:8,alignItems:"center"}}>
          {PROVIDERS.map(p=>{
            const st=providerStatus[p.id];
            const col=st==="ok"?C.success:st==="trying"?C.warn:st==="error"?C.danger:C.textDim;
            return (
              <span key={p.id} style={{padding:"2px 7px",borderRadius:4,
                border:"1px solid "+col+"33",background:col+"11",color:col,fontSize:9}}>
                {st==="trying"?"...":st==="ok"?"ok":st==="error"?"err":"."} {p.name}
              </span>
            );
          })}
          {ethPrice > 0 && (
            <span style={{marginLeft:"auto",padding:"2px 10px",borderRadius:4,
              border:"1px solid "+C.accent+"33",background:C.accentGlow,
              color:C.accent,fontSize:9,fontWeight:700,fontFamily:"monospace"}}>
              {NETWORK_CURRENCY[network]?.symbol||"ETH"} ${ethPrice.toLocaleString("en-US", {minimumFractionDigits:2,maximumFractionDigits:2})}
            </span>
          )}
        </div>
      </div>

      {/* Trace History Breadcrumb */}
      {traceHistory.length > 0 && (
        <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:8,padding:"8px 12px",marginBottom:12,display:"flex",gap:6,alignItems:"center",overflowX:"auto"}}>
          <span style={{color:C.textDim,fontSize:10,flexShrink:0}}>TRACE PATH:</span>
          {traceHistory.slice(0,8).map((h,i)=>{
            const known=KNOWN_ENTITIES[h.address.toLowerCase()];
            return (
              <div key={i} style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                {i>0&&<span style={{color:C.textDim,fontSize:12}}>→</span>}
                <button
                  onClick={()=>{setSearchQuery(h.address);fetchWithFallback(h.address);}}
                  style={{padding:"3px 8px",borderRadius:5,background:i===0?C.accentGlow:"transparent",
                    border:"1px solid "+(i===0?C.accent:C.border),
                    color:i===0?C.accent:C.textMid,fontSize:9,cursor:"pointer",fontFamily:"monospace"}}>
                  {known?.label||h.address.slice(0,8)+"..."}
                  <span style={{color:C.textDim,marginLeft:4}}>{h.txCount}tx</span>
                </button>
              </div>
            );
          })}
          <button onClick={()=>setTraceHistory([])}
            style={{marginLeft:"auto",background:"transparent",border:"none",color:C.danger+"66",cursor:"pointer",fontSize:10,flexShrink:0}}>
            Clear
          </button>
        </div>
      )}

      <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)} style={S.btn(tab===t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:6}}>
          <button
            onClick={()=>setShowNarrativePanel(!showNarrativePanel)}
            title="Add analysis narrative and notes"
            style={{padding:"8px 14px",background:showNarrativePanel?C.accentGlow:C.surface,border:`1px solid ${showNarrativePanel?C.accent:C.border}`,borderRadius:6,color:showNarrativePanel?C.accent:C.text,fontSize:11,fontWeight:600,cursor:"pointer",transition:"all 0.2s"}}
          >
            📝 {narrative.length > 0 ? "Notes" : "Add Notes"}
          </button>
        </div>
      </div>

      {/* Narrative Panel */}
      {showNarrativePanel && (
        <div style={{background:C.surface,border:`2px solid ${C.accent}`,borderRadius:8,padding:16,marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <h3 style={{margin:0,color:C.accent,fontSize:14,fontWeight:600}}>📝 Analysis Narrative</h3>
            <span style={{fontSize:10,color:C.textDim}}>{narrative.length} characters</span>
          </div>
          <textarea
            value={narrative}
            onChange={e=>setNarrative(e.target.value)}
            placeholder="Enter your analysis notes, findings, risks identified, suspected activities, or any other observations..."
            style={{width:"100%",minHeight:"120px",padding:12,background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:12,fontFamily:"monospace",outline:"none",resize:"vertical",marginBottom:12}}
          />
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button
              onClick={saveNarrative}
              style={{padding:"8px 16px",background:C.success,border:"none",borderRadius:6,color:C.bg,fontWeight:600,cursor:"pointer",fontSize:11,transition:"all 0.2s"}}
            >
              {narrativeSaved?"✓ Saved":"Save Narrative"}
            </button>
            <span style={{fontSize:10,color:C.textDim}}>Automatically loaded when you search for this address again</span>
          </div>
        </div>
      )}

      {/* Node Intel Panel — fixed right-side overlay */}
      {drawerNode && (() => {
        const nd = drawerNode;
        const typeColor = NODE_COLORS[nd.type] || C.accent;
        const meta = CATEGORY_META[nd.type] || {icon:"👛", label:"Wallet"};
        const sub = nd.type==="exchange" ? getExchangeSubtype(nd.tag||"") : null;
        const riskColor = nd.risk>=80?C.danger:nd.risk>=50?C.warn:C.success;

        // Compute flow totals from drawerTxs
        let totalIn=0, totalOut=0, txIn=0, txOut=0;
        drawerTxs.forEach(tx => {
          const v = parseFloat(tx.value)||0;
          if (tx.direction==="out") { totalOut+=v; txOut++; } else { totalIn+=v; txIn++; }
        });

        // Counterparty type breakdown
        const cpBreakdown = {};
        drawerTxs.forEach(tx => {
          const isOut = tx.direction==="out";
          const cpType = isOut ? (tx.toType||"unknown") : (tx.fromType||"unknown");
          const v = parseFloat(tx.value)||0;
          if (!cpBreakdown[cpType]) cpBreakdown[cpType] = {count:0, total:0};
          cpBreakdown[cpType].count++;
          cpBreakdown[cpType].total += v;
        });
        const cpEntries = Object.entries(cpBreakdown).sort((a,b)=>b[1].total-a[1].total);

        // Filter txs
        const filteredTxs = drawerTxFilter==="all" ? drawerTxs
          : drawerTxFilter==="in"   ? drawerTxs.filter(tx=>tx.direction!=="out")
          : drawerTxFilter==="out"  ? drawerTxs.filter(tx=>tx.direction==="out")
          : drawerTxs.filter(tx=>tx.risk==="HIGH");

        const explorerBase = EXPLORERS.find(e=>e.network===network);

        return (
          <div style={{
            position:"fixed", top:0, right:0, bottom:0, width:460,
            background:C.surface, borderLeft:"1px solid "+C.border,
            zIndex:1100, display:"flex", flexDirection:"column",
            boxShadow:"-8px 0 32px rgba(0,0,0,0.4)",
            fontFamily:"monospace",
          }}>
            {/* Header */}
            <div style={{padding:"16px 18px", borderBottom:"1px solid "+C.border, flexShrink:0}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                <div style={{
                  width:40, height:40, borderRadius:8, background:typeColor+"22",
                  border:"1px solid "+typeColor+"44", display:"flex", alignItems:"center",
                  justifyContent:"center", fontSize:20, flexShrink:0,
                }}>
                  {sub ? sub.icon : meta.icon}
                </div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{color:typeColor, fontWeight:800, fontSize:14, lineHeight:1.2}}>
                    {nd.tag || meta.label}
                  </div>
                  {sub && (
                    <div style={{color:sub.color, fontSize:10, fontWeight:600, marginTop:2}}>
                      {sub.icon} {sub.sub}
                    </div>
                  )}
                  <div style={{display:"flex",alignItems:"center",gap:6,marginTop:3}}>
                    <span style={{color:C.textDim, fontSize:8, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      {nd.id}
                    </span>
                    <button
                      onClick={()=>{copyToClipboard(nd.id,()=>{setCopyFeedback("node");setTimeout(()=>setCopyFeedback(null),1500);});}}
                      title="Copy address"
                      style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",fontSize:10,padding:"1px 3px",flexShrink:0}}
                    >
                      {copyFeedback==="node"?"✓":"📋"}
                    </button>
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4,flexShrink:0}}>
                  <span style={{
                    background:riskColor+"22", color:riskColor,
                    padding:"2px 8px", borderRadius:4, fontSize:10, fontWeight:700,
                  }}>RISK {nd.risk}</span>
                  <button onClick={()=>setDrawerNode(null)}
                    style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",fontSize:16,padding:0,lineHeight:1}}>
                    ✕
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{display:"flex",gap:6,marginTop:12}}>
                <button
                  onClick={()=>{setSearchQuery(nd.id);fetchWithFallback(nd.id);setDrawerNode(null);}}
                  style={{flex:1,padding:"7px 0",background:C.accent,border:"none",borderRadius:6,
                    color:C.bg,fontWeight:700,cursor:"pointer",fontFamily:"monospace",fontSize:10}}>
                  Full Trace →
                </button>
                <button
                  onClick={()=>explorerBase&&window.open("https://"+explorerBase.url+"/address/"+nd.id,"_blank")}
                  style={{flex:1,padding:"7px 0",background:"transparent",border:"1px solid "+C.border,
                    borderRadius:6,color:C.accent,cursor:"pointer",fontFamily:"monospace",fontSize:10}}>
                  Explorer ↗
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div style={{flex:1, overflowY:"auto", padding:"14px 18px", display:"flex", flexDirection:"column", gap:14}}>

              {/* Wallet Balance */}
              <div style={{background:C.bg, border:"1px solid "+C.accent+"33", borderRadius:8, padding:"10px 12px"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <span style={{color:C.accent, fontSize:9, letterSpacing:"0.08em", fontWeight:700}}>💰 WALLET BALANCE</span>
                  {drawerBalance?.totalUSD != null && (
                    <span style={{color:C.success, fontSize:13, fontWeight:800}}>
                      ${drawerBalance.totalUSD>=1000
                        ? (drawerBalance.totalUSD/1000).toFixed(1)+"K"
                        : drawerBalance.totalUSD.toFixed(2)
                      } total
                    </span>
                  )}
                </div>
                {!drawerBalance ? (
                  <div style={{color:C.textDim, fontSize:10}}>Loading balance…</div>
                ) : (
                  <>
                    {/* Native balance row */}
                    <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:6}}>
                      <span style={{color:C.accent, fontSize:18, fontWeight:800}}>
                        {drawerBalance.native != null
                          ? (drawerBalance.native>=1 ? drawerBalance.native.toFixed(4) : drawerBalance.native.toFixed(6))
                          : "0"
                        }
                      </span>
                      <span style={{color:C.textDim, fontSize:10}}>{NETWORK_CURRENCY[network]?.symbol||"ETH"}</span>
                      {ethPrice>0 && drawerBalance.native != null && drawerBalance.native > 0 && (
                        <span style={{color:C.success, fontSize:10}}>
                          {fmtUSD(drawerBalance.native, ethPrice)}
                        </span>
                      )}
                    </div>

                    {/* Zerion token portfolio (with real USD values) */}
                    {drawerBalance.tokenPortfolio?.length > 0 && (
                      <div style={{display:"flex",flexDirection:"column",gap:3,marginTop:4}}>
                        {drawerBalance.tokenPortfolio.slice(0,8).map((t,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                            background:C.surface, borderRadius:5, padding:"4px 8px"}}>
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <span style={{color:C.text,fontSize:10,fontWeight:700}}>{t.symbol}</span>
                              <span style={{color:C.textDim,fontSize:9}}>{t.balance>=1?t.balance.toFixed(4):t.balance.toFixed(6)}</span>
                            </div>
                            <span style={{color:C.success,fontSize:10,fontWeight:600}}>
                              ${t.usd>=0.01?t.usd.toFixed(2):"<0.01"}
                            </span>
                          </div>
                        ))}
                        {drawerBalance.tokenPortfolio.length>8 && (
                          <div style={{color:C.textDim,fontSize:9,padding:"2px 0"}}>
                            +{drawerBalance.tokenPortfolio.length-8} more tokens
                          </div>
                        )}
                      </div>
                    )}

                    {/* Fallback: token symbols from tx history if no Zerion data */}
                    {!drawerBalance.tokenPortfolio?.length && drawerBalance.tokens?.length > 0 && (
                      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
                        {drawerBalance.tokens.slice(0,12).map((t,i)=>(
                          <span key={i} style={{
                            background:C.surface, border:"1px solid "+C.border,
                            borderRadius:4, padding:"1px 6px", fontSize:9, color:C.textMid,
                          }}>{t.symbol}</span>
                        ))}
                        {drawerBalance.tokens.length>12 && (
                          <span style={{color:C.textDim,fontSize:9}}>+{drawerBalance.tokens.length-12}</span>
                        )}
                      </div>
                    )}

                    {drawerBalance.native === 0 && !drawerBalance.tokenPortfolio?.length && !drawerBalance.tokens?.length && (
                      <div style={{color:C.textDim,fontSize:10,marginTop:4}}>No assets found on {network}</div>
                    )}
                  </>
                )}
              </div>

              {/* Flow totals */}
              {drawerTxs.length > 0 && (
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                  {[
                    {label:"TOTAL IN",   val:fmtNative(totalIn,network),  col:C.success, sub:txIn+"tx"},
                    {label:"TOTAL OUT",  val:fmtNative(totalOut,network), col:C.danger,  sub:txOut+"tx"},
                    {label:"NET FLOW",   val:fmtNative(totalIn-totalOut,network), col:C.accent,  sub:(totalIn-totalOut>=0?"positive":"negative")},
                  ].map(({label,val,col,sub})=>(
                    <div key={label} style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:"8px 10px"}}>
                      <div style={{color:C.textDim,fontSize:8,letterSpacing:"0.06em",marginBottom:3}}>{label}</div>
                      <div style={{color:col,fontSize:11,fontWeight:700,lineHeight:1.2}}>{val}</div>
                      <div style={{color:C.textDim,fontSize:8,marginTop:2}}>{sub}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Counterparty type breakdown */}
              {cpEntries.length > 0 && (
                <div>
                  <div style={{color:C.textMid, fontSize:9, letterSpacing:"0.08em", fontWeight:700, marginBottom:6}}>
                    COUNTERPARTY TYPES
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {cpEntries.map(([type, {count, total}])=>{
                      const cm = CATEGORY_META[type]||{icon:"👛",label:type};
                      const tc = NODE_COLORS[type]||C.textMid;
                      const maxTotal = cpEntries[0][1].total;
                      const pct = maxTotal > 0 ? (total/maxTotal)*100 : 0;
                      return (
                        <div key={type} style={{background:C.bg,border:"1px solid "+C.border,borderRadius:6,padding:"6px 8px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                            <span style={{fontSize:12}}>{cm.icon}</span>
                            <span style={{color:tc,fontSize:10,fontWeight:600,flex:1}}>{cm.label}</span>
                            <span style={{color:C.textDim,fontSize:9}}>{count} tx</span>
                            <span style={{color:C.success,fontSize:9,marginLeft:4}}>{fmtNative(total,network)}</span>
                          </div>
                          <div style={{background:C.surface,borderRadius:3,height:3}}>
                            <div style={{background:tc,width:pct+"%",height:"100%",borderRadius:3,transition:"width 0.3s"}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Transaction list */}
              <div>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{color:C.textMid, fontSize:9, letterSpacing:"0.08em", fontWeight:700}}>
                    TRANSACTIONS ({filteredTxs.length})
                  </div>
                  <div style={{display:"flex",gap:4}}>
                    {[["all","All"],["in","↓ In"],["out","↑ Out"],["high","🔴 High"]].map(([f,l])=>(
                      <button key={f} onClick={()=>setDrawerTxFilter(f)}
                        style={{
                          padding:"2px 7px", borderRadius:4, cursor:"pointer",
                          fontFamily:"monospace", fontSize:8, fontWeight:600,
                          border:"1px solid "+(drawerTxFilter===f?C.accent:C.border),
                          background:drawerTxFilter===f?C.accent+"22":"transparent",
                          color:drawerTxFilter===f?C.accent:C.textDim,
                        }}>{l}</button>
                    ))}
                  </div>
                </div>

                {drawerLoading ? (
                  <div style={{color:C.textDim,fontSize:11,padding:"8px 0"}}>Loading transactions…</div>
                ) : filteredTxs.length === 0 ? (
                  <div style={{color:C.textDim,fontSize:11,padding:"8px 0"}}>
                    {drawerTxs.length===0
                      ? "No transactions in current trace. Use Full Trace → to fetch directly."
                      : "No transactions match this filter."}
                  </div>
                ) : (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {filteredTxs.slice(0,50).map((tx,i)=>{
                      const rc = tx.risk==="HIGH"?C.danger:tx.risk==="MED"?C.warn:C.success;
                      const isOut = tx.direction==="out";
                      const cpAddr = isOut ? tx.to : tx.from;
                      const cpLabel = isOut ? (tx.toLabel||tx.to?.slice(0,10)+"...") : (tx.fromLabel||tx.from?.slice(0,10)+"...");
                      const cpType = isOut ? tx.toType : tx.fromType;
                      const cpMeta = CATEGORY_META[cpType]||{icon:"👛"};
                      const alreadyInGraph = graph?.nodes?.some(n=>n.id?.toLowerCase()===cpAddr?.toLowerCase());
                      return (
                        <div key={i} style={{
                          background:C.bg, border:"1px solid "+(tx.risk==="HIGH"?C.danger+"44":C.border),
                          borderRadius:7, padding:"8px 10px",
                        }}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                            <span style={{color:isOut?C.accent:C.success,fontWeight:700,fontSize:13,flexShrink:0}}>
                              {isOut?"↑":"↓"}
                            </span>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{display:"flex",alignItems:"center",gap:4}}>
                                <span style={{fontSize:10}}>{cpMeta.icon}</span>
                                <span style={{color:C.text,fontSize:10,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                                  {cpLabel}
                                </span>
                              </div>
                              <div style={{color:C.textDim,fontSize:8,fontFamily:"monospace",marginTop:1}}>
                                {cpAddr?.slice(0,12)}...{cpAddr?.slice(-6)}
                              </div>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{color:isOut?C.accent:C.success,fontSize:10,fontWeight:700}}>
                                {fmtNative(tx.value,network)}
                              </div>
                              {ethPrice>0 && (
                                <div style={{color:C.textDim,fontSize:8}}>{fmtUSD(tx.value,ethPrice)}</div>
                              )}
                            </div>
                          </div>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:6}}>
                            <div style={{display:"flex",gap:6,alignItems:"center"}}>
                              <span style={{background:rc+"22",color:rc,padding:"1px 5px",borderRadius:3,fontSize:8,fontWeight:700}}>{tx.risk}</span>
                              <span style={{color:C.textDim,fontSize:8}}>{tx.age}</span>
                              {tx.hash && (
                                <a href={(explorerBase?"https://"+explorerBase.url:"https://etherscan.io")+"/tx/"+tx.hash}
                                  target="_blank" rel="noopener noreferrer"
                                  style={{color:C.accent,fontSize:8,textDecoration:"none"}}>
                                  {tx.hash.slice(0,8)}…
                                </a>
                              )}
                            </div>
                            {cpAddr && !cpAddr.startsWith("manual-") && (
                              <button
                                onClick={()=>addTxToGraph(tx, nd.id)}
                                disabled={alreadyInGraph}
                                style={{
                                  padding:"3px 8px", borderRadius:5, cursor:alreadyInGraph?"default":"pointer",
                                  fontFamily:"monospace", fontSize:8, fontWeight:700,
                                  border:"1px solid "+(alreadyInGraph?C.border:C.accent),
                                  background:alreadyInGraph?"transparent":C.accent+"22",
                                  color:alreadyInGraph?C.textDim:C.accent,
                                  whiteSpace:"nowrap",
                                }}>
                                {alreadyInGraph ? "✓ In Graph" : "+ Add to Graph"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {filteredTxs.length>50 && (
                      <div style={{color:C.textDim,fontSize:9,textAlign:"center",padding:"6px 0"}}>
                        Showing 50 of {filteredTxs.length} — use Full Trace for complete history
                      </div>
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {tab==="graph" && (
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          {loading && (
            <div style={{...S.card}}>
              <div style={{display:"flex",gap:16,alignItems:"center"}}>
                <div style={{fontSize:24}}>⏳</div>
                <div style={{flex:1}}>
                  <Skeleton height="1.2rem" width="200px" style={{marginBottom:8}} />
                  <Skeleton height="0.9rem" width="150px" />
                </div>
              </div>
            </div>
          )}
          <div>
            {walletBalance && (
              <div style={{...S.card, marginBottom:14, background:"rgba(0,212,255,0.04)", border:"1px solid "+C.accent+"33"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <span style={{color:C.accent,fontSize:10,letterSpacing:"0.08em",fontWeight:700}}>💰 WALLET BALANCE</span>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    {walletBalance.totalUSD != null && (
                      <span style={{color:C.success,fontSize:14,fontWeight:800}}>
                        ${walletBalance.totalUSD>=1000
                          ? (walletBalance.totalUSD/1000).toFixed(2)+"K"
                          : walletBalance.totalUSD.toFixed(2)
                        } total
                      </span>
                    )}
                    <span style={{color:C.textDim,fontSize:9,fontFamily:"monospace"}}>{walletBalance.address?.slice(0,10)}...{walletBalance.address?.slice(-6)}</span>
                  </div>
                </div>
                <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"flex-start"}}>
                  {/* Native balance */}
                  <div style={{background:C.bg,border:"1px solid "+C.accent+"44",borderRadius:8,padding:"8px 14px",flexShrink:0}}>
                    <div style={{color:C.textDim,fontSize:9,marginBottom:2}}>{NETWORK_CURRENCY[network]?.symbol||"ETH"} BALANCE</div>
                    <div style={{color:C.accent,fontSize:18,fontWeight:800,fontFamily:"monospace"}}>
                      {walletBalance.native != null
                        ? (walletBalance.native>=1 ? walletBalance.native.toFixed(4) : walletBalance.native.toFixed(6))
                        : "0"
                      }
                    </div>
                    {ethPrice>0 && walletBalance.native > 0 && (
                      <div style={{color:C.success,fontSize:10}}>{fmtUSD(walletBalance.native,ethPrice)}</div>
                    )}
                  </div>
                  {/* Zerion token portfolio with USD values */}
                  {walletBalance.tokenPortfolio?.length > 0 ? (
                    <div style={{flex:1,minWidth:200}}>
                      <div style={{color:C.textDim,fontSize:9,marginBottom:5,letterSpacing:"0.05em"}}>TOKEN HOLDINGS</div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {walletBalance.tokenPortfolio.slice(0,10).map((t,i)=>(
                          <div key={i} style={{background:C.bg,border:"1px solid "+C.border,borderRadius:5,padding:"3px 8px",display:"flex",gap:5,alignItems:"center"}}>
                            <span style={{color:C.text,fontSize:10,fontWeight:700}}>{t.symbol}</span>
                            <span style={{color:C.success,fontSize:9}}>${t.usd>=0.01?t.usd.toFixed(2):"<0.01"}</span>
                          </div>
                        ))}
                        {walletBalance.tokenPortfolio.length>10 && (
                          <span style={{color:C.textDim,fontSize:9,padding:"3px 0"}}>+{walletBalance.tokenPortfolio.length-10} more</span>
                        )}
                      </div>
                    </div>
                  ) : walletBalance.tokens?.length > 0 ? (
                    <div style={{flex:1}}>
                      <div style={{color:C.textDim,fontSize:9,marginBottom:5}}>TOKENS IN HISTORY</div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {walletBalance.tokens.slice(0,15).map((t,i)=>(
                          <span key={i} style={{background:C.bg,border:"1px solid "+C.border,borderRadius:4,padding:"2px 8px",fontSize:10,color:C.text,fontFamily:"monospace"}}>
                            {t.symbol}
                          </span>
                        ))}
                        {walletBalance.tokens.length>15 && <span style={{color:C.textDim,fontSize:9,padding:"2px 0"}}>+{walletBalance.tokens.length-15} more</span>}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}
            {/* TX Hash banner — shown when graph was built from a hash search */}
            {txHashDetail && !txHashDetail.error && (
              <div style={{...S.card, marginBottom:14, background:"rgba(0,212,255,0.05)", border:"1px solid "+C.accent+"33", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap"}}>
                <span style={{color:C.accent, fontSize:11, fontWeight:700}}>TX</span>
                <span style={{color:C.textDim, fontSize:10, fontFamily:"monospace", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  {txHashDetail.hash}
                </span>
                <div style={{display:"flex", gap:16, flexShrink:0}}>
                  {txHashDetail.value && parseFloat(txHashDetail.value) > 0 && (
                    <span style={{color:C.success, fontSize:11, fontWeight:700}}>
                      {fmtNative(txHashDetail.value, network)}
                      {ethPrice > 0 && <span style={{color:C.textDim, fontWeight:400}}> · {fmtUSD(txHashDetail.value, ethPrice)}</span>}
                    </span>
                  )}
                  {txHashDetail.status && (
                    <span style={{
                      fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4,
                      background: txHashDetail.status==="Success" ? "rgba(0,230,118,0.15)" : "rgba(255,34,68,0.15)",
                      color: txHashDetail.status==="Success" ? "#00E676" : "#FF2244",
                    }}>{txHashDetail.status}</span>
                  )}
                  {txHashDetail.age && <span style={{color:C.textDim, fontSize:10}}>{txHashDetail.age}</span>}
                  <button onClick={()=>setTab("txhash")}
                    style={{padding:"3px 10px", background:"transparent", border:"1px solid "+C.border, borderRadius:5, color:C.accent, cursor:"pointer", fontFamily:"monospace", fontSize:9}}>
                    Full Detail
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'analysis' && (
              <GraphCanvas
                graph={graph} setGraph={setGraph}
                selectedNode={selectedNode} setSelectedNode={setSelectedNode}
                network={network} fetchWithFallback={fetchWithFallback}
                onOpenDrawer={openNodeDrawer}
                onNodeClick={openNodeDrawer}
                ethPrice={ethPrice}
                S={S}
                initLockPhysics={txHashLock}
                onAddLabel={() => setShowAddLabelModal(true)}
              />
            )}

            {activeTab === 'watchlist' && (
              <div style={{...S.card, padding: 20}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                  <h3 style={{margin: 0, color: C.text}}>Watchlist Management</h3>
                  <button onClick={() => setShowAddWatchlist(!showAddWatchlist)} style={{padding: '8px 16px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12}}>
                    {showAddWatchlist ? '✕ Cancel' : '➕ Add Address'}
                  </button>
                </div>

                {showAddWatchlist && (
                  <div style={{background: C.surface, padding: 15, borderRadius: 8, marginBottom: 20, border: `1px solid ${C.border}`}}>
                    <div style={{marginBottom: 10}}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>WALLET ADDRESS</label>
                      <input type="text" placeholder="0x..." value={watchlistForm.address} onChange={e => setWatchlistForm({...watchlistForm, address: e.target.value})} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: 'monospace', fontSize: 12, boxSizing: 'border-box'}} />
                    </div>
                    <div style={{marginBottom: 10}}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>LABEL</label>
                      <input type="text" placeholder="e.g. Suspicious Wallet" value={watchlistForm.label} onChange={e => setWatchlistForm({...watchlistForm, label: e.target.value})} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, boxSizing: 'border-box'}} />
                    </div>
                    <div style={{marginBottom: 10}}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>RISK THRESHOLD (0-100)</label>
                      <input type="number" min="0" max="100" value={watchlistForm.riskThreshold} onChange={e => setWatchlistForm({...watchlistForm, riskThreshold: parseInt(e.target.value)})} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, boxSizing: 'border-box'}} />
                    </div>
                    <button onClick={addToWatchlist} style={{width: '100%', padding: '10px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12}}>Save to Watchlist</button>
                  </div>
                )}

                {watchlist.length > 0 ? (
                  <div style={{display: 'grid', gap: 10}}>
                    {watchlist.map(item => (
                      <div key={item.id} style={{background: C.surface, padding: 12, borderRadius: 6, border: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <div style={{flex: 1, minWidth: 0}}>
                          <div style={{fontFamily: 'monospace', fontSize: 11, color: C.textDim, marginBottom: 4}}>{item.address}</div>
                          {item.label && <div style={{fontSize: 12, color: C.text, fontWeight: 600}}>{item.label}</div>}
                          <div style={{fontSize: 11, color: C.textDim, marginTop: 4}}>Threshold: {item.risk_threshold}% • Alert: {item.alert_enabled ? '✓' : '✕'}</div>
                        </div>
                        <button onClick={() => removeFromWatchlist(item.id)} style={{padding: '6px 12px', background: '#ff4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, marginLeft: 10}}>Remove</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign: 'center', padding: '30px 20px', color: C.textDim}}>No addresses in watchlist</div>
                )}
              </div>
            )}

            {activeTab === 'portfolio' && (
              <div style={{...S.card, padding: 20}}>
                <h3 style={{margin:'0 0 20px 0', color: C.text}}>Portfolio Tracking</h3>

                {portfolioData && portfolioData.length > 0 ? (
                  <div style={{overflowX: 'auto'}}>
                    <table style={{width: '100%', fontSize: 12, borderCollapse: 'collapse'}}>
                      <thead>
                        <tr style={{borderBottom: `1px solid ${C.border}`}}>
                          <th style={{textAlign: 'left', padding: '10px 8px', color: C.textDim}}>ADDRESS</th>
                          <th style={{textAlign: 'left', padding: '10px 8px', color: C.textDim}}>TOKEN</th>
                          <th style={{textAlign: 'right', padding: '10px 8px', color: C.textDim}}>BALANCE</th>
                          <th style={{textAlign: 'right', padding: '10px 8px', color: C.textDim}}>USD VALUE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolioData.map((holding, i) => (
                          <tr key={i} style={{borderBottom: `1px solid ${C.border}`}}>
                            <td style={{padding: '8px', fontFamily: 'monospace', fontSize: 10, color: C.textDim}}>{holding.address?.slice(0, 10)}...</td>
                            <td style={{padding: '8px', color: C.text}}>{holding.token_symbol} ({holding.token_name})</td>
                            <td style={{padding: '8px', textAlign: 'right', color: C.text, fontFamily: 'monospace'}}>{parseFloat(holding.balance).toFixed(4)}</td>
                            <td style={{padding: '8px', textAlign: 'right', color: C.text, fontFamily: 'monospace'}}>${parseFloat(holding.usd_value).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{textAlign: 'center', padding: '30px 20px', color: C.textDim}}>No portfolio data</div>
                )}
              </div>
            )}

            {activeTab === 'patterns' && (
              <div style={{...S.card, padding: 20}}>
                <h3 style={{margin:'0 0 20px 0', color: C.text}}>Pattern Detection</h3>

                {patternDetections.length > 0 ? (
                  <div style={{display: 'grid', gap: 10}}>
                    {patternDetections.map(pattern => (
                      <div key={pattern.id} style={{background: C.surface, padding: 12, borderRadius: 6, border: `1px solid ${C.border}`}}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8}}>
                          <div style={{fontSize: 12, color: C.text, fontWeight: 600}}>{pattern.pattern_type.replace(/_/g, ' ').toUpperCase()}</div>
                          <div style={{fontSize: 11, color: pattern.confidence > 75 ? '#ff8800' : '#0a0', fontWeight: 600}}>Confidence: {pattern.confidence}%</div>
                        </div>
                        <div style={{fontFamily: 'monospace', fontSize: 10, color: C.textDim, marginBottom: 8}}>{pattern.address}</div>
                        {pattern.description && <div style={{fontSize: 11, color: C.text, marginBottom: 8}}>{pattern.description}</div>}
                        <div style={{fontSize: 10, color: C.textDim}}>Severity: {pattern.severity} • {pattern.count || 1} transaction(s)</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign: 'center', padding: '30px 20px', color: C.textDim}}>No patterns detected</div>
                )}
              </div>
            )}

            {activeTab === 'rules' && (
              <div style={{...S.card, padding: 20}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20}}>
                  <h3 style={{margin: 0, color: C.text}}>Custom Rules Engine</h3>
                  <button onClick={() => setShowAddRule(!showAddRule)} style={{padding: '8px 16px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12}}>
                    {showAddRule ? '✕ Cancel' : '➕ Create Rule'}
                  </button>
                </div>

                {showAddRule && (
                  <div style={{background: C.surface, padding: 15, borderRadius: 8, marginBottom: 20, border: `1px solid ${C.border}`}}>
                    <div style={{marginBottom: 10}}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>RULE NAME</label>
                      <input type="text" placeholder="e.g. CEX Interaction Rule" value={ruleForm.name} onChange={e => setRuleForm({...ruleForm, name: e.target.value})} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, boxSizing: 'border-box'}} />
                    </div>
                    <div style={{marginBottom: 10}}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>RULE TYPE</label>
                      <select value={ruleForm.ruleType} onChange={e => setRuleForm({...ruleForm, ruleType: e.target.value})} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, boxSizing: 'border-box'}}>
                        <option value="threshold">Threshold</option>
                        <option value="pattern">Pattern Detection</option>
                        <option value="counterparty">Counterparty</option>
                        <option value="velocity">Velocity Analysis</option>
                      </select>
                    </div>
                    <div style={{marginBottom: 10}}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>RISK ADJUSTMENT (0-100)</label>
                      <input type="number" min="0" max="100" value={ruleForm.riskAdjustment} onChange={e => setRuleForm({...ruleForm, riskAdjustment: e.target.value})} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontSize: 12, boxSizing: 'border-box'}} />
                    </div>
                    <div style={{marginBottom: 10}}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>CONDITIONS (JSON)</label>
                      <textarea value={ruleForm.conditions} onChange={e => setRuleForm({...ruleForm, conditions: e.target.value})} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: 'monospace', fontSize: 11, boxSizing: 'border-box', minHeight: '60px'}} />
                    </div>
                    <button onClick={addCustomRule} style={{width: '100%', padding: '10px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12}}>Create Rule</button>
                  </div>
                )}

                <div style={{marginBottom: 20}}>
                  <div style={{fontSize: 11, color: C.textDim, fontWeight: 600, marginBottom: 10}}>STANDARD RULES</div>
                  {STANDARD_RULES.map(rule => (
                    <div key={rule.id} style={{background: C.surface, padding: 12, borderRadius: 6, border: `1px solid ${C.border}`, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                      <div style={{flex: 1}}>
                        <div style={{fontSize: 12, color: C.text, fontWeight: 600}}>{rule.name}</div>
                        <div style={{fontSize: 11, color: C.textDim, marginTop: 4}}>{rule.description}</div>
                        <div style={{fontSize: 10, color: C.textDim, marginTop: 4}}>Type: {rule.ruleType} • Risk Adjustment: +{rule.riskAdjustment}</div>
                      </div>
                      <div style={{marginLeft: 10}}>
                        <input type="checkbox" checked={rule.enabled} onChange={() => {}} style={{cursor: 'pointer', width: 18, height: 18}} title="Enable/disable this rule" />
                      </div>
                    </div>
                  ))}
                </div>

                {customRules.length > 0 && (
                  <div>
                    <div style={{fontSize: 11, color: C.textDim, fontWeight: 600, marginBottom: 10}}>CUSTOM RULES</div>
                    {customRules.map(rule => (
                      <div key={rule.id} style={{background: C.surface, padding: 12, borderRadius: 6, border: `1px solid ${C.border}`, marginBottom: 8}}>
                        <div style={{fontSize: 12, color: C.text, fontWeight: 600}}>{rule.name}</div>
                        <div style={{fontSize: 10, color: C.textDim, marginTop: 4}}>Type: {rule.rule_type} • Risk: +{rule.risk_adjustment}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'alerts' && (
              <div style={{...S.card, padding: 20}}>
                <h3 style={{margin:'0 0 20px 0', color: C.text}}>Alerts Dashboard</h3>

                {alerts.length > 0 ? (
                  <div style={{display: 'grid', gap: 10}}>
                    {alerts.map(alert => (
                      <div key={alert.id} style={{background: alert.risk_score > 70 ? '#ff444422' : '#ff880022', padding: 12, borderRadius: 6, border: `2px solid ${alert.risk_score > 70 ? '#ff4444' : '#ff8800'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                        <div style={{flex: 1}}>
                          <div style={{fontSize: 12, color: C.text, fontWeight: 600}}>
                            {alert.risk_score > 70 ? '🔴' : '🟠'} {alert.alert_type.replace(/_/g, ' ').toUpperCase()}
                          </div>
                          <div style={{fontFamily: 'monospace', fontSize: 10, color: C.textDim, marginTop: 4}}>{alert.address}</div>
                          {alert.message && <div style={{fontSize: 11, color: C.text, marginTop: 4}}>{alert.message}</div>}
                          <div style={{fontSize: 10, color: C.textDim, marginTop: 4}}>Risk Score: {alert.risk_score} • Status: {alert.status}</div>
                        </div>
                        <button onClick={() => dismissAlert(alert.id)} style={{padding: '6px 12px', background: C.border, color: C.text, border: `1px solid ${C.border}`, borderRadius: 4, cursor: 'pointer', fontSize: 11, marginLeft: 10}}>Dismiss</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign: 'center', padding: '30px 20px', color: C.textDim}}>No active alerts</div>
                )}
              </div>
            )}

            {activeTab === 'comparison' && (
              <div style={{...S.card, padding: 20}}>
                <h3 style={{margin:'0 0 20px 0', color: C.text}}>Address Comparison</h3>

                <div style={{marginBottom: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15}}>
                  {[0, 1].map(idx => (
                    <div key={idx}>
                      <label style={{display: 'block', fontSize: 11, color: C.textDim, marginBottom: 4}}>ADDRESS {idx + 1}</label>
                      <input type="text" placeholder="0x..." value={comparisonAddresses[idx]} onChange={e => {
                        const newAddrs = [...comparisonAddresses];
                        newAddrs[idx] = e.target.value;
                        setComparisonAddresses(newAddrs);
                      }} style={{width: '100%', padding: '8px 12px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, fontFamily: 'monospace', fontSize: 12, boxSizing: 'border-box'}} />
                    </div>
                  ))}
                </div>

                {comparisonAddresses[0] && comparisonAddresses[1] && (
                  <div style={{background: C.surface, padding: 15, borderRadius: 8, border: `1px solid ${C.border}`}}>
                    <div style={{fontSize: 11, color: C.textDim, marginBottom: 10, fontWeight: 600}}>COMPARISON RESULTS</div>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, fontSize: 12}}>
                      <div>
                        <div style={{color: C.textDim, marginBottom: 4}}>ADDRESS 1</div>
                        <div style={{fontFamily: 'monospace', color: C.text}}>{comparisonAddresses[0].slice(0, 12)}...</div>
                      </div>
                      <div>
                        <div style={{color: C.textDim, marginBottom: 4}}>COMPARISON</div>
                        <div style={{color: C.text, textAlign: 'center'}}>🔍</div>
                      </div>
                      <div>
                        <div style={{color: C.textDim, marginBottom: 4}}>ADDRESS 2</div>
                        <div style={{fontFamily: 'monospace', color: C.text}}>{comparisonAddresses[1].slice(0, 12)}...</div>
                      </div>
                    </div>
                    <div style={{marginTop: 15, fontSize: 11, color: C.textDim}}>
                      📊 Analyze risk scores, transaction patterns, and counterparties
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div style={{...S.card, padding: 20}}>
                <h3 style={{margin:'0 0 20px 0', color: C.text}}>Compliance Reports</h3>

                {complianceReports.length > 0 ? (
                  <div style={{display: 'grid', gap: 10}}>
                    {complianceReports.map(report => (
                      <div key={report.id} style={{background: C.surface, padding: 12, borderRadius: 6, border: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <div style={{flex: 1}}>
                          <div style={{fontSize: 12, color: C.text, fontWeight: 600}}>
                            {report.report_type === 'sar' ? '📋 SAR Report' : report.report_type === 'cdd' ? '👤 CDD Report' : '🔍 EDD Report'}
                          </div>
                          <div style={{fontFamily: 'monospace', fontSize: 10, color: C.textDim, marginTop: 4}}>{report.address}</div>
                          <div style={{fontSize: 10, color: C.textDim, marginTop: 4}}>Generated: {new Date(report.created_at).toLocaleDateString()}</div>
                        </div>
                        <button style={{padding: '6px 12px', background: C.accent, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, marginLeft: 10}}>View</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{textAlign: 'center', padding: '30px 20px', color: C.textDim}}>No reports generated</div>
                )}
              </div>
            )}

            {/* Item 19: Add Wallet Label Modal */}
            {showAddLabelModal && (
              <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}}>
                <div style={{background:S.card.background||"#1a2030",border:`1px solid ${S.card.border||"#2a3a5a"}`,borderRadius:12,padding:28,width:420,maxWidth:"90vw",color:"#e2e8f0"}}>
                  <div style={{fontWeight:700,fontSize:16,marginBottom:18}}>Add Wallet Label</div>
                  {[
                    { key:"address", label:"Wallet Address (0x...)", placeholder:"0x..." },
                    { key:"label",   label:"Label / Name", placeholder:"e.g. Binance Hot Wallet" },
                    { key:"notes",   label:"Notes (optional)", placeholder:"Investigation context..." },
                  ].map(f => (
                    <div key={f.key} style={{marginBottom:12}}>
                      <div style={{fontSize:10,letterSpacing:"0.08em",marginBottom:4,opacity:0.6}}>{f.label.toUpperCase()}</div>
                      <input value={addLabelForm[f.key]} onChange={e=>setAddLabelForm(p=>({...p,[f.key]:e.target.value}))}
                        placeholder={f.placeholder} style={{width:"100%",background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,color:"#e2e8f0",padding:"8px 10px",fontSize:12,fontFamily:"'DM Mono',monospace",boxSizing:"border-box"}} />
                    </div>
                  ))}
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
                    <div>
                      <div style={{fontSize:10,letterSpacing:"0.08em",marginBottom:4,opacity:0.6}}>TYPE</div>
                      <select value={addLabelForm.type} onChange={e=>setAddLabelForm(p=>({...p,type:e.target.value}))}
                        style={{width:"100%",background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,color:"#e2e8f0",padding:"8px 10px",fontSize:12}}>
                        {["exchange","defi","mixer","sanctioned","atm","gambling","darknet","ransomware","scam","bridge","nft","staking","unknown"].map(t=><option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <div style={{fontSize:10,letterSpacing:"0.08em",marginBottom:4,opacity:0.6}}>RISK SCORE (0-100)</div>
                      <input type="number" min={0} max={100} value={addLabelForm.risk} onChange={e=>setAddLabelForm(p=>({...p,risk:e.target.value}))}
                        style={{width:"100%",background:"rgba(0,0,0,0.3)",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,color:"#e2e8f0",padding:"8px 10px",fontSize:12,boxSizing:"border-box"}} />
                    </div>
                  </div>
                  <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                    <button onClick={()=>setShowAddLabelModal(false)} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.2)",borderRadius:7,color:"#e2e8f0",padding:"8px 18px",cursor:"pointer",fontSize:12,fontFamily:"'DM Mono',monospace"}}>Cancel</button>
                    <button onClick={saveCustomLabel} style={{background:"#00D4FF",border:"none",borderRadius:7,color:"#000",padding:"8px 20px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'DM Mono',monospace"}}>Save Label</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Bottom panel row: AI Risk · High-Risk Exchanges · Graph Legend ── */}
          {activeTab === 'analysis' && (
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,alignItems:"start"}}>

            {/* AI Risk Analysis */}
            <div style={{...S.card, maxHeight:480, overflowY:"auto"}}>
              <AIRiskPanel node={selectedNode} graph={graph||{nodes:[],links:[]}} setGraph={setGraph} txList={txList} onLog={log}
                company={company} currentUser={currentUser} drawerNode={drawerNode} drawerBalance={drawerBalance} setDrawerNode={setDrawerNode}
                savedAnalyses={savedAnalyses} setSavedAnalyses={setSavedAnalyses} onSave={handleSaveAnalysis} onLoad={handleLoadSavedAnalysis}
                customEntities={customEntities}
                defiIntelligence={defiIntelligence} defiLoading={defiLoading} defiError={defiError}/>
            </div>

            {/* High-Risk Exchanges Reference */}
            <div style={{maxHeight:480, overflowY:"auto"}}>
              <HighRiskSidebar onSearch={handleSidebarSearch} />
            </div>

            {/* Graph Legend + TX Exchange Badges */}
            <div style={{...S.card, maxHeight:480, overflowY:"auto"}}>
              <div style={{color:C.textMid,fontSize:10,letterSpacing:"0.08em",marginBottom:12,fontWeight:700}}>GRAPH LEGEND</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5}}>
                {[
                  {icon:"🎯",color:NODE_COLORS.origin,    label:"Traced Wallet",       desc:"Wallet you searched"},
                  {icon:"🏦",color:NODE_COLORS.exchange,  label:"Exchange",            desc:"CEX / Hot / Cold / Custody"},
                  {icon:"⚙️",color:NODE_COLORS.defi,      label:"DeFi Protocol",       desc:"Uniswap, Aave, Compound"},
                  {icon:"🌀",color:NODE_COLORS.mixer,     label:"Mixer / Tumbler",     desc:"Tornado Cash — HIGH RISK"},
                  {icon:"🚫",color:NODE_COLORS.sanctioned,label:"OFAC Sanctioned",     desc:"US sanctions list"},
                  {icon:"🏧",color:NODE_COLORS.atm,       label:"Crypto ATM",          desc:"Physical ATM"},
                  {icon:"🎰",color:NODE_COLORS.gambling,  label:"Gambling",            desc:"Crypto casino / betting"},
                  {icon:"🕶️",color:NODE_COLORS.darknet,   label:"Darknet Market",      desc:"Illegal marketplace"},
                  {icon:"💀",color:NODE_COLORS.ransomware,label:"Ransomware",          desc:"Known ransomware"},
                  {icon:"⚠️",color:NODE_COLORS.scam,      label:"Scam / Fraud",        desc:"Reported scam wallet"},
                  {icon:"🌉",color:NODE_COLORS.bridge,    label:"Cross-chain Bridge",  desc:"Assets moved cross-chain"},
                  {icon:"🖼️",color:NODE_COLORS.nft,       label:"NFT Marketplace",     desc:"OpenSea, Blur etc"},
                  {icon:"📈",color:NODE_COLORS.staking,   label:"Staking / Yield",     desc:"Lido, ETH2 deposit"},
                  {icon:"👛",color:NODE_COLORS.unknown,   label:"Unknown Wallet",      desc:"Not in database"},
                ].map(({icon,color,label,desc})=>(
                  <div key={label} style={{display:"flex",alignItems:"center",gap:6,padding:"3px 0"}}>
                    <div style={{width:8,height:8,borderRadius:2,background:color,flexShrink:0}}/>
                    <span style={{fontSize:11}}>{icon}</span>
                    <div style={{minWidth:0}}>
                      <div style={{color,fontSize:9,fontWeight:700,lineHeight:1.2}}>{label}</div>
                      <div style={{color:C.textDim,fontSize:8,lineHeight:1.2}}>{desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Exchange sub-type legend */}
              <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid "+C.border}}>
                <div style={{color:C.textMid,fontSize:10,letterSpacing:"0.08em",marginBottom:8,fontWeight:700}}>EXCHANGE SUB-TYPES</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
                  {[
                    {icon:"🔥",color:"#FF8C00",label:"Hot Wallet",   desc:"Active trading"},
                    {icon:"🧊",color:"#00D4FF",label:"Cold Wallet",  desc:"Cold storage"},
                    {icon:"🏛",color:"#10B981",label:"Custody",      desc:"Institutional"},
                    {icon:"⭐",color:"#C084FC",label:"Prime / Inst", desc:"Prime brokerage"},
                    {icon:"🏦",color:"#A855F7",label:"CEX",          desc:"Generic exchange"},
                  ].map(s=>(
                    <div key={s.label} style={{display:"flex",alignItems:"center",gap:5,padding:"2px 0"}}>
                      <span style={{fontSize:10}}>{s.icon}</span>
                      <div>
                        <div style={{color:s.color,fontSize:9,fontWeight:700,lineHeight:1.2}}>{s.label}</div>
                        <div style={{color:C.textDim,fontSize:8}}>{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* TX badge legend */}
              <div style={{marginTop:12,paddingTop:10,borderTop:"1px solid "+C.border}}>
                <div style={{color:C.textMid,fontSize:10,letterSpacing:"0.08em",marginBottom:8,fontWeight:700}}>TX EXCHANGE BADGES</div>
                <div style={{display:"flex",flexDirection:"column",gap:5}}>
                  {[
                    {bg:"rgba(168,85,247,0.18)",color:"#A855F7",badge:"🏦 Binance",  desc:"Known exchange"},
                    {bg:"rgba(255,107,53,0.2)", color:"#FF6B35",badge:"🌀 Mixer",    desc:"Mixing service — HIGH RISK"},
                    {bg:"rgba(255,34,68,0.2)",  color:"#FF2244",badge:"🚫 Sanctioned",desc:"OFAC sanctioned"},
                    {bg:"rgba(255,34,68,0.2)",  color:"#FF2244",badge:"🕶 Darknet",  desc:"Darknet interaction"},
                    {bg:"rgba(255,140,0,0.2)",  color:"#FF8C00",badge:"🏧 ATM",      desc:"Crypto ATM"},
                    {bg:"rgba(245,158,11,0.2)", color:"#F59E0B",badge:"🎰 Gambling", desc:"Gambling platform"},
                  ].map(r=>(
                    <div key={r.badge} style={{display:"flex",alignItems:"center",gap:7}}>
                      <span style={{background:r.bg,color:r.color,padding:"2px 6px",borderRadius:4,fontSize:8,fontWeight:700,whiteSpace:"nowrap"}}>{r.badge}</span>
                      <span style={{color:C.textDim,fontSize:9}}>{r.desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
          )}
        </div>
      )}

      {tab==="trail" && (
        <div style={S.card}>
          <span style={S.label}>FOLLOW THE MONEY</span>
          {graph ? (
            <div>
              <div style={{overflowX:"auto",marginBottom:20}}>
                <div style={{display:"flex",alignItems:"center",gap:6,minWidth:"max-content",padding:"8px 0"}}>
                  {(()=>{
                    const visited=new Set();
                    const queue=[[graph.nodes[0]?.id,[]]];
                    const nm=Object.fromEntries(graph.nodes.map(n=>[n.id,n]));
                    let longest=[];
                    while(queue.length) {
                      const [cur,path]=queue.shift();
                      if (visited.has(cur)) continue;
                      visited.add(cur);
                      const nxt=path.concat(cur);
                      if (nxt.length>longest.length) longest=nxt;
                      graph.links.filter(l=>(l.source?.id||l.source)===cur).forEach(l=>{
                        const t=l.target?.id||l.target;
                        if (!visited.has(t)) queue.push([t,nxt]);
                      });
                    }
                    return longest.map((addr,i)=>{
                      const n=nm[addr], col=NODE_COLORS[n?.type]||C.textDim;
                      return (
                        <div key={addr} style={{display:"flex",alignItems:"center",gap:6}}>
                          {i>0 && <span style={{color:C.textDim,fontSize:14}}>to</span>}
                          <div style={{background:col+"22",border:"1px solid "+col+"44",borderRadius:7,padding:"8px 12px",textAlign:"center"}}>
                            <div style={{color:col,fontSize:10,fontWeight:700,textTransform:"uppercase"}}>{n?.type||"?"}</div>
                            <div style={{color:C.text,fontSize:10,fontFamily:"monospace"}}>{addr.slice(0,8)}...</div>
                            {n?.risk>=70 && <div style={{color:C.danger,fontSize:9,marginTop:2}}>HIGH RISK</div>}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
              {graph.links.map((l,i)=>{
                const sid=l.source?.id||l.source, tid=l.target?.id||l.target;
                const sn=graph.nodes.find(n=>n.id===sid), tn=graph.nodes.find(n=>n.id===tid);
                const col=l.direction==="in"?C.success:C.accent;
                return (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:C.bg,border:"1px solid "+C.border,borderRadius:7,marginBottom:6}}>
                    <span style={{color:col,fontSize:14}}>{l.direction==="in"?"in":"out"}</span>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
                        <span style={{color:NODE_COLORS[sn?.type]||C.textDim,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>{sn?.type||"?"}</span>
                        <span style={{color:C.textMid,fontSize:10,fontFamily:"monospace"}}>{sid.slice(0,12)}...</span>
                        <span style={{color:C.textDim}}>to</span>
                        <span style={{color:NODE_COLORS[tn?.type]||C.textDim,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>{tn?.type||"?"}</span>
                        <span style={{color:C.textMid,fontSize:10,fontFamily:"monospace"}}>{tid.slice(0,12)}...</span>
                      </div>
                    </div>
                    <div style={{textAlign:"right",whiteSpace:"nowrap"}}>
                            <span style={{color:col,fontSize:11,fontWeight:700}}>{fmtNative(l.amount||0, network)}</span>
                            {ethPrice>0&&parseFloat(l.amount||0)>0&&<div style={{color:C.success,fontSize:9}}>{fmtUSD(l.amount||0,ethPrice)}</div>}
                          </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon="🔗"
              title="No connections yet"
              subtitle="Enter a wallet address and click Trace to see transaction connections"
              C={C}
            />
          )}
        </div>
      )}

      {tab==="txhash" && (
        <div style={{...S.card}}>
          <span style={S.label}>TRANSACTION DETAIL</span>
          {!txHashDetail
            ? (
              <EmptyState
                icon="🔍"
                title="No transaction loaded"
                subtitle="Search a transaction hash (0x... 66 chars) in the search box above"
                C={C}
              />
            )
            : txHashDetail.error
            ? <div style={{background:"rgba(255,34,68,0.1)",border:"1px solid rgba(255,34,68,0.3)",borderRadius:8,padding:18}}>
                <div style={{color:C.danger,fontWeight:700,marginBottom:8}}>Failed to load transaction</div>
                <div style={{color:C.textMid,fontSize:12,fontFamily:"monospace",marginBottom:12}}>{txHashDetail.hash}</div>
                <div style={{color:C.danger,fontSize:12,marginBottom:14}}>{txHashDetail.error}</div>
                <div style={{display:"flex",gap:8}}>
                  {EXPLORERS.map(e=>(
                    <button key={e.network} onClick={()=>window.open("https://"+e.url+"/tx/"+txHashDetail.hash,"_blank")}
                      style={{padding:"6px 12px",background:"transparent",border:"1px solid "+e.color+"66",borderRadius:6,color:e.color,cursor:"pointer",fontSize:10,fontFamily:"monospace"}}>
                      {e.icon} {e.name} ↗
                    </button>
                  ))}
                </div>
              </div>
            : (() => {
                const tx = txHashDetail;
                const usd = (v) => ethPrice>0&&parseFloat(v)>0 ? " ("+fmtUSD(v,ethPrice)+")" : "";
                const rows = [
                  {label:"Hash",         value:<div style={{display:"flex",alignItems:"center",gap:8}}><a href={"https://etherscan.io/tx/"+tx.hash} target="_blank" rel="noopener noreferrer" style={{color:C.accent,fontFamily:"monospace",wordBreak:"break-all",fontSize:11,flex:1}}>{tx.hash}</a><button onClick={()=>{copyToClipboard(tx.hash,()=>{setCopyFeedback("hash");setTimeout(()=>setCopyFeedback(null),1500);});}} title="Copy hash" style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",fontSize:11,padding:"2px 4px",flexShrink:0}}>{copyFeedback==="hash"?"✓":"📋"}</button></div>},
                  {label:"Status",       value:<span style={{color:tx.status==="Success"?C.success:tx.status==="Failed"?C.danger:C.warn,fontWeight:700}}>{tx.status||"—"}</span>},
                  {label:"From",         value:<div style={{display:"flex",alignItems:"center",gap:8}}><span style={{fontFamily:"monospace",fontSize:11}}>{tx.from}</span>{tx.fromLabel&&<span style={{background:C.accentGlow,color:C.accent,fontSize:9,padding:"1px 6px",borderRadius:3}}>{tx.fromLabel}</span>}<button onClick={()=>{copyToClipboard(tx.from,()=>{setCopyFeedback("from-detail");setTimeout(()=>setCopyFeedback(null),1500);});}} title="Copy address" style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",fontSize:9,padding:"2px 4px"}}>{copyFeedback==="from-detail"?"✓":"📋"}</button><button onClick={()=>{setSearchQuery(tx.from);fetchWithFallback(tx.from);}} style={{padding:"2px 8px",background:"transparent",border:"1px solid "+C.border,borderRadius:4,color:C.accent,cursor:"pointer",fontSize:9,fontFamily:"monospace"}}>Trace →</button></div>},
                  {label:"To",           value:<div style={{display:"flex",alignItems:"center",gap:8}}><span style={{fontFamily:"monospace",fontSize:11}}>{tx.to||"Contract Creation"}</span>{tx.toLabel&&<span style={{background:C.accentGlow,color:C.accent,fontSize:9,padding:"1px 6px",borderRadius:3}}>{tx.toLabel}</span>}{tx.to&&<><button onClick={()=>{copyToClipboard(tx.to,()=>{setCopyFeedback("to-detail");setTimeout(()=>setCopyFeedback(null),1500);});}} title="Copy address" style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",fontSize:9,padding:"2px 4px"}}>{copyFeedback==="to-detail"?"✓":"📋"}</button><button onClick={()=>{setSearchQuery(tx.to);fetchWithFallback(tx.to);}} style={{padding:"2px 8px",background:"transparent",border:"1px solid "+C.border,borderRadius:4,color:C.accent,cursor:"pointer",fontSize:9,fontFamily:"monospace"}}>Trace →</button></>}</div>},
                  {label:"Value",        value:<span style={{color:C.text,fontWeight:700}}>{fmtNative(tx.value,network)}<span style={{color:C.success,fontWeight:400,marginLeft:8}}>{usd(tx.value)}</span></span>},
                  {label:"Gas Fee",      value:tx.gasFeeEth?<span>{fmtNative(tx.gasFeeEth,network)}<span style={{color:C.success,marginLeft:8}}>{usd(tx.gasFeeEth)}</span></span>:"—"},
                  {label:"Block",        value:tx.blockNumber||"—"},
                  {label:"Nonce",        value:tx.nonce??  "—"},
                  {label:"Method",       value:<span style={{background:C.accentGlow,color:C.accent,padding:"2px 8px",borderRadius:4,fontSize:10}}>{tx.method}</span>},
                ];
                return (
                  <div>
                    <div style={{background:"rgba(0,212,255,0.05)",border:"1px solid "+C.accent+"22",borderRadius:10,padding:18,marginBottom:14}}>
                      {rows.map(({label,value})=>(
                        <div key={label} style={{display:"flex",gap:12,padding:"9px 0",borderBottom:"1px solid "+C.border+"22",alignItems:"flex-start"}}>
                          <span style={{color:C.textDim,fontSize:10,letterSpacing:"0.08em",minWidth:90,paddingTop:2}}>{label.toUpperCase()}</span>
                          <span style={{color:C.text,fontSize:12,flex:1,wordBreak:"break-all"}}>{value}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>window.open("https://etherscan.io/tx/"+tx.hash,"_blank")}
                        style={{padding:"8px 16px",background:"transparent",border:"1px solid "+C.accent,borderRadius:7,color:C.accent,cursor:"pointer",fontFamily:"monospace",fontSize:11}}>
                        View on Etherscan ↗
                      </button>
                      {tx.from&&<button onClick={()=>{setSearchQuery(tx.from);fetchWithFallback(tx.from);}}
                        style={{padding:"8px 16px",background:C.accent,border:"none",borderRadius:7,color:C.bg,fontWeight:700,cursor:"pointer",fontFamily:"monospace",fontSize:11}}>
                        Trace Sender →
                      </button>}
                      {tx.to&&<button onClick={()=>{setSearchQuery(tx.to);fetchWithFallback(tx.to);}}
                        style={{padding:"8px 16px",background:"transparent",border:"1px solid "+C.border,borderRadius:7,color:C.textMid,cursor:"pointer",fontFamily:"monospace",fontSize:11}}>
                        Trace Receiver →
                      </button>}
                    </div>
                  </div>
                );
              })()
          }
        </div>
      )}

      {tab==="txlist" && (
        <div style={S.card}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
            <span style={S.label}>TRANSACTION HISTORY ({txList.length})</span>
            {txList.length > 0 && (
              <div style={{display:"flex",gap:6}}>
                <button
                  onClick={()=>exportToCSV(txList,`transactions-${Date.now()}.csv`)}
                  title="Export as CSV"
                  style={{padding:"5px 10px",background:"transparent",border:"1px solid "+C.border,borderRadius:5,color:C.accent,cursor:"pointer",fontSize:9,fontFamily:"monospace"}}>
                  📥 CSV
                </button>
                <button
                  onClick={()=>exportToJSON(txList,`transactions-${Date.now()}.json`)}
                  title="Export as JSON"
                  style={{padding:"5px 10px",background:"transparent",border:"1px solid "+C.border,borderRadius:5,color:C.accent,cursor:"pointer",fontSize:9,fontFamily:"monospace"}}>
                  📥 JSON
                </button>
              </div>
            )}
          </div>
          {txList.length ? (
            <>
              <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{color:C.textDim,fontSize:10}}>FILTER:</span>
                {["all","in","out","high"].map(f=>(
                  <button key={f} onClick={()=>setTxFilter(f)}
                    style={{padding:"4px 9px",background:txFilter===f?C.accent:C.bg,border:"1px solid "+(txFilter===f?C.accent:C.border),borderRadius:4,color:txFilter===f?C.bg:C.text,cursor:"pointer",fontSize:9,fontFamily:"monospace",fontWeight:txFilter===f?700:400}}>
                    {f==="all"?"All":f==="in"?"Incoming":f==="out"?"Outgoing":"High Risk"}
                  </button>
                ))}
                <span style={{marginLeft:"auto",color:C.textDim,fontSize:10}}>SORT:</span>
                {["date","value","risk"].map(s=>(
                  <button key={s} onClick={()=>setTxSortBy(s)}
                    style={{padding:"4px 9px",background:txSortBy===s?C.accent:C.bg,border:"1px solid "+(txSortBy===s?C.accent:C.border),borderRadius:4,color:txSortBy===s?C.bg:C.text,cursor:"pointer",fontSize:9,fontFamily:"monospace",fontWeight:txSortBy===s?700:400}}>
                    {s==="date"?"Date":s==="value"?"Value":"Risk"}
                  </button>
                ))}
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                  <thead>
                    <tr style={{color:C.textDim,borderBottom:"1px solid "+C.border,textAlign:"left"}}>
                      {["Hash","Method","From","To","Exchange",`Value (${NETWORK_CURRENCY[network]?.symbol||"ETH"})`, "Value (USD)","Age","Risk"].map(h=>(
                        <th key={h} style={{padding:"7px 9px",fontSize:10,letterSpacing:"0.05em"}}>{h}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    let filtered = txList;
                    if (txFilter === "in") filtered = filtered.filter(t => t.direction === "in");
                    else if (txFilter === "out") filtered = filtered.filter(t => t.direction === "out");
                    else if (txFilter === "high") filtered = filtered.filter(t => t.risk === "HIGH");

                    if (txSortBy === "value") {
                      filtered = [...filtered].sort((a,b) => (parseFloat(b.value)||0) - (parseFloat(a.value)||0));
                    } else if (txSortBy === "risk") {
                      const riskOrder = {HIGH:3, MED:2, LOW:1};
                      filtered = [...filtered].sort((a,b) => (riskOrder[b.risk]||0) - (riskOrder[a.risk]||0));
                    }

                    return filtered.map((tx,i)=>{
                      const rc=tx.risk==="HIGH"?C.danger:tx.risk==="MED"?C.warn:C.success;
                      return (
                      <tr key={i} style={{borderBottom:"1px solid "+C.border+"22",background:i%2?"transparent":C.bg+"44"}}>
                        <td style={{padding:"8px 9px"}}>
                          <a href={"https://etherscan.io/tx/"+tx.hash} target="_blank" rel="noopener noreferrer"
                            style={{color:C.accent,fontFamily:"monospace",textDecoration:"none"}}>
                            {tx.hash?.slice(0,10)}...
                          </a>
                        </td>
                        <td style={{padding:"8px 9px"}}>
                          <span style={{background:C.accentGlow,color:C.accent,padding:"2px 6px",borderRadius:4,fontSize:9}}>
                            {tx.method}
                          </span>
                        </td>
                        <td style={{padding:"8px 9px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{color:C.textMid,fontFamily:"monospace",flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>{tx.fromLabel||tx.from?.slice(0,10)+"..."}</span>
                            {tx.from && (
                              <button
                                onClick={()=>{copyToClipboard(tx.from,()=>{setCopyFeedback("from");setTimeout(()=>setCopyFeedback(null),1500);});}}
                                title="Copy address"
                                style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",fontSize:11,padding:"2px 4px",flexShrink:0}}
                              >
                                {copyFeedback==="from"?"✓":"📋"}
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{padding:"8px 9px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{color:C.textMid,fontFamily:"monospace",flex:1,minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>{tx.toLabel||tx.to?.slice(0,10)+"..."}</span>
                            {tx.to && (
                              <button
                                onClick={()=>{copyToClipboard(tx.to,()=>{setCopyFeedback("to");setTimeout(()=>setCopyFeedback(null),1500);});}}
                                title="Copy address"
                                style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",fontSize:11,padding:"2px 4px",flexShrink:0}}
                              >
                                {copyFeedback==="to"?"✓":"📋"}
                              </button>
                            )}
                          </div>
                        </td>
                        <td style={{padding:"8px 9px"}}>
                          {(() => {
                            // Check both from and to addresses against KNOWN_ENTITIES + name patterns
                            const fromE = KNOWN_ENTITIES[tx.from?.toLowerCase()] || matchByName(tx.fromLabel);
                            const toE   = KNOWN_ENTITIES[tx.to?.toLowerCase()]   || matchByName(tx.toLabel);
                            const entity = toE || fromE;
                            if (!entity) return <span style={{color:C.textDim,fontSize:9}}>—</span>;

                            const typeIcons = {
                              exchange:   {icon:"🏦", bg:"rgba(168,85,247,0.18)", color:"#A855F7"},
                              mixer:      {icon:"🌀", bg:"rgba(255,107,53,0.2)",  color:"#FF6B35"},
                              sanctioned: {icon:"🚫", bg:"rgba(255,34,68,0.2)",   color:"#FF2244"},
                              darknet:    {icon:"🕶", bg:"rgba(255,34,68,0.2)",   color:"#FF2244"},
                              ransomware: {icon:"💀", bg:"rgba(255,34,68,0.2)",   color:"#FF2244"},
                              atm:        {icon:"🏧", bg:"rgba(255,140,0,0.2)",   color:"#FF8C00"},
                              gambling:   {icon:"🎰", bg:"rgba(245,158,11,0.2)",  color:"#F59E0B"},
                              defi:       {icon:"⚙️", bg:"rgba(57,255,20,0.15)",  color:"#39FF14"},
                              bridge:     {icon:"🌉", bg:"rgba(0,212,255,0.15)",  color:C.accent},
                              nft:        {icon:"🖼️", bg:"rgba(139,92,246,0.15)", color:"#8B5CF6"},
                              staking:    {icon:"📈", bg:"rgba(16,185,129,0.15)", color:"#10B981"},
                            };
                            const cfg = typeIcons[entity.type] || {icon:"👛", bg:"rgba(74,88,112,0.2)", color:C.textMid};
                            const shortLabel = (entity.label||"").split(" ").slice(0,2).join(" ");
                            return (
                              <span style={{background:cfg.bg, color:cfg.color, padding:"2px 6px",
                                borderRadius:4, fontSize:9, fontWeight:700, whiteSpace:"nowrap"}}>
                                {cfg.icon} {shortLabel}
                              </span>
                            );
                          })()}
                        </td>
                        <td style={{padding:"8px 9px",whiteSpace:"nowrap"}}>
                          <span style={{color:C.text,fontWeight:600}}>{tx.displayVal||fmtNative(tx.value,network)}</span>
                        </td>
                        <td style={{padding:"8px 9px",whiteSpace:"nowrap"}}>
                          <span style={{color:C.success,fontSize:10}}>{ethPrice?fmtUSD(tx.value,ethPrice):"—"}</span>
                        </td>
                        <td style={{padding:"8px 9px",color:C.textDim}}>{tx.age}</td>
                        <td style={{padding:"8px 9px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:rc}} title={tx.risk} />
                            <span style={{background:rc+"22",color:rc,padding:"1px 5px",borderRadius:4,fontSize:9,fontWeight:700}}>
                              {tx.risk}
                            </span>
                          </div>
                        </td>
                      </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
              </div>
            </>
          ) : (
            <EmptyState
              icon="📊"
              title="No transactions found"
              subtitle="Enter a wallet address and click Trace to load transaction history"
              C={C}
            />
          )}
        </div>
      )}

      {tab==="explorer" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12}}>
          {EXPLORERS.map(e=>(
            <div key={e.network} onClick={()=>window.open("https://"+e.url,"_blank")}
              style={{...S.card,cursor:"pointer",borderTop:"2px solid "+e.color}}
              onMouseEnter={ev=>ev.currentTarget.style.background=C.surfaceAlt}
              onMouseLeave={ev=>ev.currentTarget.style.background=C.surface}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <span style={{fontSize:20,color:e.color}}>{e.icon}</span>
                <div>
                  <div style={{color:C.text,fontWeight:700,fontSize:13}}>{e.name}</div>
                  <div style={{color:C.textDim,fontSize:10}}>{e.url}</div>
                </div>
              </div>
              <div style={{color:e.color,fontSize:9,fontWeight:700,textTransform:"uppercase"}}>{e.network}</div>
            </div>
          ))}
        </div>
      )}

      {tab==="osint" && (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <OSINTPanel address={searchQuery.trim()} txList={txList} graph={graph} onLog={log} />
          <OSINTResources C={C} />
        </div>
      )}

      {tab==="platforms" && (
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <PlatformsPanel txList={txList} graph={graph} address={searchQuery.trim()} network={network} ethPrice={ethPrice} />
        </div>
      )}

      {tab==="recent" && (
        <div style={S.card}>
          <span style={S.label}>RECENT SEARCHES</span>
          {saved.length===0
            ? (
              <EmptyState
                icon="⏰"
                title="No search history"
                subtitle="Your wallet traces will appear here for quick re-access"
                C={C}
              />
            )
            : saved.map(s=>(
              <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:C.bg,border:"1px solid "+C.border,borderRadius:7,padding:"9px 12px",marginBottom:7}}>
                <div>
                  <div style={{color:C.text,fontFamily:"monospace",fontSize:11,marginBottom:2}}>{s.query}</div>
                  <div style={{color:C.textDim,fontSize:10}}>{s.network.toUpperCase()} · {s.provider||"?"} · {s.timestamp}</div>
                </div>
                <button
                  onClick={()=>{setSearchQuery(s.query);setNetwork(s.network);fetchWithFallback(s.query);setTab("graph");}}
                  style={{padding:"5px 11px",background:"transparent",border:"1px solid "+C.border,borderRadius:5,color:C.accent,cursor:"pointer",fontFamily:"monospace",fontSize:10}}>
                  Re-trace
                </button>
              </div>
            ))
          }
        </div>
      )}

      {tab==="history" && (
        <div style={S.card}>
          <span style={S.label}>SAVED ANALYSES ({savedAnalyses.length})</span>
          {savedAnalyses.length===0
            ? (
              <EmptyState
                icon="📚"
                title="No saved analyses"
                subtitle="Save wallet analyses after tracing to build your analysis history"
                C={C}
              />
            )
            : savedAnalyses.map(analysis=>(
              <div key={analysis.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:C.bg,border:"1px solid "+C.border,borderRadius:7,padding:"9px 12px",marginBottom:7}}>
                <div style={{flex:1}}>
                  <div style={{color:C.text,fontFamily:"monospace",fontSize:11,marginBottom:2}}>{analysis.wallet_name}</div>
                  <div style={{color:C.textMid,fontSize:10,marginBottom:2}}>{analysis.wallet_address.substring(0,12)}...</div>
                  <div style={{color:C.textDim,fontSize:9}}>Risk: {analysis.risk_score}/100 · {new Date(analysis.created_at).toLocaleDateString()}</div>
                </div>
                <button
                  onClick={()=>{setSearchQuery(analysis.wallet_address);setNetwork("ethereum");fetchWithFallback(analysis.wallet_address);setTab("graph");}}
                  style={{padding:"5px 11px",background:"transparent",border:"1px solid "+C.border,borderRadius:5,color:C.accent,cursor:"pointer",fontFamily:"monospace",fontSize:10,whiteSpace:"nowrap",marginLeft:8}}>
                  Load
                </button>
              </div>
            ))
          }
        </div>
      )}
      </div>
    </div>
  );
}